/**
 * BloomQuiz — headless quiz engine.
 *
 * Pure logic, no DOM. Host page (nav.html, Shopify page, any landing) is
 * responsible for rendering UI on top of this engine.
 *
 * Usage:
 *   const engine = BloomQuiz.createEngine(BLOOM_GIFTS); // or BLOOM_SELF
 *   engine.steps                  // array of step configs for rendering
 *   engine.getStep(i)             // step config at index i
 *   engine.answer(key, value)     // record an answer
 *   engine.getState()             // { key: value, ... }
 *   engine.isComplete()           // all steps answered?
 *   engine.score()                // ranked [{ id, score, collection }, ...]
 *   engine.getAnswerLabels()      // human labels of selected options
 *   engine.reset()                // clear answers
 *
 * Data contract: `data` is a full BLOOM_* object with `quiz` and `collections`.
 * `quiz.map` values are *keys into `collections`* — in the current nav.html
 * `collections` is indexed by title, so map values are titles. If host
 * indexes collections by id, map values must be ids.
 */
(function (global) {
	"use strict";

	function createEngine(data) {
		if (!data || !data.quiz || !data.collections) {
			throw new Error("BloomQuiz: data must have { quiz, collections }");
		}
		const quiz = data.quiz;
		const collections = data.collections;
		let state = {};

		return {
			steps: quiz.steps,
			stepCount: quiz.steps.length,

			getStep: function (i) {
				return quiz.steps[i];
			},

			answer: function (key, value) {
				state[key] = value;
				return this;
			},

			getState: function () {
				return Object.assign({}, state);
			},

			reset: function () {
				state = {};
				return this;
			},

			isComplete: function () {
				for (const s of quiz.steps) if (state[s.key] == null) return false;
				return true;
			},

			score: function () {
				const scores = {};
				for (const [k, v] of Object.entries(state)) {
					const bucket = quiz.map[k] && quiz.map[k][v];
					if (!bucket) continue;
					for (const id of bucket) {
						if (collections[id]) scores[id] = (scores[id] || 0) + 1;
					}
				}
				return Object.entries(scores)
					.sort(function (a, b) {
						if (b[1] !== a[1]) return b[1] - a[1];
						const ap = (collections[a[0]].products || []).length;
						const bp = (collections[b[0]].products || []).length;
						return bp - ap;
					})
					.map(function (entry) {
						return { id: entry[0], score: entry[1], collection: collections[entry[0]] };
					});
			},

			getAnswerLabels: function () {
				const labels = [];
				for (const [k, v] of Object.entries(state)) {
					const step = quiz.steps.find(function (s) { return s.key === k; });
					const opt = step && step.options.find(function (o) { return o.id === v; });
					if (opt) labels.push(opt.title);
				}
				return labels;
			},
		};
	}

	const api = { createEngine: createEngine };

	if (typeof module !== "undefined" && module.exports) {
		module.exports = api;
	}
	global.BloomQuiz = api;
})(typeof window !== "undefined" ? window : globalThis);

/**
 * BloomBrowse — headless guided-navigation helper.
 *
 * Pure data layer, no DOM. Host page renders its own UI — this module
 * returns structured data for each screen of the 3-step browse flow:
 *   categories → sections → collections.
 *
 * Usage:
 *   const nav = BloomBrowse.createNavigator(BLOOM_GIFTS); // or BLOOM_SELF
 *
 *   // Screen 1 — list categories
 *   nav.categories();
 *     → [{ name, icon, count }, ...]
 *
 *   // Screen 2 — list sections inside a category
 *   nav.sections("By Mood & Aesthetic");
 *     → { skip: false, items: [{ label, count, index }, ...] }
 *     // when category has a single section → { skip: true, sectionIndex: 0 }
 *
 *   // Screen 3 — list collections inside a section
 *   nav.items("By Mood & Aesthetic", 0);
 *     → { category, section, items: [{ title, subtitle, id, exists }, ...] }
 *
 *   // Helpers
 *   nav.getMenu();                // raw menu object
 *   nav.getIcons();               // raw icons map
 *   nav.getCategoryNames();       // [names, ...]
 *   nav.findCollection(title);    // DATA[title] or null
 *
 * Data contract: `data` is a full BLOOM_* object with `browse` and `collections`.
 * `browse.menu[cat].sections[i].items[j]` values are keys into `collections`
 * (titles, like `quiz.map`). If host indexes collections by id, pre-build a
 * title-keyed DATA before calling createNavigator.
 */
(function (global) {
	"use strict";

	function createNavigator(data) {
		if (!data || !data.browse || !data.collections) {
			throw new Error("BloomBrowse: data must have { browse, collections }");
		}
		const menu = data.browse.menu || {};
		const icons = data.browse.icons || {};
		const collections = data.collections;

		function countItems(cat) {
			const c = menu[cat];
			if (!c) return 0;
			let n = 0;
			for (const sec of c.sections) n += sec.items.length;
			return n;
		}

		return {
			getMenu: function () { return menu; },
			getIcons: function () { return icons; },

			getCategoryNames: function () {
				return Object.keys(menu);
			},

			categories: function () {
				return Object.keys(menu).map(function (name) {
					return {
						name: name,
						icon: icons[name] || "",
						count: countItems(name),
					};
				});
			},

			sections: function (cat) {
				const c = menu[cat];
				if (!c) return { skip: false, items: [] };
				if (c.sections.length === 1) {
					return { skip: true, sectionIndex: 0 };
				}
				return {
					skip: false,
					items: c.sections.map(function (sec, i) {
						return { label: sec.label, count: sec.items.length, index: i };
					}),
				};
			},

			items: function (cat, secIdx) {
				const c = menu[cat];
				const sec = c && c.sections[secIdx];
				if (!sec) return { category: cat, section: null, items: [] };
				return {
					category: cat,
					section: sec.label,
					items: sec.items.map(function (title) {
						const coll = collections[title];
						return {
							title: title,
							subtitle: coll ? coll.subtitle : "",
							id: coll ? coll.id : null,
							exists: !!coll,
						};
					}),
				};
			},

			findCollection: function (title) {
				return collections[title] || null;
			},
		};
	}

	const api = { createNavigator: createNavigator };

	if (typeof module !== "undefined" && module.exports) {
		module.exports = api;
	}
	global.BloomBrowse = api;
})(typeof window !== "undefined" ? window : globalThis);

const BLOOM_GIFTS = {
	"updated_at": "2026-11-04T11:17:14.561Z",
	"total_collections": 91,
	"total_unique_products": 372,
	"products": {
		"106": {
			"title": "106 Chromatic Rose",
			"brand": "Bon Parfumeur",
			"price": 115,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/106_442ec6b9-c82e-459c-9c47-dbeb3c3a6434_500x.jpg?v=1779898934",
			"gender": "Female",
			"collection_handle": "bon-parfumeur",
			"price_ol": 115,
			"price_ol_ml": 100,
			"price_tf": 95.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"202": {
			"title": "202 Frutti Solari",
			"brand": "Bon Parfumeur",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/202_500x.jpg?v=1779904762",
			"gender": "Unisex",
			"collection_handle": "bon-parfumeur",
			"price_ol": 60,
			"price_ol_ml": 30,
			"price_tf": 50,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"301": {
			"title": "301 Noces de Santal",
			"brand": "Bon Parfumeur",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/301_500x.jpg?v=1779905209",
			"gender": "Unisex",
			"collection_handle": "bon-parfumeur",
			"price_ol": 60,
			"price_ol_ml": 30,
			"price_tf": 50,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"302": {
			"title": "302 Groovy Amber",
			"brand": "Bon Parfumeur",
			"price": 115,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/302_500x.jpg?v=1779905244",
			"gender": "Unisex",
			"collection_handle": "bon-parfumeur",
			"price_ol": 115,
			"price_ol_ml": 100,
			"price_tf": 95.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"565": {
			"title": "565",
			"brand": "SweDoft",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/565_3ea757ea-98c0-44ea-a3ac-b4921c01b8a6_500x.jpg?v=1632583902",
			"gender": "Unisex",
			"collection_handle": "swedoft",
			"price_ol": 120,
			"price_ol_ml": 100,
			"price_tf": 100,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"602": {
			"title": "602 Bois Narcotique",
			"brand": "Bon Parfumeur",
			"price": 115,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/602_500x.jpg?v=1779906219",
			"gender": "Male",
			"collection_handle": "bon-parfumeur",
			"price_ol": 115,
			"price_ol_ml": 100,
			"price_tf": 95.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"803": {
			"title": "803 Givre Patchouli",
			"brand": "Bon Parfumeur",
			"price": 55,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/803_500x.jpg?v=1779898265",
			"gender": "Unisex",
			"collection_handle": "bon-parfumeur",
			"price_ol": 55,
			"price_ol_ml": 30,
			"price_tf": 45.83,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"1993": {
			"title": "1993",
			"brand": "Mabra",
			"price": 75,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/1993_500x.jpg?v=1750077107",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 75,
			"price_ol_ml": 30,
			"price_tf": 62.5,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"20359": {
			"title": "20359",
			"brand": "32.JULI",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/juli_20359_500x.jpg?v=1788782995",
			"gender": "Unisex",
			"collection_handle": "32-juli",
			"price_ol": 155,
			"price_ol_ml": 50,
			"price_tf": 129.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"vanille-glace": {
			"title": "Vanille Glacé",
			"brand": "Insenf",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/vanille_500x.jpg?v=1783068498",
			"gender": "Unisex",
			"collection_handle": "insenf",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"ruby": {
			"title": "Ruby",
			"brand": "Kajal",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ruby_500x.jpg?v=1765390184",
			"gender": "Unisex",
			"collection_handle": "kajal",
			"price_ol": 195,
			"price_ol_ml": 100,
			"price_tf": 162.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"vanhera": {
			"title": "Vanhera",
			"brand": "Laboratorio Olfattivo",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/vanhera_5e196cc6-073e-4f33-aff5-86840e8d5535_500x.jpg?v=1669649566",
			"gender": "Unisex",
			"collection_handle": "laboratorio-olfattivo",
			"price_ol": 120,
			"price_ol_ml": 100,
			"price_tf": 100,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"happy-nuts": {
			"title": "NUTS (formerly known as HAPPY NUTS)",
			"brand": "BORNTOSTANDOUT",
			"price": 182,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/HAPPYNUTS_BOTTLE_500x.png?v=1710143839",
			"gender": "Unisex",
			"collection_handle": "born-to-stand-oud",
			"price_ol": 182,
			"price_ol_ml": 50,
			"price_tf": 151.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"sadonaso": {
			"title": "Sadonaso",
			"brand": "Nasomatto",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/sadonaso_500x.jpg?v=1756321000",
			"gender": "Unisex",
			"collection_handle": "nasomatto",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"nightfall-in-the-city-of-kings": {
			"title": "Nightfall in the City of Kings",
			"brand": "Mystikum",
			"price": 190,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mystikum_nightfallt_500x.jpg?v=1776444005",
			"gender": "Unisex",
			"collection_handle": "mystikum",
			"price_ol": 190,
			"price_ol_ml": 100,
			"price_tf": 158.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"dark-honey": {
			"title": "Dark Honey",
			"brand": "Ourte",
			"price": 115,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/dark_honey_new_500x.jpg?v=1792512680",
			"gender": "Unisex",
			"collection_handle": "ourte",
			"price_ol": 115,
			"price_ol_ml": 50,
			"price_tf": 95.83,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"mooncake": {
			"title": "Mooncake | 月餅",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/bc-mooncake_500x.jpg?v=1760113353",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"nebula-viridis": {
			"title": "Nebula Viridis",
			"brand": "Aura Eximia",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/23d9cc_f5c43f2bb55f4efbb06d6e3623690bcb_mv2_1_500x.png?v=1785402457",
			"gender": "Unisex",
			"collection_handle": "aura-eximia",
			"price_ol": 165,
			"price_ol_ml": 50,
			"price_tf": 137.5,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"masa": {
			"title": "Masa",
			"brand": "Kajal",
			"price": 175,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/masa_500x.jpg?v=1765389953",
			"gender": "Unisex",
			"collection_handle": "kajal",
			"price_ol": 175,
			"price_ol_ml": 100,
			"price_tf": 145.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"resonant": {
			"title": "Resonant",
			"brand": "Oddity",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/resonant_oddity_500x.jpg?v=1678910627",
			"gender": "Unisex",
			"collection_handle": "oddity",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"sal-y-limon": {
			"title": "Sal y Limón",
			"brand": "CARNER",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/carner_sal_y_limon_500x.jpg?v=1660318127",
			"gender": "Unisex",
			"collection_handle": "carner",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"a-grove-by-the-sea": {
			"title": "A Grove By The Sea",
			"brand": "Arquiste",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ARQUISTE_A_GROVE_BY_THE_SEA_500x.jpg?v=1722411367",
			"gender": "Unisex",
			"collection_handle": "arquiste",
			"price_ol": 195,
			"price_ol_ml": 100,
			"price_tf": 162.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"neroli-petitgrain": {
			"title": "Neroli & Petitgrain",
			"brand": "100BON",
			"price": 35,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/neroli_petit_grain_500x.jpg?v=1731682971",
			"gender": "Unisex",
			"collection_handle": "100bon",
			"price_ol": 35,
			"price_ol_ml": 50,
			"price_tf": 29.17,
			"price_tf_ml": 50,
			"concentration": "EdC"
		},
		"thirsty": {
			"title": "Thirsty",
			"brand": "Fugazzi",
			"price": 205,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/100_thirsty_500x.png?v=1745859441",
			"gender": "Unisex",
			"collection_handle": "fugazzi",
			"price_ol": 205,
			"price_ol_ml": 100,
			"price_tf": 170.83,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"portamarina": {
			"title": "Portamarina",
			"brand": "Agatho",
			"price": 305,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/portamania_500x.jpg?v=1764500453",
			"gender": "Unisex",
			"collection_handle": "agatho",
			"price_ol": 305,
			"price_ol_ml": 100,
			"price_tf": 254.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"pg16-jardins-de-kerylos": {
			"title": "PG16 Jardins de Kérylos",
			"brand": "Pierre Guillaume - Parfumerie Générale",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/PG16_Jardin_de_Kerylos_500x.jpg?v=1589662162",
			"gender": "Female",
			"collection_handle": "pierre-guillaume-parfumerie-generale",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"playground-1": {
			"title": "Playground",
			"brand": "Der Duft",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/der_duft_playground_6443ec99-dc9f-44c5-8c9a-3733b5c80df2_500x.png?v=1747051567",
			"gender": "Unisex",
			"collection_handle": "der-duft",
			"price_ol": 110,
			"price_ol_ml": 50,
			"price_tf": 91.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"moonlight-ceremony": {
			"title": "Moonlight Ceremony",
			"brand": "Mystikum",
			"price": 190,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mystikum_moonlight_500x.jpg?v=1776443717",
			"gender": "Unisex",
			"collection_handle": "mystikum",
			"price_ol": 190,
			"price_ol_ml": 100,
			"price_tf": 158.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"black": {
			"title": "Black",
			"brand": "Comme Des Garcons",
			"price": 95,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/black_8652c882-6fb1-47d4-b691-a30985352886_500x.jpg?v=1769330871",
			"gender": "Unisex",
			"collection_handle": "comme-des-garcons",
			"price_ol": 95,
			"price_ol_ml": 100,
			"price_tf": 79.17,
			"price_tf_ml": 100,
			"concentration": "EdT"
		},
		"oud-imperial-extrait": {
			"title": "Oud Imperial Extrait",
			"brand": "Perris Monte Carlo",
			"price": 250,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Extrait-Oud-Imperial_500x.jpg?v=1609164109",
			"gender": "Unisex",
			"collection_handle": "perris-monte-carlo",
			"price_ol": 250,
			"price_ol_ml": 50,
			"price_tf": 208.33,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"vi-et-armis": {
			"title": "Vi et Armis",
			"brand": "Beaufort",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/BeauFort_London_Vi_et_Armis_Bloom_500x.jpg?v=1623765299",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"corpus-equus": {
			"title": "Corpus Equus",
			"brand": "Naomi Goodsir",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/NGP_25252BCORPUS_25252BEQUUS_500x.jpg?v=1633940026",
			"gender": "Unisex",
			"collection_handle": "naomi-goodsir",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"honey-and-tar": {
			"title": "Honey and Tar",
			"brand": "Anna Zworykina",
			"price": 95,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/anna_honey_500x.jpg?v=1777360645",
			"gender": "Unisex",
			"collection_handle": "anna-zworykina",
			"price_ol": 95,
			"price_ol_ml": 10,
			"price_tf": 79.17,
			"price_tf_ml": 10,
			"concentration": "EdP"
		},
		"beso-de-fuego": {
			"title": "Beso de Fuego",
			"brand": "Beso Beach",
			"price": 145,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/beso_fuego_500x.jpg?v=1783354059",
			"gender": "Unisex",
			"collection_handle": "beso-beach",
			"price_ol": 145,
			"price_ol_ml": 100,
			"price_tf": 120.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"cruelle": {
			"title": "Cruelle",
			"brand": "Wesker",
			"price": 220,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Cruelle-Product-Page-1800-1_500x.jpg?v=1747312888",
			"gender": "Unisex",
			"collection_handle": "wesker",
			"price_ol": 220,
			"price_ol_ml": 75,
			"price_tf": 183.33,
			"price_tf_ml": 75,
			"concentration": "Extrait de Parfum"
		},
		"cuoium": {
			"title": "Cuoium",
			"brand": "Orto Parisi",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/orto_cuoium_275a3644-0d9d-41e9-8901-669b0ab2542d_500x.jpg?v=1758620326",
			"gender": "Unisex",
			"collection_handle": "orto-parisi",
			"price_ol": 155,
			"price_ol_ml": 50,
			"price_tf": 129.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"cashmere-100": {
			"title": "Cashmere 100",
			"brand": "Insenf",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/cashmere_500x.jpg?v=1783068412",
			"gender": "Unisex",
			"collection_handle": "insenf",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"white-rice": {
			"title": "White Rice",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/White-Rice_500x.jpg?v=1746626005",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"pg-41-le-musc-la-peau": {
			"title": "PG4.1 Le Musc & La Peau",
			"brand": "Pierre Guillaume - Parfumerie Générale",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/le_musc_at_la_peau_pierre_guillaume_500x.jpg?v=1589662178",
			"gender": "Female",
			"collection_handle": "pierre-guillaume-parfumerie-generale",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"the-ghost-in-the-shell": {
			"title": "The Ghost In The Shell",
			"brand": "Etat Libre d'Orange",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Ghost-in-the-Shell-flacon-100-ml_1800x1800_6a38eab9-76bd-4fc7-a90d-b9d697d75280_500x.jpg?v=1632307386",
			"gender": "Female",
			"collection_handle": "etat-libre-dorange",
			"price_ol": 155,
			"price_ol_ml": 100,
			"price_tf": 129.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"les-cahiers-secrets": {
			"title": "Les Cahiers Secrets (Secret Diaries)",
			"brand": "Jouissance",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/les_cahiers_500x.jpg?v=1792439386",
			"gender": "Female",
			"collection_handle": "jouissance",
			"price_ol": 180,
			"price_ol_ml": 50,
			"price_tf": 150,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"merina": {
			"title": "Merina",
			"brand": "Otzlab",
			"price": 240,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/merina_500x.jpg?v=1781437552",
			"gender": "Unisex",
			"collection_handle": "otzlab",
			"price_ol": 240,
			"price_ol_ml": 40,
			"price_tf": 200,
			"price_tf_ml": 40,
			"concentration": "Extrait de Parfum"
		},
		"yawahada": {
			"title": "Yawahada (Soft skin)",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/yawahada-01_500x.jpg?v=1741339211",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"blanc-sada": {
			"title": "Blanc Sada",
			"brand": "L'Entropiste",
			"price": 185,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/blanc_sada_500x.jpg?v=1766576180",
			"gender": "Unisex",
			"collection_handle": "lentropiste",
			"price_ol": 185,
			"price_ol_ml": 50,
			"price_tf": 154.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"intime-extime": {
			"title": "Intime-Extime",
			"brand": "Pierre Guillaume White Collection",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/PIERRE-GUILLAUME-PARIS-PARFUM-COLLECTION-BLANCHE-INTIME-EXTIME-100ML_500x.png?v=1652202927",
			"gender": "Unisex",
			"collection_handle": "pierre-guillaume-white-collection",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"jany": {
			"title": "Jany",
			"brand": "Sora Dora",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/jany_500x.jpg?v=1781697454",
			"gender": "Unisex",
			"collection_handle": "sora-dora",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"vietnamese-coffee": {
			"title": "Vietnamese Coffee",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Vietnamese-Coffee_500x.jpg?v=1746626102",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"celestial-object": {
			"title": "Celestial Object",
			"brand": "Liis",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Liis_Silo_CelestialObject_500x.jpg?v=1764835866",
			"gender": "Unisex",
			"collection_handle": "liis",
			"price_ol": 160,
			"price_ol_ml": 50,
			"price_tf": 133.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"calisson": {
			"title": "Calisson",
			"brand": "Karzaz",
			"price": 69,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/calisson_500x.jpg?v=1773068174",
			"gender": "Unisex",
			"collection_handle": "karzaz",
			"price_ol": 69,
			"price_ol_ml": 50,
			"price_tf": 57.5,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"vanilla-for-breakfast": {
			"title": "Vanilla For Breakfast",
			"brand": "French Cowboy",
			"price": 90,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/vanilla_for_break_02dfd976-5533-49e3-b5d3-a6e77b00306f_500x.jpg?v=1783184168",
			"gender": "Unisex",
			"collection_handle": "french-cowboy",
			"price_ol": 90,
			"price_ol_ml": 100,
			"price_tf": 75,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"davana-vanille": {
			"title": "Davana & Vanille",
			"brand": "100BON",
			"price": 35,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/davana_vanilla_500x.jpg?v=1731682971",
			"gender": "Female",
			"collection_handle": "100bon",
			"price_ol": 35,
			"price_ol_ml": 50,
			"price_tf": 29.17,
			"price_tf_ml": 50,
			"concentration": "EdC"
		},
		"dominanti-santi": {
			"title": "Dominanti Santi",
			"brand": "Santi Peccatori",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/dominanti_500x.jpg?v=1786728365",
			"gender": "Unisex",
			"collection_handle": "santi-peccatori",
			"price_ol": 180,
			"price_ol_ml": 100,
			"price_tf": 150,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"yuzu": {
			"title": "Yuzu",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/yuzu-01_500x.jpg?v=1741339275",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"this-is-not-a-blue-bottle-1-6": {
			"title": "This is Not a Blue Bottle 1/.6 – Love",
			"brand": "Histoires de Parfums",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/not_a_blue_bottle_06_74efa7f4-ba07-4d26-896d-2e1761e7458f_500x.jpg?v=1757946455",
			"gender": "Unisex",
			"collection_handle": "histoires-de-parfums",
			"price_ol": 165,
			"price_ol_ml": 115,
			"price_tf": 137.5,
			"price_tf_ml": 115,
			"concentration": "EdP"
		},
		"kajal-iii": {
			"title": "Kajal III",
			"brand": "Kajal",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/kajal_3_500x.jpg?v=1786565193",
			"gender": "Male",
			"collection_handle": "kajal",
			"price_ol": 195,
			"price_ol_ml": 100,
			"price_tf": 162.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"elisa": {
			"title": "Elisa",
			"brand": "Marie Jeanne",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/elisa_500x.jpg?v=1758616405",
			"gender": "Unisex",
			"collection_handle": "marie-jeanne",
			"price_ol": 110,
			"price_ol_ml": 100,
			"price_tf": 91.67,
			"price_tf_ml": 100,
			"concentration": "EdT"
		},
		"still-life-in-rio": {
			"title": "Still Life in Rio",
			"brand": "Olfactive Studio",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/still_life_in_Rio_500x.jpg?v=1589665496",
			"gender": "Unisex",
			"collection_handle": "olfactive-studio",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"en-plein-air": {
			"title": "En Plein Air",
			"brand": "Jouissance",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/plein_air_500x.jpg?v=1792413754",
			"gender": "Unisex",
			"collection_handle": "jouissance",
			"price_ol": 180,
			"price_ol_ml": 50,
			"price_tf": 150,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"ramune": {
			"title": "Ramune (Japanese soda)",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ramune-01_500x.jpg?v=1741339409",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"santal": {
			"title": "Santal",
			"brand": "Floris",
			"price": 100,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/floris_santal_500x.jpg?v=1787327450",
			"gender": "Unisex",
			"collection_handle": "floris",
			"price_ol": 100,
			"price_ol_ml": 100,
			"price_tf": 83.33,
			"price_tf_ml": 100,
			"concentration": "EdT"
		},
		"afrique-en-fleurs": {
			"title": "Afrique en Fleurs",
			"brand": "Reserve en Afrique 1934",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/1.Reserve_en_Afrique_Afrique_en_Fleurs_100ml_Bottle_500x.png?v=1754905382",
			"gender": "Unisex",
			"collection_handle": "reserve-en-afrique-1934",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"isla-bohemia": {
			"title": "Isla Bohemia",
			"brand": "CARNER",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/carner_isla_bohemia_500x.jpg?v=1781197324",
			"gender": "Unisex",
			"collection_handle": "carner",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"figlia-di-eva": {
			"title": "Figlia di Eva",
			"brand": "SweDoft",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/FIGLIA-DI-EVA_500x.jpg?v=1632583623",
			"gender": "Unisex",
			"collection_handle": "swedoft",
			"price_ol": 110,
			"price_ol_ml": 100,
			"price_tf": 91.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"tobacco-tuberose": {
			"title": "Tobacco Tuberose",
			"brand": "Anna Zworykina",
			"price": 95,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/anna_tobaco_500x.jpg?v=1777368741",
			"gender": "Unisex",
			"collection_handle": "anna-zworykina",
			"price_ol": 95,
			"price_ol_ml": 10,
			"price_tf": 79.17,
			"price_tf_ml": 10,
			"concentration": "EdP"
		},
		"mad": {
			"title": "Mad",
			"brand": "And Fragrance",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/MAD-50ML-MASTER_500x.jpg?v=1717843533",
			"gender": "Unisex",
			"collection_handle": "and-fragrance",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"rose-shiso": {
			"title": "Rose Shiso ローズ シソ",
			"brand": "Marie Jeanne",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/MARIEJEANNE_Perfumes_Rose-Shiso_zoom_500x.jpg?v=1738674696",
			"gender": "Unisex",
			"collection_handle": "marie-jeanne",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"concrete": {
			"title": "Concrete",
			"brand": "Comme Des Garcons",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/concrete_500x.jpg?v=1769329773",
			"gender": "Unisex",
			"collection_handle": "comme-des-garcons",
			"price_ol": 160,
			"price_ol_ml": 80,
			"price_tf": 133.33,
			"price_tf_ml": 80,
			"concentration": "EdP"
		},
		"neon-night": {
			"title": "Neon Night",
			"brand": "MINT",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/neon_night_500x.jpg?v=1761555011",
			"gender": "Unisex",
			"collection_handle": "mint",
			"price_ol": 170,
			"price_ol_ml": 70,
			"price_tf": 141.67,
			"price_tf_ml": 70,
			"concentration": "EdP"
		},
		"the-empress-kiss": {
			"title": "The Empress Kiss",
			"brand": "Mystikum",
			"price": 190,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Mystikum_empress_b7a030d0-8b09-4b98-b2bd-3627cec7577c_500x.jpg?v=1776443907",
			"gender": "Unisex",
			"collection_handle": "mystikum",
			"price_ol": 190,
			"price_ol_ml": 100,
			"price_tf": 158.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"ambre-royal": {
			"title": "Ambre Royal",
			"brand": "Ormonde Jayne",
			"price": 235,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ambre-royal-88ml-front_1020x1530_1_500x.jpg?v=1711454095",
			"gender": "Unisex",
			"collection_handle": "ormonde-jayne",
			"price_ol": 235,
			"price_ol_ml": 88,
			"price_tf": 195.83,
			"price_tf_ml": 88,
			"concentration": "EdP"
		},
		"summer-street": {
			"title": "Summer Street",
			"brand": "Brandt",
			"price": 185,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/summer_street_500x.jpg?v=1761235919",
			"gender": "Unisex",
			"collection_handle": "brandt",
			"price_ol": 185,
			"price_ol_ml": 100,
			"price_tf": 154.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"malli-nadu": {
			"title": "Malli Nadu",
			"brand": "Nissaba",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mali_nadu_500x.jpg?v=1757077513",
			"gender": "Female",
			"collection_handle": "nissaba",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"myrrh-blood-drop": {
			"title": "No. 8 – Drop of Blood",
			"brand": "Binet-Papillon",
			"price": 215,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/binet-papillon-8_500x.jpg?v=1764007714",
			"gender": "Unisex",
			"collection_handle": "binet-papillon",
			"price_ol": 215,
			"price_ol_ml": 100,
			"price_tf": 179.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"au-pied-du-rosier": {
			"title": "Au Pied du Rosier",
			"brand": "Marie Jeanne",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/MARIEJEANNE_Perfumes_Au-pied-du-rosier_500x.jpg?v=1738674757",
			"gender": "Unisex",
			"collection_handle": "marie-jeanne",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"bohemian": {
			"title": "Bohemian",
			"brand": "SweDoft",
			"price": 185,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/BOHEMIAN_cd233cae-ece3-46c3-8b18-bd8d33a2cb45_500x.jpg?v=1632582931",
			"gender": "Unisex",
			"collection_handle": "swedoft",
			"price_ol": 185,
			"price_ol_ml": 100,
			"price_tf": 154.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"eye-contact": {
			"title": "Eye Contact",
			"brand": "Discothèque",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/eye_300ecb6f-3af2-4b4f-b910-737ee4c3e5d8_500x.jpg?v=1768129262",
			"gender": "Unisex",
			"collection_handle": "discotheque",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"fauno": {
			"title": "Fauno",
			"brand": "Agatho",
			"price": 305,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/fauno_500x.jpg?v=1657827144",
			"gender": "Unisex",
			"collection_handle": "agatho",
			"price_ol": 305,
			"price_ol_ml": 100,
			"price_tf": 254.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"veles": {
			"title": "Veles",
			"brand": "Wolf Brothers",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/WB-VELES-PERFUME-50ml_932cf17a-af19-4d67-baa6-6dd1018bb352_500x.jpg?v=1742991780",
			"gender": "",
			"collection_handle": "wolf-brothers",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"athanor": {
			"title": "Athanor",
			"brand": "Mendittorosa",
			"price": 430,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Mendittorosa-Athanor-01_500x.jpg?v=1625873603",
			"gender": "Unisex",
			"collection_handle": "mendittorosa",
			"price_ol": 430,
			"price_ol_ml": 100,
			"price_tf": 358.33,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"santorini": {
			"title": "Santorini",
			"brand": "Ampersand",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/santorini_500x.jpg?v=1778351025",
			"gender": "Unisex",
			"collection_handle": "ampersand",
			"price_ol": 60,
			"price_ol_ml": 50,
			"price_tf": 50,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"2025-bestsellers-for-him": {
			"title": "2025 Bestsellers | For Him",
			"brand": "Bloom Sample Packs",
			"price": 49,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/bestsellers_2025_for_him_500x.jpg?v=1765740640",
			"gender": "Unisex",
			"collection_handle": "bloom-sample-packs",
			"price_ol": 49,
			"price_ol_ml": 9,
			"price_tf": 40.83,
			"price_tf_ml": 9,
			"concentration": "9"
		},
		"vanille-creme": {
			"title": "Vanille Creme",
			"brand": "Karzaz",
			"price": 69,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/vanille_cream_500x.jpg?v=1773068117",
			"gender": "Unisex",
			"collection_handle": "karzaz",
			"price_ol": 69,
			"price_ol_ml": 50,
			"price_tf": 57.5,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"blueforest": {
			"title": "Blueforest",
			"brand": "Perroy",
			"price": 70,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/perroy_blueforest_500x.jpg?v=1781190511",
			"gender": "Unisex",
			"collection_handle": "perroy",
			"price_ol": 70,
			"price_ol_ml": 50,
			"price_tf": 58.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"fiery-fig-24-05": {
			"title": "Fiery Fig 24.05",
			"brand": "Neydo",
			"price": 80,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/neydo_fiery_500x.jpg?v=1770373380",
			"gender": "Unisex",
			"collection_handle": "neydo",
			"price_ol": 80,
			"price_ol_ml": 50,
			"price_tf": 66.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"figo": {
			"title": "Figo",
			"brand": "Granado",
			"price": 65,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/granado_figo_608a1cf1-c948-4858-999c-b18b53950242_500x.jpg?v=1753451126",
			"gender": "Unisex",
			"collection_handle": "granado",
			"price_ol": 65,
			"price_ol_ml": 100,
			"price_tf": 54.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"niche-discovery-box": {
			"title": "Niche Discovery Box",
			"brand": "Ortir Apothecari",
			"price": 30,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/IMG_8890_2200x_b3129e42-76aa-44e9-a0ef-31225d89b658_500x.jpg?v=1711569326",
			"gender": "Unisex",
			"collection_handle": "ortir-apothecari",
			"price_ol": 30,
			"price_ol_ml": 4,
			"price_tf": 25,
			"price_tf_ml": 4,
			"concentration": "EdP"
		},
		"patchouli-labdanum": {
			"title": "Patchouli & Labdanum",
			"brand": "100BON",
			"price": 35,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/pathouli_labdanum_500x.jpg?v=1731682971",
			"gender": "Unisex",
			"collection_handle": "100bon",
			"price_ol": 35,
			"price_ol_ml": 50,
			"price_tf": 29.17,
			"price_tf_ml": 50,
			"concentration": "EdC"
		},
		"2025-bestsellers-for-her": {
			"title": "2025 Bestsellers | For Her",
			"brand": "Bloom Sample Packs",
			"price": 49,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/bestsellers_2025_for_her_500x.jpg?v=1765740616",
			"gender": "Female",
			"collection_handle": "bloom-sample-packs",
			"price_ol": 49,
			"price_ol_ml": 9,
			"price_tf": 40.83,
			"price_tf_ml": 9,
			"concentration": "9"
		},
		"exploration-kit": {
			"title": "Exploration Kit",
			"brand": "Commodity",
			"price": 38,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/commodity_kit_500x.jpg?v=1755167390",
			"gender": "Unisex",
			"collection_handle": "commodity",
			"price_ol": 38,
			"price_ol_ml": 18,
			"price_tf": 31.67,
			"price_tf_ml": 18,
			"concentration": "EdP"
		},
		"j-scent-discovery-set": {
			"title": "J-Scent Discovery Set",
			"brand": "J-Scent",
			"price": 40,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/j-scent-discovery-set_angle_30f4af3e-a50a-4441-960a-774f28462eef_500x.jpg?v=1775226811",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 40,
			"price_ol_ml": 6,
			"price_tf": 33.33,
			"price_tf_ml": 6,
			"concentration": "EdP"
		},
		"cherry-passion": {
			"title": "Cherry Passion",
			"brand": "Sly John's Lab",
			"price": 85,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/CHERRY_20PASSION-1006x698_2f9e4c76-a475-43da-8314-d6cb75d71697_500x.jpg?v=1661530569",
			"gender": "Unisex",
			"collection_handle": "sly-johns-lab",
			"price_ol": 85,
			"price_ol_ml": 100,
			"price_tf": 70.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"fig-lemonade": {
			"title": "Fig Lemonade",
			"brand": "French Cowboy",
			"price": 90,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/fig_lemonade_73b129dd-8c1b-4caf-9eec-0bdd3adb9e3d_500x.jpg?v=1783184407",
			"gender": "Unisex",
			"collection_handle": "french-cowboy",
			"price_ol": 90,
			"price_ol_ml": 100,
			"price_tf": 75,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"cloud-essence-28-08": {
			"title": "Cloud Essence 28.08",
			"brand": "Neydo",
			"price": 80,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/neydo_cloud_1a934fe1-5740-42fa-8bcb-3b97d3db4657_500x.jpg?v=1770373674",
			"gender": "Unisex",
			"collection_handle": "neydo",
			"price_ol": 80,
			"price_ol_ml": 50,
			"price_tf": 66.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"series-3-incense-avignon": {
			"title": "Series 3: Incense | Avignon",
			"brand": "Comme Des Garcons",
			"price": 90,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/avignon_500x.jpg?v=1769331129",
			"gender": "Unisex",
			"collection_handle": "comme-des-garcons",
			"price_ol": 90,
			"price_ol_ml": 50,
			"price_tf": 75,
			"price_tf_ml": 50,
			"concentration": "EdT"
		},
		"osmanthus-oolong": {
			"title": "Osmanthus & Oolong",
			"brand": "NuWilds",
			"price": 80,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/oolong_500x.jpg?v=1788191620",
			"gender": "Unisex",
			"collection_handle": "nu-wilds",
			"price_ol": 80,
			"price_ol_ml": 30,
			"price_tf": 66.67,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"a-pas-de-loup": {
			"title": "À Pas De Loup (On Tiptoe)",
			"brand": "Milleseve",
			"price": 115,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/APDL-100ml---3_500x.jpg?v=1789384159",
			"gender": "Unisex",
			"collection_handle": "milleseve",
			"price_ol": 115,
			"price_ol_ml": 100,
			"price_tf": 95.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"afternoon-tea": {
			"title": "Afternoon Tea",
			"brand": "Exaltatum",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/tea_500x.jpg?v=1780922426",
			"gender": "Unisex",
			"collection_handle": "exaltatum",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"arashiyama": {
			"title": "Arashiyama | 嵐山",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Arashiyama_500x.jpg?v=1746626744",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"deer": {
			"title": "Deer",
			"brand": "Wolf Brothers",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/deer_wolf-brothers_500x.jpg?v=1671450860",
			"gender": "Unisex",
			"collection_handle": "wolf-brothers",
			"price_ol": 120,
			"price_ol_ml": 50,
			"price_tf": 100,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"milk": {
			"title": "Milk | Personal",
			"brand": "Commodity",
			"price": 135,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Milk_45819a57-e3fc-46b8-8b01-faed8e357ad3_500x.png?v=1740651017",
			"gender": "Unisex",
			"collection_handle": "commodity",
			"price_ol": 135,
			"price_ol_ml": 100,
			"price_tf": 112.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"paper-soap": {
			"title": "Soap Paper",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/papersoap-01_500x.jpg?v=1741339437",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"pg31-tonka-bodycon": {
			"title": "PG31 Tonka Bodykon",
			"brand": "Pierre Guillaume - Parfumerie Générale",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/tonkabodykon_500x.jpg?v=1668777890",
			"gender": "Unisex",
			"collection_handle": "pierre-guillaume-parfumerie-generale",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"rouge-karkade": {
			"title": "Rouge Karkade",
			"brand": "Reserve en Afrique 1934",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/1.RougeKarkade_Bottle_500x.png?v=1754905004",
			"gender": "Unisex",
			"collection_handle": "reserve-en-afrique-1934",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"sample-set-1": {
			"title": "Sample set",
			"brand": "Stora Skuggan",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/sample_500x.jpg?v=1764954233",
			"gender": "Unisex",
			"collection_handle": "stora-skuggan",
			"price_ol": 60,
			"price_ol_ml": 9,
			"price_tf": 50,
			"price_tf_ml": 9,
			"concentration": "EdP"
		},
		"dark-drunk-discovery-kit": {
			"title": "DARK & DRUNK DISCOVERY KIT",
			"brand": "BORNTOSTANDOUT",
			"price": 61,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/dark_drunk_500x.jpg?v=1706518116",
			"gender": "",
			"collection_handle": "born-to-stand-oud",
			"price_ol": 61,
			"price_ol_ml": 8,
			"price_tf": 50.83,
			"price_tf_ml": 8,
			"concentration": "EdP"
		},
		"oddity-discovery-set": {
			"title": "Oddity discovery set",
			"brand": "Oddity",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Oddity_set_500x.jpg?v=1755168359",
			"gender": "Unisex",
			"collection_handle": "oddity",
			"price_ol": 60,
			"price_ol_ml": 4,
			"price_tf": 50,
			"price_tf_ml": 4,
			"concentration": "EdP"
		},
		"osang": {
			"title": "Osang",
			"brand": "Mendittorosa",
			"price": 315,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Osang_1_1500px_wyszparowany_500x.jpg?v=1614678157",
			"gender": "Unisex",
			"collection_handle": "mendittorosa",
			"price_ol": 315,
			"price_ol_ml": 100,
			"price_tf": 262.5,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"cavallo-di-fuoco": {
			"title": "火马 - Cavallo di fuoco",
			"brand": "Zanini",
			"price": 210,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/cavalio_500x.jpg?v=1789409626",
			"gender": "Unisex",
			"collection_handle": "zanini",
			"price_ol": 210,
			"price_ol_ml": 30,
			"price_tf": 175,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"black-oregano": {
			"title": "Black Oregano",
			"brand": "Atelier Materi",
			"price": 240,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/black_oregano_500x.jpg?v=1781694266",
			"gender": "Unisex",
			"collection_handle": "atelier-materi",
			"price_ol": 240,
			"price_ol_ml": 50,
			"price_tf": 200,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"rouge": {
			"title": "Rouge",
			"brand": "Comme Des Garcons",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/comme_rouge_500x.jpg?v=1773779864",
			"gender": "Unisex",
			"collection_handle": "comme-des-garcons",
			"price_ol": 160,
			"price_ol_ml": 100,
			"price_tf": 133.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"boar": {
			"title": "Boar",
			"brand": "Wolf Brothers",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/boar_wolf-brothers_500x.jpg?v=1671451038",
			"gender": "Unisex",
			"collection_handle": "wolf-brothers",
			"price_ol": 120,
			"price_ol_ml": 50,
			"price_tf": 100,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"indian-grass": {
			"title": "Indian Grass",
			"brand": "Sly John's Lab",
			"price": 85,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/INDIAN_20GRASS-1006x698_a5a51507-cdbd-4a46-8a1c-4e9fbb575541_500x.jpg?v=1661530659",
			"gender": "Unisex",
			"collection_handle": "sly-johns-lab",
			"price_ol": 85,
			"price_ol_ml": 100,
			"price_tf": 70.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"monopteros": {
			"title": "Monopteros",
			"brand": "Der Duft",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Foto_DER_DUFT_MONOPTEROS_Schatten_74d5be6e-e22b-4182-a819-c577107bbf21_500x.jpg?v=1625874166",
			"gender": "Unisex",
			"collection_handle": "der-duft",
			"price_ol": 110,
			"price_ol_ml": 50,
			"price_tf": 91.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"v-i-t-r-i-o-l": {
			"title": "V.I.T.R.I.O.L.",
			"brand": "Maqueda",
			"price": 290,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/maqueda_r_500x.jpg?v=1776442490",
			"gender": "Unisex",
			"collection_handle": "maqueda",
			"price_ol": 290,
			"price_ol_ml": 50,
			"price_tf": 241.67,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"dawn-whispers": {
			"title": "Dawn Whispers",
			"brand": "L'Entropiste",
			"price": 185,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/dawn_500x.jpg?v=1766576329",
			"gender": "Unisex",
			"collection_handle": "lentropiste",
			"price_ol": 185,
			"price_ol_ml": 50,
			"price_tf": 154.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"rainy-city": {
			"title": "Rainy City",
			"brand": "MINT",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/rainy_city_500x.jpg?v=1737306038",
			"gender": "Unisex",
			"collection_handle": "mint",
			"price_ol": 170,
			"price_ol_ml": 70,
			"price_tf": 141.67,
			"price_tf_ml": 70,
			"concentration": "EdP"
		},
		"flow": {
			"title": "Flow",
			"brand": "Stereotype",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Sterotype_Flow_500x.jpg?v=1776443110",
			"gender": "Female",
			"collection_handle": "stereotype",
			"price_ol": 130,
			"price_ol_ml": 75,
			"price_tf": 108.33,
			"price_tf_ml": 75,
			"concentration": "EdP"
		},
		"seychelles-breeze": {
			"title": "Seychelles Breeze",
			"brand": "Sly John's Lab",
			"price": 85,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/SEYCHELLES_20BREEZE-1006x698_e327e3fa-1246-4576-93c2-25060e9b85c2_500x.jpg?v=1661531038",
			"gender": "Unisex",
			"collection_handle": "sly-johns-lab",
			"price_ol": 85,
			"price_ol_ml": 100,
			"price_tf": 70.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"lumire-blanche": {
			"title": "Lumiere Blanche",
			"brand": "Olfactive Studio",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/lb_500x.jpg?v=1589662069",
			"gender": "Unisex",
			"collection_handle": "olfactive-studio",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"albatros": {
			"title": "Albatros",
			"brand": "Mendittorosa",
			"price": 200,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mendittorosa-albatros-01_8f217198-cb0c-4628-8842-6e5eed3c23bc_500x.jpg?v=1705512881",
			"gender": "Unisex",
			"collection_handle": "mendittorosa",
			"price_ol": 200,
			"price_ol_ml": 100,
			"price_tf": 166.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"vetiver-santal": {
			"title": "Vetiver Santal",
			"brand": "Marie Jeanne",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/MARIEJEANNE_Perfumes_Vetiver-Santal_shadow_500x.jpg?v=1738671355",
			"gender": "Unisex",
			"collection_handle": "marie-jeanne",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"pg191-neroli-ad-astra": {
			"title": "PG19.1 Neroli ad Astra",
			"brand": "Pierre Guillaume - Parfumerie Générale",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/191_neroli_ad_astra_parfumerie_generale_500x.jpg?v=1589662166",
			"gender": "Unisex",
			"collection_handle": "pierre-guillaume-parfumerie-generale",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"in-this-world": {
			"title": "In This World",
			"brand": "Liis",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Liis_Silo_InThisWorld_500x.jpg?v=1764836604",
			"gender": "Unisex",
			"collection_handle": "liis",
			"price_ol": 160,
			"price_ol_ml": 50,
			"price_tf": 133.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"pomelo-oolong": {
			"title": "Pomelo Oolong | 柚子乌龙茶",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/pomelo_oolong_616f4df3-c523-4df9-bc10-8854255e55e7_500x.jpg?v=1756822738",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"velvet-bold": {
			"title": "Velvet | Bold",
			"brand": "Commodity",
			"price": 135,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Velvet_500x.png?v=1740648640",
			"gender": "Unisex",
			"collection_handle": "commodity",
			"price_ol": 135,
			"price_ol_ml": 100,
			"price_tf": 112.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"skin-fetish": {
			"title": "Skin Fetish",
			"brand": "Ourte",
			"price": 115,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/skin_fetish_new_500x.jpg?v=1792512389",
			"gender": "Unisex",
			"collection_handle": "ourte",
			"price_ol": 115,
			"price_ol_ml": 50,
			"price_tf": 95.83,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"andevalo": {
			"title": "Andevalo",
			"brand": "Nissaba",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/andevalo_500x.jpg?v=1788434336",
			"gender": "Unisex",
			"collection_handle": "nissaba",
			"price_ol": 180,
			"price_ol_ml": 100,
			"price_tf": 150,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"iris-altesse": {
			"title": "Iris Altesse",
			"brand": "Danner & Flemming",
			"price": 280,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/iris_altesse_500x.jpg?v=1781384722",
			"gender": "Unisex",
			"collection_handle": "danner-flemming",
			"price_ol": 280,
			"price_ol_ml": 50,
			"price_tf": 233.33,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"cambodian-oud": {
			"title": "Cambodian Oud",
			"brand": "SweDoft",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/CAMBODIAN-OUD_500x.jpg?v=1632583742",
			"gender": "Unisex",
			"collection_handle": "swedoft",
			"price_ol": 120,
			"price_ol_ml": 100,
			"price_tf": 100,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"castiamanti": {
			"title": "Castiamanti",
			"brand": "Agatho",
			"price": 305,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/castiamanti_500x.jpg?v=1657827333",
			"gender": "Unisex",
			"collection_handle": "agatho",
			"price_ol": 305,
			"price_ol_ml": 100,
			"price_tf": 254.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"high-to-lotus": {
			"title": "High to Lotus",
			"brand": "Space Fluid",
			"price": 290,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/lotus_01_500x.jpg?v=1770030576",
			"gender": "Unisex",
			"collection_handle": "space-fluid",
			"price_ol": 290,
			"price_ol_ml": 50,
			"price_tf": 241.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"nuit-de-baklite": {
			"title": "Nuit de Bakélite",
			"brand": "Naomi Goodsir",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/NGP_NUIT_DE_BAKELITE_2019_bloom_500x.png?v=1589662126",
			"gender": "Unisex",
			"collection_handle": "naomi-goodsir",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"rosa-umami-v2": {
			"title": "Rosa umami V2",
			"brand": "Zanini",
			"price": 305,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/rosa_umami_500x.jpg?v=1789410210",
			"gender": "Unisex",
			"collection_handle": "zanini",
			"price_ol": 305,
			"price_ol_ml": 30,
			"price_tf": 254.17,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"a-masquerade": {
			"title": "A Masquerade",
			"brand": "Plowden & Fallow",
			"price": 108,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/pf_08_500x.jpg?v=1716723960",
			"gender": "Unisex",
			"collection_handle": "plowden-fallow",
			"price_ol": 108,
			"price_ol_ml": 100,
			"price_tf": 90,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"lamar": {
			"title": "Lamar",
			"brand": "Kajal",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/lamar_500x.jpg?v=1765390324",
			"gender": "Unisex",
			"collection_handle": "kajal",
			"price_ol": 170,
			"price_ol_ml": 100,
			"price_tf": 141.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"guava-sorbet": {
			"title": "Guava Sorbet",
			"brand": "Brandt",
			"price": 185,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/guava_sorbet_500x.jpg?v=1761236084",
			"gender": "Unisex",
			"collection_handle": "brandt",
			"price_ol": 185,
			"price_ol_ml": 100,
			"price_tf": 154.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"helene": {
			"title": "Hélène",
			"brand": "Rance 1795",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/helene_500x.jpg?v=1779881514",
			"gender": "Unisex",
			"collection_handle": "rance-1795",
			"price_ol": 110,
			"price_ol_ml": 50,
			"price_tf": 91.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"asoral": {
			"title": "Asoral",
			"brand": "Mendittorosa",
			"price": 260,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/freedom-asoral-01_1_500x.jpg?v=1789568167",
			"gender": "Unisex",
			"collection_handle": "mendittorosa",
			"price_ol": 260,
			"price_ol_ml": 100,
			"price_tf": 216.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"ensang-noir": {
			"title": "Ensang Noir",
			"brand": "L'Entropiste",
			"price": 260,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ensang_eccccf9d-0736-466f-ab16-c9856bb296a5_500x.jpg?v=1766576633",
			"gender": "Unisex",
			"collection_handle": "lentropiste",
			"price_ol": 260,
			"price_ol_ml": 100,
			"price_tf": 216.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"juice-personal": {
			"title": "Juice | Personal",
			"brand": "Commodity",
			"price": 135,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Juice_500x.png?v=1740650774",
			"gender": "Unisex",
			"collection_handle": "commodity",
			"price_ol": 135,
			"price_ol_ml": 100,
			"price_tf": 112.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"koiame": {
			"title": "恋雨 Koiame (It's Raining Love)",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/koiame-01_500x.jpg?v=1741339492",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"marbella": {
			"title": "Marbella",
			"brand": "CARNER",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/marbella_4e20b016-94cc-4dab-8d67-c90059b5b5e4_500x.jpg?v=1643873956",
			"gender": "Female",
			"collection_handle": "carner",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"purple-eclipse": {
			"title": "Purple Eclipse",
			"brand": "MINT",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/MEEK_PASSION-2_47268d4d-3151-41e6-a133-9ef0b66b4e76_500x.jpg?v=1703020157",
			"gender": "Female",
			"collection_handle": "mint",
			"price_ol": 170,
			"price_ol_ml": 70,
			"price_tf": 141.67,
			"price_tf_ml": 70,
			"concentration": "EdP"
		},
		"ziveli": {
			"title": "Živeli",
			"brand": "Boka",
			"price": 215,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/boka_ziveli_bb007d5c-9e42-44d8-aed1-d3c0500c3290_500x.jpg?v=1786786659",
			"gender": "Unisex",
			"collection_handle": "boka",
			"price_ol": 215,
			"price_ol_ml": 50,
			"price_tf": 179.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"agrani": {
			"title": "Agrani",
			"brand": "Byermia",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/agrani_6e9a96b0-b83b-4837-bc09-c8f6ef1ac5c1_500x.jpg?v=1792073237",
			"gender": "Unisex",
			"collection_handle": "byermia",
			"price_ol": 155,
			"price_ol_ml": 30,
			"price_tf": 129.17,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"vettii": {
			"title": "Vettii",
			"brand": "Agatho",
			"price": 305,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/vetti_500x.jpg?v=1764500554",
			"gender": "Unisex",
			"collection_handle": "agatho",
			"price_ol": 305,
			"price_ol_ml": 100,
			"price_tf": 254.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"drunk-saffron": {
			"title": "DRUNK SAFFRON",
			"brand": "BORNTOSTANDOUT",
			"price": 182,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/DRUNKSAFFRON_BOTTLE_500x.png?v=1710232337",
			"gender": "Unisex",
			"collection_handle": "born-to-stand-oud",
			"price_ol": 182,
			"price_ol_ml": 50,
			"price_tf": 151.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"closeup": {
			"title": "Close-Up",
			"brand": "Olfactive Studio",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/close_up_olfcative_studio_bloom_500x.jpg?v=1589660908",
			"gender": "Unisex",
			"collection_handle": "olfactive-studio",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"entre-ciel-et-mer": {
			"title": "Entre Ciel et Mer",
			"brand": "Pierre Guillaume Cruise/Croisiere",
			"price": 144,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/ENTRE_CIEL_ET_MER-100ML_500x.jpg?v=1589660953",
			"gender": "Unisex",
			"collection_handle": "pierre-guillaume-cruise-croisiere",
			"price_ol": 144,
			"price_ol_ml": 100,
			"price_tf": 120,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"hey-bitch": {
			"title": "Hey Bxtch",
			"brand": "Ourte",
			"price": 115,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/hey_bitch_new_500x.jpg?v=1792512562",
			"gender": "Unisex",
			"collection_handle": "ourte",
			"price_ol": 115,
			"price_ol_ml": 50,
			"price_tf": 95.83,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"japanese-whiskey": {
			"title": "Japanese Whiskey",
			"brand": "d'Annam",
			"price": 295,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/japanese_whiskey_500x.jpg?v=1747316222",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 295,
			"price_ol_ml": 150,
			"price_tf": 245.83,
			"price_tf_ml": 150,
			"concentration": "EdP"
		},
		"mauna": {
			"title": "Mauna",
			"brand": "Mendittorosa",
			"price": 225,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Mauna-01-main_48e5206b-9a96-41de-b5e0-42bf72820392_500x.jpg?v=1712072738",
			"gender": "Unisex",
			"collection_handle": "mendittorosa",
			"price_ol": 225,
			"price_ol_ml": 100,
			"price_tf": 187.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"moko": {
			"title": "Moko",
			"brand": "Wolf Brothers",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/WB-MOKO-PERFUME-50ml-white_29bde728-40a1-40ab-825c-adefd8c67f67_500x.jpg?v=1742991648",
			"gender": "Unisex",
			"collection_handle": "wolf-brothers",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"pg-111-indian-wood": {
			"title": "PG11.1 Indian Wood",
			"brand": "Pierre Guillaume - Parfumerie Générale",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/pg11_1_indian_wood_pierre_guillaume_500x.jpg?v=1589662157",
			"gender": "Male",
			"collection_handle": "pierre-guillaume-parfumerie-generale",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"smokin-gun": {
			"title": "SMOKIN' GUN",
			"brand": "BORNTOSTANDOUT",
			"price": 182,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/SMOKINGUN_BOTTLE_500x.png?v=1710232432",
			"gender": "Unisex",
			"collection_handle": "born-to-stand-oud",
			"price_ol": 182,
			"price_ol_ml": 50,
			"price_tf": 151.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"montabaco": {
			"title": "Montabaco",
			"brand": "Ormonde Jayne",
			"price": 295,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/montabaco-intensivo-88ml-flat_1020x1530_1_500x.jpg?v=1711456746",
			"gender": "Unisex",
			"collection_handle": "ormonde-jayne",
			"price_ol": 295,
			"price_ol_ml": 88,
			"price_tf": 245.83,
			"price_tf_ml": 88,
			"concentration": "Extrait de Parfum"
		},
		"tennis-club": {
			"title": "Tennis Club",
			"brand": "CARNER",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/carner_tennis_club_500x.jpg?v=1660317978",
			"gender": "Unisex",
			"collection_handle": "carner",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"hiraeth": {
			"title": "Hiraeth",
			"brand": "Ortir Apothecari",
			"price": 114,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ORTIRHIRAETH_500x.png?v=1711459620",
			"gender": "Unisex",
			"collection_handle": "ortir-apothecari",
			"price_ol": 114,
			"price_ol_ml": 50,
			"price_tf": 95,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"flower-frost-19-12": {
			"title": "Flower Frost 19.12",
			"brand": "Neydo",
			"price": 80,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/1912_895ae71f-1e66-4931-a98a-787f58ad9c50_500x.jpg?v=1793130848",
			"gender": "Unisex",
			"collection_handle": "neydo",
			"price_ol": 80,
			"price_ol_ml": 50,
			"price_tf": 66.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"lacura": {
			"title": "Lacura",
			"brand": "Mendittorosa",
			"price": 200,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/medium_Lacura_05_500x.jpg?v=1589662046",
			"gender": "Unisex",
			"collection_handle": "mendittorosa",
			"price_ol": 200,
			"price_ol_ml": 100,
			"price_tf": 166.67,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"aurantium-jasmine": {
			"title": "Aurantium Jasmine",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/aurantiumjasmine-01_500x.jpg?v=1741339104",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"chateau-de-velours": {
			"title": "Château de Velours",
			"brand": "Insenf",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/chateu_500x.jpg?v=1783068143",
			"gender": "",
			"collection_handle": "insenf",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"tokyo-nights": {
			"title": "Tokyo Nights | ナイト",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Tokyo-Nights_500x.jpg?v=1746626539",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"juice-balanced": {
			"title": "Juice | Balanced & Expressive",
			"brand": "Commodity",
			"price": 135,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Juice_99918fb3-bf67-42ed-8dd9-2af32f425966_500x.png?v=1740650789",
			"gender": "Unisex",
			"collection_handle": "commodity",
			"price_ol": 135,
			"price_ol_ml": 100,
			"price_tf": 112.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"fraisi": {
			"title": "Fraisi",
			"brand": "Mabra",
			"price": 75,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/fraisi_500x.jpg?v=1750076034",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 75,
			"price_ol_ml": 30,
			"price_tf": 62.5,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"indecent-cherry": {
			"title": "INDECENT CHERRY",
			"brand": "BORNTOSTANDOUT",
			"price": 182,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/INDECENTCHERRY_BOTTLE_500x.png?v=1710231844",
			"gender": "Unisex",
			"collection_handle": "born-to-stand-oud",
			"price_ol": 182,
			"price_ol_ml": 50,
			"price_tf": 151.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"matcha-soft-serve": {
			"title": "Matcha Soft Serve | 抹茶ソフト",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Matcha-Soft-Serve_500x.jpg?v=1746627479",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"culot-the": {
			"title": "Culot The",
			"brand": "Versatile",
			"price": 55,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/culot_the_500x.jpg?v=1790357607",
			"gender": "",
			"collection_handle": "versatile",
			"price_ol": 55,
			"price_ol_ml": 30,
			"price_tf": 45.83,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"tea-rock-n-roll": {
			"title": "Tea & Rock ‘n‘ Roll",
			"brand": "Voyages Imaginaires",
			"price": 175,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/tea_999x1500_50197860-f8bb-4dbf-b654-41528ee640bd_500x.webp?v=1717745705",
			"gender": "Unisex",
			"collection_handle": "voyages-imaginaires",
			"price_ol": 175,
			"price_ol_ml": 75,
			"price_tf": 145.83,
			"price_tf_ml": 75,
			"concentration": "EdP"
		},
		"leaf-crush": {
			"title": "Leaf Crush",
			"brand": "Insenf",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/leaf_500x.jpg?v=1783068296",
			"gender": "Unisex",
			"collection_handle": "insenf",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"beso-pasion": {
			"title": "Beso Pasión",
			"brand": "Beso Beach",
			"price": 145,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/beso-pasion_500x.jpg?v=1610206676",
			"gender": "Unisex",
			"collection_handle": "beso-beach",
			"price_ol": 145,
			"price_ol_ml": 100,
			"price_tf": 120.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"chaco": {
			"title": "Chaco",
			"brand": "Nissaba",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/chaco_500x.jpg?v=1757078342",
			"gender": "Unisex",
			"collection_handle": "nissaba",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"moonmilk": {
			"title": "Moonmilk",
			"brand": "Stora Skuggan",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/moon_500x.jpg?v=1764953808",
			"gender": "Unisex",
			"collection_handle": "stora-skuggan",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"liquid-incense": {
			"title": "Liquid Incense",
			"brand": "Maksim Perfume",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/liquid_500x.jpg?v=1793040801",
			"gender": "Unisex",
			"collection_handle": "maksim-perfume",
			"price_ol": 160,
			"price_ol_ml": 50,
			"price_tf": 133.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"sand": {
			"title": "Sand",
			"brand": "And Fragrance",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/SAND-50ML-MASTER_500x.jpg?v=1717843271",
			"gender": "Unisex",
			"collection_handle": "and-fragrance",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"sunken-tobacco": {
			"title": "Sunken Tobacco",
			"brand": "Beaufort",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/sunken_tobacco_500x.jpg?v=1792010392",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"berbera": {
			"title": "Berbera",
			"brand": "Nissaba",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/berbera_500x.jpg?v=1757077260",
			"gender": "Unisex",
			"collection_handle": "nissaba",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"volcano": {
			"title": "Volcano",
			"brand": "CARNER",
			"price": 225,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/volcano_carner_500x.jpg?v=1589665551",
			"gender": "Unisex",
			"collection_handle": "carner",
			"price_ol": 225,
			"price_ol_ml": 100,
			"price_tf": 187.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"woodoo": {
			"title": "Woodoo",
			"brand": "Reserve en Afrique 1934",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/woodoo_500x.jpg?v=1783328394",
			"gender": "Unisex",
			"collection_handle": "reserve-en-afrique-1934",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"bois-dascse": {
			"title": "Bois d’Ascese",
			"brand": "Naomi Goodsir",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/NGP_BOIS_D_ASCESE_2019_500x.png?v=1604928347",
			"gender": "Unisex",
			"collection_handle": "naomi-goodsir",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"anti-blues": {
			"title": "Anti-Blues",
			"brand": "Pierre Guillaume Black Collection",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/anti-blues-100_500x.jpg?v=1689022528",
			"gender": "Unisex",
			"collection_handle": "pierre-guillaume-black-collection",
			"price_ol": 195,
			"price_ol_ml": 100,
			"price_tf": 162.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"bake": {
			"title": "Bake",
			"brand": "Akro",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Akro_100-ML-BAKE_500x.jpg?v=1756809437",
			"gender": "Unisex",
			"collection_handle": "akro",
			"price_ol": 160,
			"price_ol_ml": 100,
			"price_tf": 133.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"far": {
			"title": "Far",
			"brand": "And Fragrance",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/image_50379521_1d5acf8f-6014-4855-b122-2e742174ec50_500x.jpg?v=1718889300",
			"gender": "Unisex",
			"collection_handle": "and-fragrance",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"god-bless-cola": {
			"title": "God Bless Cola",
			"brand": "Versatile",
			"price": 55,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/god_bless_cola_500x.jpg?v=1790357365",
			"gender": "Female",
			"collection_handle": "versatile",
			"price_ol": 55,
			"price_ol_ml": 30,
			"price_tf": 45.83,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"matcha-mist": {
			"title": "Brume Matcha",
			"brand": "Marie Jeanne",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/MARIEJEANNE_Mists_Brume-matcha_shadow_500x.jpg?v=1738669327",
			"gender": "Unisex",
			"collection_handle": "marie-jeanne",
			"price_ol": 60,
			"price_ol_ml": 100,
			"price_tf": 50,
			"price_tf_ml": 100,
			"concentration": "EdC"
		},
		"play-green": {
			"title": "Play Green",
			"brand": "Comme Des Garcons",
			"price": 95,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/heart_02_e41f32d0-ef5f-4cca-87bd-83ff785a948b_500x.jpg?v=1773780011",
			"gender": "Unisex",
			"collection_handle": "comme-des-garcons",
			"price_ol": 95,
			"price_ol_ml": 100,
			"price_tf": 79.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"you-or-someone-like-you": {
			"title": "You or Someone Like You",
			"brand": "Etat Libre d'Orange",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/You_Or_Someone_Like_You_100_ml_1_946b1c29-12a6-444f-9b02-7c047eded2ab_500x.jpg?v=1625876471",
			"gender": "Unisex",
			"collection_handle": "etat-libre-dorange",
			"price_ol": 155,
			"price_ol_ml": 100,
			"price_tf": 129.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"hakka-japanese-mint": {
			"title": "Hakka (Japanese Mint)",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/hakka-01_500x.jpg?v=1741339802",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"provence": {
			"title": "Provence",
			"brand": "Nissaba",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/provence_500x.jpg?v=1757078761",
			"gender": "Unisex",
			"collection_handle": "nissaba",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"falaise-49-5-n": {
			"title": "Falaise 49.5°N (Cliff 49.5°N)",
			"brand": "Milleseve",
			"price": 115,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/FA-100ml---4_500x.jpg?v=1789385407",
			"gender": "Unisex",
			"collection_handle": "milleseve",
			"price_ol": 115,
			"price_ol_ml": 100,
			"price_tf": 95.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"sous-le-pont-mirabeau": {
			"title": "Sous Le Pont Mirabeau",
			"brand": "Etat Libre d'Orange",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/SouslepontMirabeau100ml_5ac7f65b-8205-4d1f-81e1-70fd89082f81_500x.jpg?v=1684152518",
			"gender": "Unisex",
			"collection_handle": "etat-libre-dorange",
			"price_ol": 155,
			"price_ol_ml": 100,
			"price_tf": 129.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"beso-de-sal": {
			"title": "Beso de Sal",
			"brand": "Beso Beach",
			"price": 145,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/beso_de_sal_500x.jpg?v=1783354178",
			"gender": "Unisex",
			"collection_handle": "beso-beach",
			"price_ol": 145,
			"price_ol_ml": 100,
			"price_tf": 120.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"breathe-out-01-02": {
			"title": "Breathe Out 01.02",
			"brand": "Neydo",
			"price": 80,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/neydo_breath_500x.jpg?v=1770373220",
			"gender": "Unisex",
			"collection_handle": "neydo",
			"price_ol": 80,
			"price_ol_ml": 50,
			"price_tf": 66.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"sydney-rock-pool": {
			"title": "Sydney Rock Pool",
			"brand": "Arquiste",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/sydney_rockpool_arquiste_c1df3be0-2ab0-4f24-b272-33798ef6b37d_500x.jpg?v=1625876155",
			"gender": "Unisex",
			"collection_handle": "arquiste",
			"price_ol": 195,
			"price_ol_ml": 100,
			"price_tf": 162.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"dancing-light": {
			"title": "Dancing Light",
			"brand": "Olfactive Studio",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Dancing-Light-100-ML-bottle_500x.jpg?v=1654970114",
			"gender": "Unisex",
			"collection_handle": "olfactive-studio",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"muguet-fleuri": {
			"title": "Muguet Fleuri",
			"brand": "Oriza L. Legrand",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/muguet_500x.jpg?v=1788436361",
			"gender": "Female",
			"collection_handle": "oriza-l-legrand",
			"price_ol": 155,
			"price_ol_ml": 100,
			"price_tf": 129.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"bamboo": {
			"title": "Bamboo",
			"brand": "NuWilds",
			"price": 80,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/bamboo_bfdb0893-c20f-40db-8a65-4980cc59dba3_500x.jpg?v=1788191309",
			"gender": "Unisex",
			"collection_handle": "nu-wilds",
			"price_ol": 80,
			"price_ol_ml": 30,
			"price_tf": 66.67,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"juli": {
			"title": "Juli",
			"brand": "32.JULI",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/juli_juli_500x.jpg?v=1788783233",
			"gender": "Unisex",
			"collection_handle": "32-juli",
			"price_ol": 155,
			"price_ol_ml": 50,
			"price_tf": 129.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"shaft-of-light": {
			"title": "Shaft of Light (Sunbeams)",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/shaftoflight-01_500x.jpg?v=1741339345",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"32-c": {
			"title": "32°C",
			"brand": "32.JULI",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/juli_32_500x.jpg?v=1788784252",
			"gender": "Unisex",
			"collection_handle": "32-juli",
			"price_ol": 155,
			"price_ol_ml": 50,
			"price_tf": 129.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"kolada": {
			"title": "Kolada",
			"brand": "Kajal",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/kolada_500x.jpg?v=1765390870",
			"gender": "Unisex",
			"collection_handle": "kajal",
			"price_ol": 195,
			"price_ol_ml": 100,
			"price_tf": 162.5,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"in-love-with-the-cocos": {
			"title": "In Love With the Cocos",
			"brand": "Fugazzi",
			"price": 205,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/100_Fugazzi_productfotos2022_coco_500x.png?v=1745850603",
			"gender": "Unisex",
			"collection_handle": "fugazzi",
			"price_ol": 205,
			"price_ol_ml": 100,
			"price_tf": 170.83,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"divina-flora": {
			"title": "Divina Flora",
			"brand": "Aura Eximia",
			"price": 225,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/23d9cc_bac3c41327f14615a069600a780e1889_mv2_1_500x.png?v=1785402567",
			"gender": "Unisex",
			"collection_handle": "aura-eximia",
			"price_ol": 225,
			"price_ol_ml": 50,
			"price_tf": 187.5,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"firenze": {
			"title": "Firenze",
			"brand": "Ampersand",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/firenze_500x.jpg?v=1778351061",
			"gender": "Unisex",
			"collection_handle": "ampersand",
			"price_ol": 60,
			"price_ol_ml": 50,
			"price_tf": 50,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"sun-bleached": {
			"title": "Sun Bleached",
			"brand": "Insenf",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/sun_500x.jpg?v=1783068206",
			"gender": "Unisex",
			"collection_handle": "insenf",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"this-is-not-a-blue-bottle-1-4": {
			"title": "This is Not a Blue Bottle 1/.4 – Yin",
			"brand": "Histoires de Parfums",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/not_a_blue_bottle_04_500x.jpg?v=1757945935",
			"gender": "Unisex",
			"collection_handle": "histoires-de-parfums",
			"price_ol": 165,
			"price_ol_ml": 115,
			"price_tf": 137.5,
			"price_tf_ml": 115,
			"concentration": "EdP"
		},
		"tonkade": {
			"title": "Tonkade",
			"brand": "Laboratorio Olfattivo",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/tonkade_18414833-44f7-481b-93fa-583752ff357d_500x.jpg?v=1708450480",
			"gender": "Unisex",
			"collection_handle": "laboratorio-olfattivo",
			"price_ol": 120,
			"price_ol_ml": 100,
			"price_tf": 100,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"drunk-lovers": {
			"title": "DRUNK LOVERS",
			"brand": "BORNTOSTANDOUT",
			"price": 262,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/borntostandout_drunk_lovers_500x.jpg?v=1680168958",
			"gender": "Unisex",
			"collection_handle": "born-to-stand-oud",
			"price_ol": 262,
			"price_ol_ml": 100,
			"price_tf": 218.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"patchouli-cannelle": {
			"title": "Patchouli Cannelle",
			"brand": "La Closerie",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/patchouli_cannele_500x.jpg?v=1790694990",
			"gender": "Unisex",
			"collection_handle": "la-closerie",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"vanille-mafonja": {
			"title": "Vanille Mafonja",
			"brand": "Reserve en Afrique 1934",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/1.Reserve_en_Afrique_VanilleMafonja_100ml_Bottle_500x.png?v=1754904945",
			"gender": "Unisex",
			"collection_handle": "reserve-en-afrique-1934",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"le-mat": {
			"title": "Le Mat",
			"brand": "Mendittorosa",
			"price": 225,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Le_Mat_1_biale_resize_500x.jpg?v=1589662051",
			"gender": "Unisex",
			"collection_handle": "mendittorosa",
			"price_ol": 225,
			"price_ol_ml": 100,
			"price_tf": 187.5,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"wolf-extrait": {
			"title": "Wolf (Extrait)",
			"brand": "Wolf Brothers",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/wold_01_500x.jpg?v=1737390854",
			"gender": "Unisex",
			"collection_handle": "wolf-brothers",
			"price_ol": 160,
			"price_ol_ml": 50,
			"price_tf": 133.33,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"lu-bu": {
			"title": "Lü Bu",
			"brand": "Sora Dora",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/lubu_500x.jpg?v=1781697322",
			"gender": "Unisex",
			"collection_handle": "sora-dora",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"milk-bold": {
			"title": "Milk | Bold",
			"brand": "Commodity",
			"price": 135,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Milk_a9553e1d-50b3-4c02-8ebf-e07057b7850f_500x.png?v=1740650988",
			"gender": "Unisex",
			"collection_handle": "commodity",
			"price_ol": 135,
			"price_ol_ml": 100,
			"price_tf": 112.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"rasputin": {
			"title": "Rasputin",
			"brand": "SweDoft",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/RASPUTIN_500x.jpg?v=1632583409",
			"gender": "Male",
			"collection_handle": "swedoft",
			"price_ol": 120,
			"price_ol_ml": 100,
			"price_tf": 100,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"23-59": {
			"title": "23:59",
			"brand": "32.JULI",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/juli_2359_500x.jpg?v=1788783112",
			"gender": "Unisex",
			"collection_handle": "32-juli",
			"price_ol": 155,
			"price_ol_ml": 50,
			"price_tf": 129.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"ember-shaan": {
			"title": "Ember Shaan",
			"brand": "Byermia",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ember_shaan_500x.jpg?v=1792074939",
			"gender": "Unisex",
			"collection_handle": "byermia",
			"price_ol": 140,
			"price_ol_ml": 30,
			"price_tf": 116.67,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"or-du-srail": {
			"title": "Or du Sérail",
			"brand": "Naomi Goodsir",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/NGP_OR_DU_SERAIL_2019_500x.jpg?v=1589662135",
			"gender": "Unisex",
			"collection_handle": "naomi-goodsir",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"tiger-rouge": {
			"title": "Tiger Rouge",
			"brand": "Byermia",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/tiger_rouge_500x.jpg?v=1792072917",
			"gender": "Unisex",
			"collection_handle": "byermia",
			"price_ol": 160,
			"price_ol_ml": 30,
			"price_tf": 133.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"fat-cola": {
			"title": "Fat Cola",
			"brand": "Ourte",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/fat_cola_205f209d-9a65-4cc8-8626-ea0f78e9d2a3_500x.jpg?v=1793559383",
			"gender": "Unisex",
			"collection_handle": "ourte",
			"price_ol": 110,
			"price_ol_ml": 50,
			"price_tf": 91.67,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"ladano": {
			"title": "Ladano",
			"brand": "D:SOL MMXVI",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ladano_500x.jpg?v=1769108500",
			"gender": "Unisex",
			"collection_handle": "d-sol",
			"price_ol": 140,
			"price_ol_ml": 75,
			"price_tf": 116.67,
			"price_tf_ml": 75,
			"concentration": "EdP"
		},
		"meek-passion": {
			"title": "Meek Passion",
			"brand": "MINT",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/MEEK_PASSION_500x.jpg?v=1729348188",
			"gender": "Unisex",
			"collection_handle": "mint",
			"price_ol": 170,
			"price_ol_ml": 70,
			"price_tf": 141.67,
			"price_tf_ml": 70,
			"concentration": "EdP"
		},
		"sugar-addict": {
			"title": "SUGAR ADDICT",
			"brand": "BORNTOSTANDOUT",
			"price": 182,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/SUGARADDICT_BOTTLE_500x.png?v=1710231628",
			"gender": "Unisex",
			"collection_handle": "born-to-stand-oud",
			"price_ol": 182,
			"price_ol_ml": 50,
			"price_tf": 151.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"cashmere-beige": {
			"title": "Cashmere Beige",
			"brand": "MINT",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/CASHMERE_BEIGE-2_500x.jpg?v=1589660895",
			"gender": "Unisex",
			"collection_handle": "mint",
			"price_ol": 170,
			"price_ol_ml": 70,
			"price_tf": 141.67,
			"price_tf_ml": 70,
			"concentration": "EdP"
		},
		"iris-pallida": {
			"title": "Iris Pallida",
			"brand": "Marie Jeanne",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/MARIEJEANNE_Perfumes_Iris-Pallidai_shadow_500x.jpg?v=1738670754",
			"gender": "Unisex",
			"collection_handle": "marie-jeanne",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"carine": {
			"title": "Carine",
			"brand": "Carine Roitfeld",
			"price": 190,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/carine_1cfce458-6e61-4626-9e9a-b09793b6d178_500x.jpg?v=1761229136",
			"gender": "Unisex",
			"collection_handle": "carine-roitfeld",
			"price_ol": 190,
			"price_ol_ml": 90,
			"price_tf": 158.33,
			"price_tf_ml": 90,
			"concentration": "EdP"
		},
		"agarwood": {
			"title": "Agarwood",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/agrawod-01_4db3c9a9-fe68-4896-95ce-bd583f77bf0c_500x.jpg?v=1741339740",
			"gender": "Unisex",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"luca-turin-loves-sample-pack": {
			"title": "Luca Turin Loves Sample Pack (2020 Update)",
			"brand": "Bloom Sample Packs",
			"price": 45,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/table_08_05_19_500x.jpg?v=1589662067",
			"gender": "Unisex",
			"collection_handle": "bloom-sample-packs",
			"price_ol": 45,
			"price_ol_ml": 8,
			"price_tf": 37.5,
			"price_tf_ml": 8,
			"concentration": "8"
		},
		"marrakech-cologne": {
			"title": "Marrakech Cologne",
			"brand": "Mabra",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mabra_marrakech_500x.jpg?v=1754916621",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 60,
			"price_ol_ml": 30,
			"price_tf": 50,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"come-hell-or-high-water-discovery-set": {
			"title": "Come Hell or High Water discovery set",
			"brand": "Beaufort",
			"price": 45,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/BFL_Come_Hell_Dicso_aa1070db-5bff-4320-a536-23bf778c7143_500x.jpg?v=1714511330",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 45,
			"price_ol_ml": 5,
			"price_tf": 37.5,
			"price_tf_ml": 5,
			"concentration": "EdP"
		},
		"mansa-musa": {
			"title": "Mansa Musa",
			"brand": "SweDoft",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/swedoft-mans-musa_500x.jpg?v=1638376966",
			"gender": "Unisex",
			"collection_handle": "swedoft",
			"price_ol": 160,
			"price_ol_ml": 100,
			"price_tf": 133.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"rouge-avignon": {
			"title": "Rouge Avignon",
			"brand": "Phaedon Paris",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/PHAEDON_ROUGE_AVIGNON_20201_500x.jpg?v=1589665446",
			"gender": "Unisex",
			"collection_handle": "phaedon-paris",
			"price_ol": 130,
			"price_ol_ml": 100,
			"price_tf": 108.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"lola-at-coat-check": {
			"title": "Lola at Coat Check",
			"brand": "Discothèque",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/lola_4d3c0145-98a1-44fd-adac-685dee6dd578_500x.jpg?v=1768129234",
			"gender": "Unisex",
			"collection_handle": "discotheque",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"cash-flower": {
			"title": "Cash Flower",
			"brand": "Fugazzi",
			"price": 205,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/100_Fugazzi_ashflower_100ml-kopieren_100_500x.png?v=1745849461",
			"gender": "",
			"collection_handle": "fugazzi",
			"price_ol": 205,
			"price_ol_ml": 100,
			"price_tf": 170.83,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"iris-sura-limited-release": {
			"title": "Iris Sura",
			"brand": "Byermia",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/iris_sura_500x.jpg?v=1792075313",
			"gender": "Unisex",
			"collection_handle": "byermia",
			"price_ol": 180,
			"price_ol_ml": 30,
			"price_tf": 150,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"195-a-c": {
			"title": "195 A.C.",
			"brand": "Agatho",
			"price": 305,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/195-AC_500x.jpg?v=1657827237",
			"gender": "Unisex",
			"collection_handle": "agatho",
			"price_ol": 305,
			"price_ol_ml": 100,
			"price_tf": 254.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"the-nirvana": {
			"title": "The Nirvana",
			"brand": "Param Sara",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/The-Nirvana_500x.jpg?v=1778077865",
			"gender": "Unisex",
			"collection_handle": "param-sara",
			"price_ol": 140,
			"price_ol_ml": 50,
			"price_tf": 116.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"awake": {
			"title": "Awake",
			"brand": "Akro",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Akro_100-ML-Awake_500x.jpg?v=1756809370",
			"gender": "Unisex",
			"collection_handle": "akro",
			"price_ol": 160,
			"price_ol_ml": 100,
			"price_tf": 133.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"grande-lle": {
			"title": "Grande Ile",
			"brand": "Nissaba",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/grandeile_500x.jpg?v=1757077764",
			"gender": "Unisex",
			"collection_handle": "nissaba",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"rose-de-taif": {
			"title": "Rose de Taif",
			"brand": "Perris Monte Carlo",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Black-Rose-de-Taif_100_ml_8c8c6a2d-afcb-44f3-82e6-21fc28998642_500x.jpg?v=1625876109",
			"gender": "Unisex",
			"collection_handle": "perris-monte-carlo",
			"price_ol": 160,
			"price_ol_ml": 100,
			"price_tf": 133.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"1805-tonnerre": {
			"title": "Tonnerre",
			"brand": "Beaufort",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/BeauFort_London_1805_Tonnerre_Bloom_500x.jpg?v=1623765370",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"pear-pavlova": {
			"title": "Pear Pavlova",
			"brand": "French Cowboy",
			"price": 90,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/pear_pavlova_500x.jpg?v=1773068554",
			"gender": "Unisex",
			"collection_handle": "french-cowboy",
			"price_ol": 90,
			"price_ol_ml": 100,
			"price_tf": 75,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"pink-sweats": {
			"title": "Pink Sweats",
			"brand": "Insenf",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/pink_sweats_500x.jpg?v=1783068572",
			"gender": "Female",
			"collection_handle": "insenf",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"arcadia-1": {
			"title": "Arcadia",
			"brand": "Otzlab",
			"price": 240,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/arcadia_74a68793-b18a-42ed-a0ed-e320d8ca1c0b_500x.jpg?v=1781438125",
			"gender": "Unisex",
			"collection_handle": "otzlab",
			"price_ol": 240,
			"price_ol_ml": 40,
			"price_tf": 200,
			"price_tf_ml": 40,
			"concentration": "Extrait de Parfum"
		},
		"fig-porn": {
			"title": "FIG PORN",
			"brand": "BORNTOSTANDOUT",
			"price": 182,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/FIGPORN_BOTTLE_500x.png?v=1710232567",
			"gender": "Unisex",
			"collection_handle": "born-to-stand-oud",
			"price_ol": 182,
			"price_ol_ml": 50,
			"price_tf": 151.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"damask": {
			"title": "Damask",
			"brand": "Ormonde Jayne",
			"price": 175,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/damask-88ml-front_1020x1530_1_500x.jpg?v=1711455708",
			"gender": "Female",
			"collection_handle": "ormonde-jayne",
			"price_ol": 175,
			"price_ol_ml": 88,
			"price_tf": 145.83,
			"price_tf_ml": 88,
			"concentration": "EdP"
		},
		"desiree": {
			"title": "Désirée",
			"brand": "Rance 1795",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/desiree_64fdc714-03ec-49fb-a3de-1b4af2ab7f0f_500x.jpg?v=1781776204",
			"gender": "Unisex",
			"collection_handle": "rance-1795",
			"price_ol": 110,
			"price_ol_ml": 50,
			"price_tf": 91.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"musc-oli": {
			"title": "Musc Oli",
			"brand": "Caron",
			"price": 275,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/musc_oli_500x.jpg?v=1789849912",
			"gender": "Female",
			"collection_handle": "caron",
			"price_ol": 275,
			"price_ol_ml": 100,
			"price_tf": 229.17,
			"price_tf_ml": 100,
			"concentration": "EdT"
		},
		"pg0-4-le-musc-la-peau": {
			"title": "Le Musc & La Peau Extrait",
			"brand": "Pierre Guillaume - Parfumerie Générale",
			"price": 225,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Pierre-Guillaume-Paris-Le-Musc-et-La-Peau-Bouteille-Extrait-de-Parfum-100ml_1_500x.jpg?v=1712072353",
			"gender": "Unisex",
			"collection_handle": "pierre-guillaume-parfumerie-generale",
			"price_ol": 225,
			"price_ol_ml": 100,
			"price_tf": 187.5,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"oud-cannelle": {
			"title": "Oud Cannelle",
			"brand": "La Closerie",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/oud_cannele_500x.jpg?v=1790694720",
			"gender": "Unisex",
			"collection_handle": "la-closerie",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"tears-on-mystic-sands": {
			"title": "Tears On Mystic Sands",
			"brand": "Mystikum",
			"price": 190,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mystikum_sands_500x.jpg?v=1776444081",
			"gender": "Unisex",
			"collection_handle": "mystikum",
			"price_ol": 190,
			"price_ol_ml": 100,
			"price_tf": 158.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"mezcal-chocolat": {
			"title": "Mezcal Chocolat",
			"brand": "French Cowboy",
			"price": 90,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mezcal_chocolat_500x.jpg?v=1773068653",
			"gender": "Unisex",
			"collection_handle": "french-cowboy",
			"price_ol": 90,
			"price_ol_ml": 100,
			"price_tf": 75,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"scrtions-magnifiques": {
			"title": "Secretions Magnifiques",
			"brand": "Etat Libre d'Orange",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/SECRETIONS-MAGNIFIQUES_c927c398-d206-42ff-8753-228ac755a81e_500x.jpg?v=1625876131",
			"gender": "Unisex",
			"collection_handle": "etat-libre-dorange",
			"price_ol": 110,
			"price_ol_ml": 50,
			"price_tf": 91.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"solar-wind": {
			"title": "Solar Wind",
			"brand": "Pictura Fragrans",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/SolarWindPicturaFragrans_500x.jpg?v=1775728618",
			"gender": "Unisex",
			"collection_handle": "pictura-fragrans",
			"price_ol": 150,
			"price_ol_ml": 50,
			"price_tf": 125,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"comme-des-garcons-2": {
			"title": "Comme des Garcons 2",
			"brand": "Comme Des Garcons",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/two_500x.jpg?v=1769329950",
			"gender": "Unisex",
			"collection_handle": "comme-des-garcons",
			"price_ol": 120,
			"price_ol_ml": 100,
			"price_tf": 100,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"mistpouffer": {
			"title": "Mistpouffer",
			"brand": "Stora Skuggan",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mist_2f0023df-1444-469f-8d90-0ee31e526ce6_500x.jpg?v=1764953526",
			"gender": "Unisex",
			"collection_handle": "stora-skuggan",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"bubble-tea": {
			"title": "Bubble Tea",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Bubble_Tea_500x.jpg?v=1784204596",
			"gender": "Female",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"gratia-aestiva": {
			"title": "Gratia Aestiva",
			"brand": "Aura Eximia",
			"price": 205,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/23d9cc_efd3f13da2cb4c4a92318998e826c52d_mv2_1_500x.png?v=1785402710",
			"gender": "Female",
			"collection_handle": "aura-eximia",
			"price_ol": 205,
			"price_ol_ml": 50,
			"price_tf": 170.83,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"misty-cypress-ash": {
			"title": "Misty Cypress Ash 青岚柏烬",
			"brand": "Zhiwuzhi 挚物志",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/zhiwuzhi_ash_500x.jpg?v=1785434148",
			"gender": "",
			"collection_handle": "zhiwuzhi",
			"price_ol": 120,
			"price_ol_ml": 50,
			"price_tf": 100,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"roasted-green-tea": {
			"title": "ほうじ茶 Hojicha (Roasted Green Tea)",
			"brand": "J-Scent",
			"price": 122,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/roastedgreentea-01_500x.jpg?v=1741340199",
			"gender": "",
			"collection_handle": "j-scent",
			"price_ol": 122,
			"price_ol_ml": 50,
			"price_tf": 101.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"talento": {
			"title": "Talento",
			"brand": "Mendittorosa",
			"price": 315,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Talento_Mendittorosa_500x.jpg?v=1614678204",
			"gender": "Female",
			"collection_handle": "mendittorosa",
			"price_ol": 315,
			"price_ol_ml": 100,
			"price_tf": 262.5,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"narcotic-v": {
			"title": "Narcotic V",
			"brand": "Nasomatto",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/narcoticv_500x.jpg?v=1756322874",
			"gender": "Female",
			"collection_handle": "nasomatto",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"monsieur": {
			"title": "Monsieur",
			"brand": "Pierre Guillaume Black Collection",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Parfumerie_black_Monsieur_500x.png?v=1589662103",
			"gender": "Male",
			"collection_handle": "pierre-guillaume-black-collection",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"apotheose": {
			"title": "Apothéose",
			"brand": "Oriza L. Legrand",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/apotheose_500x.jpg?v=1788435151",
			"gender": "",
			"collection_handle": "oriza-l-legrand",
			"price_ol": 155,
			"price_ol_ml": 100,
			"price_tf": 129.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"kajal-i": {
			"title": "Kajal I",
			"brand": "Kajal",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/kajal_1_a04718e6-7b08-42e3-ae42-47e3fbdd6aec_500x.jpg?v=1786565608",
			"gender": "Male",
			"collection_handle": "kajal",
			"price_ol": 195,
			"price_ol_ml": 100,
			"price_tf": 162.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"paradoxial": {
			"title": "Paradoxical",
			"brand": "Sly John's Lab",
			"price": 85,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/PARADOXICAL-1006x698_998e89a2-942c-411a-9b46-91fe83b416ef_500x.jpg?v=1661531302",
			"gender": "Male",
			"collection_handle": "sly-johns-lab",
			"price_ol": 85,
			"price_ol_ml": 100,
			"price_tf": 70.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"al-sarab": {
			"title": "Al Sarab",
			"brand": "Maysson",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/al_sarab_500x.jpg?v=1789046723",
			"gender": "Unisex",
			"collection_handle": "maysson",
			"price_ol": 180,
			"price_ol_ml": 50,
			"price_tf": 150,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"ara": {
			"title": "ARС",
			"brand": "Mallo",
			"price": 220,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mallo_arc_500x.jpg?v=1775660659",
			"gender": "Unisex",
			"collection_handle": "mallo",
			"price_ol": 220,
			"price_ol_ml": 30,
			"price_tf": 183.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"ayiti": {
			"title": "Ayiti",
			"brand": "Mabra",
			"price": 75,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ayiti_500x.jpg?v=1750076414",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 75,
			"price_ol_ml": 30,
			"price_tf": 62.5,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"ylang-ylang-nosy-be": {
			"title": "Ylang Ylang Nosy Be",
			"brand": "Perris Monte Carlo",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Black-Ylang-Ylang-Nosy-Be_41799f92-526d-4055-b470-765d5a760e4d_500x.jpg?v=1625876618",
			"gender": "Unisex",
			"collection_handle": "perris-monte-carlo",
			"price_ol": 160,
			"price_ol_ml": 100,
			"price_tf": 133.33,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"jasmin-ylang": {
			"title": "Jasmin & Ylang",
			"brand": "100BON",
			"price": 35,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/jasmin_ylang_500x.jpg?v=1731682971",
			"gender": "Female",
			"collection_handle": "100bon",
			"price_ol": 35,
			"price_ol_ml": 50,
			"price_tf": 29.17,
			"price_tf_ml": 50,
			"concentration": "EdC"
		},
		"delirium": {
			"title": "Delirium",
			"brand": "Wesker",
			"price": 220,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Delirium-Product-Page-1800-1_500x.jpg?v=1747312820",
			"gender": "Unisex",
			"collection_handle": "wesker",
			"price_ol": 220,
			"price_ol_ml": 75,
			"price_tf": 183.33,
			"price_tf_ml": 75,
			"concentration": "Extrait de Parfum"
		},
		"mathilde": {
			"title": "Mathilde",
			"brand": "Rance 1795",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mathilde_500x.jpg?v=1779881448",
			"gender": "Female",
			"collection_handle": "rance-1795",
			"price_ol": 110,
			"price_ol_ml": 50,
			"price_tf": 91.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"suze": {
			"title": "Suze",
			"brand": "Boka",
			"price": 215,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/boka_suze_500x.jpg?v=1786786465",
			"gender": "Unisex",
			"collection_handle": "boka",
			"price_ol": 215,
			"price_ol_ml": 50,
			"price_tf": 179.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"this-is-not-a-blue-bottle-1-7": {
			"title": "This is Not a Blue Bottle 1/.7 – Happiness",
			"brand": "Histoires de Parfums",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/not_a_blue_bottle_07_500x.jpg?v=1757946665",
			"gender": "Unisex",
			"collection_handle": "histoires-de-parfums",
			"price_ol": 165,
			"price_ol_ml": 115,
			"price_tf": 137.5,
			"price_tf_ml": 115,
			"concentration": "EdP"
		},
		"drakon": {
			"title": "Drakon",
			"brand": "CARNER",
			"price": 209,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/drakon_0dfabc1d-ccab-4893-987e-938187635433_500x.jpg?v=1625874071",
			"gender": "Male",
			"collection_handle": "carner",
			"price_ol": 209,
			"price_ol_ml": 50,
			"price_tf": 174.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"vladimir": {
			"title": "Vladimir",
			"brand": "Carine Roitfeld",
			"price": 190,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/15701681_28380308_2048_500x.jpg?v=1632307003",
			"gender": "Male",
			"collection_handle": "carine-roitfeld",
			"price_ol": 190,
			"price_ol_ml": 90,
			"price_tf": 158.33,
			"price_tf_ml": 90,
			"concentration": "EdP"
		},
		"letrog-acqua": {
			"title": "L'Etrog Acqua",
			"brand": "Arquiste",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Arquiste_Letrog_acqua_500x.jpg?v=1589662043",
			"gender": "Male",
			"collection_handle": "arquiste",
			"price_ol": 195,
			"price_ol_ml": 100,
			"price_tf": 162.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"fleur-de-rocaille": {
			"title": "Fleur de Rocaille",
			"brand": "Caron",
			"price": 295,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/fleurs_de_rocaille_500x.jpg?v=1789849155",
			"gender": "Female",
			"collection_handle": "caron",
			"price_ol": 295,
			"price_ol_ml": 100,
			"price_tf": 245.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"haf": {
			"title": "Haf",
			"brand": "Ortir Apothecari",
			"price": 114,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ORTIRHAF_500x.png?v=1711459572",
			"gender": "Female",
			"collection_handle": "ortir-apothecari",
			"price_ol": 114,
			"price_ol_ml": 50,
			"price_tf": 95,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"sirio": {
			"title": "Sirio",
			"brand": "Mendittorosa",
			"price": 225,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Sirio_white_HD_500x.jpg?v=1589665482",
			"gender": "Unisex",
			"collection_handle": "mendittorosa",
			"price_ol": 225,
			"price_ol_ml": 100,
			"price_tf": 187.5,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"ta-if": {
			"title": "Ta’if",
			"brand": "Ormonde Jayne",
			"price": 235,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ta-if-88ml-front_1020x1530_1_500x.jpg?v=1711456555",
			"gender": "Unisex",
			"collection_handle": "ormonde-jayne",
			"price_ol": 235,
			"price_ol_ml": 88,
			"price_tf": 195.83,
			"price_tf_ml": 88,
			"concentration": "EdP"
		},
		"damascus-cologne": {
			"title": "Damascus Cologne | كولونيا دمشق",
			"brand": "Mabra",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mabra_damas_500x.jpg?v=1754915946",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 60,
			"price_ol_ml": 30,
			"price_tf": 50,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"mister-bungalow": {
			"title": "Mister Bungalow",
			"brand": "Circle of Lim",
			"price": 70,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mister_bungalow_03010c03-9dfd-406f-8160-e9836a09f6ed_500x.jpg?v=1775823138",
			"gender": "Unisex",
			"collection_handle": "circle-of-lim",
			"price_ol": 70,
			"price_ol_ml": 30,
			"price_tf": 58.33,
			"price_tf_ml": 30,
			"concentration": "EdC"
		},
		"rose-bergamote": {
			"title": "Rose & Bergamote",
			"brand": "100BON",
			"price": 35,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/rose_beramote_500x.jpg?v=1731682971",
			"gender": "Unisex",
			"collection_handle": "100bon",
			"price_ol": 35,
			"price_ol_ml": 50,
			"price_tf": 29.17,
			"price_tf_ml": 50,
			"concentration": "EdC"
		},
		"bloom-away-21-04": {
			"title": "Bloom Away 21.04",
			"brand": "Neydo",
			"price": 80,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/2104_500x.jpg?v=1793130689",
			"gender": "Female",
			"collection_handle": "neydo",
			"price_ol": 80,
			"price_ol_ml": 50,
			"price_tf": 66.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"mahoganysun": {
			"title": "Mahoganysun",
			"brand": "Perroy",
			"price": 70,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/perroy_mahoganysun_500x.jpg?v=1781190464",
			"gender": "Female",
			"collection_handle": "perroy",
			"price_ol": 70,
			"price_ol_ml": 50,
			"price_tf": 58.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"brume-dete": {
			"title": "Brume d'été",
			"brand": "Marie Jeanne",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/MARIEJEANNE_Mists_Brume-d-ete_shadow_500x.jpg?v=1738671197",
			"gender": "Unisex",
			"collection_handle": "marie-jeanne",
			"price_ol": 60,
			"price_ol_ml": 100,
			"price_tf": 50,
			"price_tf_ml": 100,
			"concentration": "EdC"
		},
		"joking-aside": {
			"title": "Joking Aside",
			"brand": "MINT",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/JOKING_ASIDE-2_500x.jpg?v=1589662031",
			"gender": "Unisex",
			"collection_handle": "mint",
			"price_ol": 170,
			"price_ol_ml": 70,
			"price_tf": 141.67,
			"price_tf_ml": 70,
			"concentration": "EdP"
		},
		"bo-bo": {
			"title": "Bo-Bo",
			"brand": "CARNER",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/bobo_new_carner_b5163c6c-252d-4539-b962-0c8af844155a_500x.jpg?v=1625873621",
			"gender": "Unisex",
			"collection_handle": "carner",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"ruby-wood": {
			"title": "Ruby Wood",
			"brand": "Exaltatum",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ruby_wood_28492c81-4baa-40fc-b269-7a1e03e750f7_500x.jpg?v=1780922026",
			"gender": "Unisex",
			"collection_handle": "exaltatum",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"marcel": {
			"title": "Marcel",
			"brand": "Marie Jeanne",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/marcell_500x.jpg?v=1636650775",
			"gender": "Unisex",
			"collection_handle": "marie-jeanne",
			"price_ol": 110,
			"price_ol_ml": 100,
			"price_tf": 91.67,
			"price_tf_ml": 100,
			"concentration": "EdC"
		},
		"match": {
			"title": "Match",
			"brand": "Der Duft",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Foto_DER_DUFT_MATCH_500x.jpg?v=1630486299",
			"gender": "Unisex",
			"collection_handle": "der-duft",
			"price_ol": 110,
			"price_ol_ml": 50,
			"price_tf": 91.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"fantomas": {
			"title": "Fantomas",
			"brand": "Nasomatto",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/fanromas_500x.jpg?v=1756323244",
			"gender": "Unisex",
			"collection_handle": "nasomatto",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"zelen": {
			"title": "Zelen",
			"brand": "Boka",
			"price": 215,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/boka_zelen_500x.jpg?v=1786786311",
			"gender": "Unisex",
			"collection_handle": "boka",
			"price_ol": 215,
			"price_ol_ml": 50,
			"price_tf": 179.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"st-pauls-1": {
			"title": "St. Paul's",
			"brand": "Exaltatum",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/pauls_500x.jpg?v=1780922153",
			"gender": "Unisex",
			"collection_handle": "exaltatum",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"tardes": {
			"title": "TARDES",
			"brand": "CARNER",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Carner_TARDES_500x.jpg?v=1589665508",
			"gender": "Female",
			"collection_handle": "carner",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"eastern-veil": {
			"title": "Eastern Veil",
			"brand": "Ylem",
			"price": 190,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/eastern_veil_500x.jpg?v=1777037850",
			"gender": "Unisex",
			"collection_handle": "ylem",
			"price_ol": 190,
			"price_ol_ml": 50,
			"price_tf": 158.33,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"oblivion": {
			"title": "Oblivion",
			"brand": "Circle of Lim",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/oblivion_500x.jpg?v=1775823323",
			"gender": "Unisex",
			"collection_handle": "circle-of-lim",
			"price_ol": 110,
			"price_ol_ml": 30,
			"price_tf": 91.67,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"coton-egyptien": {
			"title": "Coton Egyptien",
			"brand": "Phaedon Paris",
			"price": 95,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/COTON-EGYPTIEN_100ML_070_500x.jpg?v=1625875901",
			"gender": "Unisex",
			"collection_handle": "phaedon-paris",
			"price_ol": 95,
			"price_ol_ml": 100,
			"price_tf": 79.17,
			"price_tf_ml": 100,
			"concentration": "EdT"
		},
		"this-is-not-a-blue-bottle-1-5": {
			"title": "This is Not a Blue Bottle 1/.5 – Yang",
			"brand": "Histoires de Parfums",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/not_a_blue_bottle_05_a1717a78-2081-4768-9928-54443c305edb_500x.jpg?v=1757946373",
			"gender": "Unisex",
			"collection_handle": "histoires-de-parfums",
			"price_ol": 165,
			"price_ol_ml": 115,
			"price_tf": 137.5,
			"price_tf_ml": 115,
			"concentration": "EdP"
		},
		"coeur-de-noir-1": {
			"title": "Coeur de Noir",
			"brand": "Beaufort",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/medium_BeauFort_London_Coeur_de_Noir_Bloom_500x.jpg?v=1623765413",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"mora": {
			"title": "Mora",
			"brand": "Wolf Brothers",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/WB-MORA-PERFUME-50ml-white_ae9de80c-3bcb-4924-95ac-8680c188f74c_500x.jpg?v=1742991705",
			"gender": "Unisex",
			"collection_handle": "wolf-brothers",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"all-black": {
			"title": "All Black",
			"brand": "Insenf",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/all_black_500x.jpg?v=1783068081",
			"gender": "Unisex",
			"collection_handle": "insenf",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"cuirs": {
			"title": "CUIRS",
			"brand": "CARNER",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Carner_CUIRS_500x.jpg?v=1589660934",
			"gender": "Unisex",
			"collection_handle": "carner",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"kamel-oud": {
			"title": "Kamel Oud",
			"brand": "Sora Dora",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/kamel_oud_500x.jpg?v=1781698015",
			"gender": "Unisex",
			"collection_handle": "sora-dora",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"styrax": {
			"title": "STYRAX",
			"brand": "Headspace",
			"price": 175,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Styrax_Face_2380ac65-1d67-45c4-823e-9b496acf5eb0_500x.jpg?v=1699872642",
			"gender": "Unisex",
			"collection_handle": "headspace",
			"price_ol": 175,
			"price_ol_ml": 100,
			"price_tf": 145.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"dangerosa": {
			"title": "Dangerosa",
			"brand": "Reserve en Afrique 1934",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/dangerosa_500x.jpg?v=1783328654",
			"gender": "Unisex",
			"collection_handle": "reserve-en-afrique-1934",
			"price_ol": 150,
			"price_ol_ml": 100,
			"price_tf": 125,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"rusalka-s-embrace": {
			"title": "Rusalka’s Embrace",
			"brand": "Pictura Fragrans",
			"price": 177,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/RusalkasEmbracePicturaFragrans_500x.jpg?v=1775728815",
			"gender": "Unisex",
			"collection_handle": "pictura-fragrans",
			"price_ol": 177,
			"price_ol_ml": 50,
			"price_tf": 147.5,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"debonaire": {
			"title": "Debonaire",
			"brand": "Plowden & Fallow",
			"price": 108,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/pf_15_500x.jpg?v=1716723817",
			"gender": "Unisex",
			"collection_handle": "plowden-fallow",
			"price_ol": 108,
			"price_ol_ml": 100,
			"price_tf": 90,
			"price_tf_ml": 100,
			"concentration": "EdT"
		},
		"immortali-santi": {
			"title": "Immortali Santi",
			"brand": "Santi Peccatori",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/immortali_8ba78e9d-320c-49bd-99cb-f5e93d1b1985_500x.jpg?v=1786728538",
			"gender": "Unisex",
			"collection_handle": "santi-peccatori",
			"price_ol": 180,
			"price_ol_ml": 100,
			"price_tf": 150,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"nerosa": {
			"title": "Nerosa",
			"brand": "Laboratorio Olfattivo",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/nerosa_500x.jpg?v=1708450533",
			"gender": "Unisex",
			"collection_handle": "laboratorio-olfattivo",
			"price_ol": 120,
			"price_ol_ml": 100,
			"price_tf": 100,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"502-iris-cartagena": {
			"title": "502 Iris Cartagena",
			"brand": "Bon Parfumeur",
			"price": 175,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/502_iris_cartagena_500x.jpg?v=1739211593",
			"gender": "Unisex",
			"collection_handle": "bon-parfumeur",
			"price_ol": 175,
			"price_ol_ml": 100,
			"price_tf": 145.83,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"fathom-v-directors-cut": {
			"title": "Fathom V Director's Cut",
			"brand": "Beaufort",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/fathom_v_02_53b46293-1d32-434a-bb69-8af9023eb362_500x.jpg?v=1774687223",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 195,
			"price_ol_ml": 50,
			"price_tf": 162.5,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"devoti-peccatori": {
			"title": "Devoti Peccatori",
			"brand": "Santi Peccatori",
			"price": 210,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/devoti_7a69a5fd-156d-497b-8262-eefa0206d9c1_500x.jpg?v=1786727813",
			"gender": "Unisex",
			"collection_handle": "santi-peccatori",
			"price_ol": 210,
			"price_ol_ml": 50,
			"price_tf": 175,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"lumieres": {
			"title": "Lumières",
			"brand": "Otzlab",
			"price": 240,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/lumieres_500x.jpg?v=1781437469",
			"gender": "Unisex",
			"collection_handle": "otzlab",
			"price_ol": 240,
			"price_ol_ml": 40,
			"price_tf": 200,
			"price_tf_ml": 40,
			"concentration": "Extrait de Parfum"
		},
		"floating": {
			"title": "Floating",
			"brand": "Liis",
			"price": 160,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Liis_Silo_Floating_500x.jpg?v=1764836387",
			"gender": "Unisex",
			"collection_handle": "liis",
			"price_ol": 160,
			"price_ol_ml": 50,
			"price_tf": 133.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"honey-oud": {
			"title": "Honey Oud",
			"brand": "Floris",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/floris_honey_500x.jpg?v=1787327796",
			"gender": "Unisex",
			"collection_handle": "floris",
			"price_ol": 180,
			"price_ol_ml": 100,
			"price_tf": 150,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"lawrence": {
			"title": "Lawrence",
			"brand": "Carine Roitfeld",
			"price": 190,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/15700804_28379750_2048_500x.jpg?v=1632305905",
			"gender": "Unisex",
			"collection_handle": "carine-roitfeld",
			"price_ol": 190,
			"price_ol_ml": 90,
			"price_tf": 158.33,
			"price_tf_ml": 90,
			"concentration": "EdP"
		},
		"packed-your-holiday-perfume-wardrobe-unisex": {
			"title": "Packed: your holiday perfume wardrobe | Unisex",
			"brand": "Bloom Sample Packs",
			"price": 29,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/holiday_unisex_500x.jpg?v=1690809862",
			"gender": "Unisex",
			"collection_handle": "bloom-sample-packs",
			"price_ol": 29,
			"price_ol_ml": 6,
			"price_tf": 24.17,
			"price_tf_ml": 6,
			"concentration": "6"
		},
		"eau-de-the-gingembre": {
			"title": "Eau de thé & Gingembre",
			"brand": "100BON",
			"price": 35,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/eau_de_the_gingembre_500x.jpg?v=1731682971",
			"gender": "Unisex",
			"collection_handle": "100bon",
			"price_ol": 35,
			"price_ol_ml": 50,
			"price_tf": 29.17,
			"price_tf_ml": 50,
			"concentration": "EdC"
		},
		"jawa-cologne": {
			"title": "Jawa Cologne",
			"brand": "Mabra",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mabra_jawa_500x.jpg?v=1754922475",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 60,
			"price_ol_ml": 30,
			"price_tf": 50,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"cape-wrath": {
			"title": "Cape Wrath",
			"brand": "Beaufort",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/cape_wrath_500x.jpg?v=1728510293",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"mulberry-fig": {
			"title": "Mulberry Fig",
			"brand": "Floris",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/MulberryFigEDPBottleWEB_500x.jpg?v=1678814657",
			"gender": "Unisex",
			"collection_handle": "floris",
			"price_ol": 180,
			"price_ol_ml": 100,
			"price_tf": 150,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"monkeyflower": {
			"title": "Monkeyflower",
			"brand": "Stora Skuggan",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/monkey_629c066c-c1c3-4911-a0bc-d67074e30483_500x.jpg?v=1764953685",
			"gender": "Unisex",
			"collection_handle": "stora-skuggan",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"shikoku-cologne": {
			"title": "Shikoku Cologne | 四国ケルン",
			"brand": "Mabra",
			"price": 60,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mabra_shikoku_500x.jpg?v=1754923384",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 60,
			"price_ol_ml": 30,
			"price_tf": 50,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"naju-spring": {
			"title": "Naju Spring",
			"brand": "Pictura Fragrans",
			"price": 150,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/NajuSpringPicturaFragrans_500x.jpg?v=1775728469",
			"gender": "Unisex",
			"collection_handle": "pictura-fragrans",
			"price_ol": 150,
			"price_ol_ml": 50,
			"price_tf": 125,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"baraonda": {
			"title": "Baraonda",
			"brand": "Nasomatto",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/baraonda_500x.jpg?v=1756323552",
			"gender": "Unisex",
			"collection_handle": "nasomatto",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"sacreste": {
			"title": "Sacreste",
			"brand": "Laboratorio Olfattivo",
			"price": 120,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/sacreste_30537c77-f38c-4df0-8eda-aa4948cefa75_500x.jpg?v=1708450496",
			"gender": "Unisex",
			"collection_handle": "laboratorio-olfattivo",
			"price_ol": 120,
			"price_ol_ml": 100,
			"price_tf": 100,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"via-dell-incenso": {
			"title": "Via dell’Incenso",
			"brand": "Farmacia SS. Annunziata dal 1561",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/via_dellincenso_100ml_500x.jpg?v=1772737720",
			"gender": "Unisex",
			"collection_handle": "farmacia-ss-annunziata-dal-1561",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "Extrait de Parfum"
		},
		"arabesque": {
			"title": "Arabesque",
			"brand": "Ormonde Jayne",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/arabesque-88ml_1020x1530_5b8da67a-e434-4fe6-bd40-5aaba43b4088_500x.jpg?v=1696788803",
			"gender": "Unisex",
			"collection_handle": "ormonde-jayne",
			"price_ol": 195,
			"price_ol_ml": 88,
			"price_tf": 162.5,
			"price_tf_ml": 88,
			"concentration": "EdP"
		},
		"parfum-sacre": {
			"title": "Parfum Sacré",
			"brand": "Caron",
			"price": 295,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/sacre_500x.jpg?v=1789848682",
			"gender": "Unisex",
			"collection_handle": "caron",
			"price_ol": 295,
			"price_ol_ml": 100,
			"price_tf": 245.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"blond-redhead-16-11": {
			"title": "Blond Redhead 16.11",
			"brand": "Neydo",
			"price": 80,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/neydo_blond_500x.jpg?v=1770373615",
			"gender": "Unisex",
			"collection_handle": "neydo",
			"price_ol": 80,
			"price_ol_ml": 50,
			"price_tf": 66.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"black-afgano": {
			"title": "Black Afgano",
			"brand": "Nasomatto",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/black_afgano_500x.jpg?v=1756323933",
			"gender": "Unisex",
			"collection_handle": "nasomatto",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"arcimboldo": {
			"title": "Arcimboldo",
			"brand": "Zanini",
			"price": 210,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/arcimboldo_3fbd69b8-f711-4be3-9f7b-90cfed1fc3b7_500x.jpg?v=1789409919",
			"gender": "Unisex",
			"collection_handle": "zanini",
			"price_ol": 210,
			"price_ol_ml": 30,
			"price_tf": 175,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"in-principio-v2": {
			"title": "In principio V2",
			"brand": "Zanini",
			"price": 255,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/in-principio_500x.jpg?v=1789410401",
			"gender": "Unisex",
			"collection_handle": "zanini",
			"price_ol": 255,
			"price_ol_ml": 30,
			"price_tf": 212.5,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"rosi-rosa": {
			"title": "Rosi Rosa",
			"brand": "Mabra",
			"price": 75,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/rosi_rosa_500x.jpg?v=1752667965",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 75,
			"price_ol_ml": 30,
			"price_tf": 62.5,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"experimentum-crucis": {
			"title": "Experimentum Crucis",
			"brand": "Etat Libre d'Orange",
			"price": 240,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Experimentum_Crucis_flacon_100_ml_04b0f3a3-a95e-484c-b596-a8fbfb7c8e9c_500x.jpg?v=1625875926",
			"gender": "Unisex",
			"collection_handle": "etat-libre-dorange",
			"price_ol": 240,
			"price_ol_ml": 100,
			"price_tf": 200,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"fathom-v": {
			"title": "Fathom V",
			"brand": "Beaufort",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/medium_new_beaufort_fathom_5_bloom_500x.jpg?v=1623765447",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"pg10-aomassa": {
			"title": "PG10 Aomassaï",
			"brand": "Pierre Guillaume - Parfumerie Générale",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/PG10_Aomassai_500x.jpg?v=1592593845",
			"gender": "Unisex",
			"collection_handle": "pierre-guillaume-parfumerie-generale",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdT"
		},
		"roissant-afe": {
			"title": "Сroissant Сafe",
			"brand": "Versatile",
			"price": 55,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/croissant_cafe_500x.jpg?v=1790357723",
			"gender": "",
			"collection_handle": "versatile",
			"price_ol": 55,
			"price_ol_ml": 30,
			"price_tf": 45.83,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"cacao-pimento": {
			"title": "Cacao Pimento",
			"brand": "La Closerie",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/cacao_pimento_8ff21320-e107-4b4c-8fa8-1254960a4598_500x.jpg?v=1790694364",
			"gender": "Unisex",
			"collection_handle": "la-closerie",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"pg04-musc-maori": {
			"title": "PG04 Musc Maori",
			"brand": "Pierre Guillaume - Parfumerie Générale",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/pg04_musc_maori_500x.jpg?v=1589662150",
			"gender": "Unisex",
			"collection_handle": "pierre-guillaume-parfumerie-generale",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"boccanera": {
			"title": "Boccanera",
			"brand": "Orto Parisi",
			"price": 155,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/orto_bocanera_500x.jpg?v=1758619581",
			"gender": "Unisex",
			"collection_handle": "orto-parisi",
			"price_ol": 155,
			"price_ol_ml": 50,
			"price_tf": 129.17,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"07-orris-cacao": {
			"title": "07 Orris+Cacao",
			"brand": "Raer",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/orris_500x.jpg?v=1789414289",
			"gender": "Unisex",
			"collection_handle": "raer",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"tabac-exquis": {
			"title": "Tabac Exquis",
			"brand": "Caron",
			"price": 295,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/tabac_exquis_500x.jpg?v=1789849442",
			"gender": "Unisex",
			"collection_handle": "caron",
			"price_ol": 295,
			"price_ol_ml": 100,
			"price_tf": 245.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"almond-suede": {
			"title": "Almond Suede",
			"brand": "Arquiste",
			"price": 195,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/almond_suede_500x.gif?v=1728847398",
			"gender": "Unisex",
			"collection_handle": "arquiste",
			"price_ol": 195,
			"price_ol_ml": 100,
			"price_tf": 162.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"flight-mode": {
			"title": "Flight Mode",
			"brand": "MINT",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/flight_mode_500x.jpg?v=1737305704",
			"gender": "Unisex",
			"collection_handle": "mint",
			"price_ol": 170,
			"price_ol_ml": 70,
			"price_tf": 141.67,
			"price_tf_ml": 70,
			"concentration": "EdP"
		},
		"orange-patina": {
			"title": "Orange Patina",
			"brand": "Insenf",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/orange_500x.jpg?v=1783068174",
			"gender": "Unisex",
			"collection_handle": "insenf",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"iris-tannat": {
			"title": "Iris Tannat",
			"brand": "Danner & Flemming",
			"price": 280,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/iris_tannat_b2151c4c-4c3e-44fe-ac0b-93dcb46941d5_500x.jpg?v=1793647899",
			"gender": "Unisex",
			"collection_handle": "danner-flemming",
			"price_ol": 280,
			"price_ol_ml": 50,
			"price_tf": 233.33,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"malt-night-fighter": {
			"title": "No. 10 – Night Warrior",
			"brand": "Binet-Papillon",
			"price": 215,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/binet-papillon-10_892de856-3f0b-4282-9261-be6f19e48905_500x.jpg?v=1764008363",
			"gender": "Unisex",
			"collection_handle": "binet-papillon",
			"price_ol": 215,
			"price_ol_ml": 100,
			"price_tf": 179.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"kar-wai": {
			"title": "Kar-Wai",
			"brand": "Carine Roitfeld",
			"price": 190,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/9eba791468f6eb4f332b4cf6c8a2b1c0d4011a9a_kar-wai-no-background-reduced_500x.jpg?v=1632307163",
			"gender": "Unisex",
			"collection_handle": "carine-roitfeld",
			"price_ol": 190,
			"price_ol_ml": 90,
			"price_tf": 158.33,
			"price_tf_ml": 90,
			"concentration": "EdP"
		},
		"chere-colette": {
			"title": "Chère Colette (Dear Colette)",
			"brand": "Milleseve",
			"price": 115,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/CC-100ml---4_500x.jpg?v=1789385739",
			"gender": "Female",
			"collection_handle": "milleseve",
			"price_ol": 115,
			"price_ol_ml": 100,
			"price_tf": 95.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"alyazia": {
			"title": "Alyazia",
			"brand": "Maysson",
			"price": 180,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/alyazia_500x.jpg?v=1789046156",
			"gender": "Unisex",
			"collection_handle": "maysson",
			"price_ol": 180,
			"price_ol_ml": 50,
			"price_tf": 150,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		},
		"almaz": {
			"title": "Almaz",
			"brand": "Kajal",
			"price": 175,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/almaz_27124158-ddbd-4c08-a6c1-0cc8603e5a8f_500x.jpg?v=1765389857",
			"gender": "Unisex",
			"collection_handle": "kajal",
			"price_ol": 175,
			"price_ol_ml": 100,
			"price_tf": 145.83,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"baise-moi-on-the-dancefloor": {
			"title": "Baise Moi On The Dancefloor",
			"brand": "Discothèque",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/baise_moi_500x.jpg?v=1768128001",
			"gender": "Unisex",
			"collection_handle": "discotheque",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"iris-cendr": {
			"title": "Iris Cendré",
			"brand": "Naomi Goodsir",
			"price": 170,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/iris_cendre_naomi_goodsir_500x.jpg?v=1589662020",
			"gender": "Unisex",
			"collection_handle": "naomi-goodsir",
			"price_ol": 170,
			"price_ol_ml": 50,
			"price_tf": 141.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"iris-coriandre": {
			"title": "Iris Coriandre",
			"brand": "La Closerie",
			"price": 165,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/iris_coriandre_500x.jpg?v=1790695633",
			"gender": "Unisex",
			"collection_handle": "la-closerie",
			"price_ol": 165,
			"price_ol_ml": 100,
			"price_tf": 137.5,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"pour-un-homme": {
			"title": "Pour Un Homme de Caron",
			"brand": "Caron",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/pour_un_homme_01_500x.jpg?v=1792507504",
			"gender": "Male",
			"collection_handle": "caron",
			"price_ol": 125,
			"price_ol_ml": 125,
			"price_tf": 104.17,
			"price_tf_ml": 125,
			"concentration": "EdT"
		},
		"forbidden-rouge": {
			"title": "Forbidden Rouge",
			"brand": "Mystikum",
			"price": 210,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/forbidden_a8dc0b7e-4539-49f8-84a0-61e6c58ecfd7_500x.jpg?v=1791808008",
			"gender": "Unisex",
			"collection_handle": "mystikum",
			"price_ol": 210,
			"price_ol_ml": 100,
			"price_tf": 175,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"giardinodiercole": {
			"title": "Giardinodiercole",
			"brand": "Agatho",
			"price": 305,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Giardinodiercole_500x.jpg?v=1657827277",
			"gender": "Unisex",
			"collection_handle": "agatho",
			"price_ol": 305,
			"price_ol_ml": 100,
			"price_tf": 254.17,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"sirenide": {
			"title": "Sirenide",
			"brand": "Beaufort",
			"price": 125,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/Sirenide_18206527-e2d7-4982-81c0-088e1839b75a_500x.jpg?v=1760544266",
			"gender": "Unisex",
			"collection_handle": "beaufort",
			"price_ol": 125,
			"price_ol_ml": 50,
			"price_tf": 104.17,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"princess-of-china": {
			"title": "Princess of China | 中國公主",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/bc-princess-china_500x.jpg?v=1762937954",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"01-nagarmotha": {
			"title": "01 Nagarmotha",
			"brand": "Raer",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/nagarmotha_500x.jpg?v=1789414597",
			"gender": "Unisex",
			"collection_handle": "raer",
			"price_ol": 130,
			"price_ol_ml": 30,
			"price_tf": 108.33,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"tears": {
			"title": "Tears",
			"brand": "Comme Des Garcons",
			"price": 135,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/tears_8226a79b-1acc-4a50-94ce-ab85bc92ec4c_500x.jpg?v=1789757776",
			"gender": "Unisex",
			"collection_handle": "comme-des-garcons",
			"price_ol": 135,
			"price_ol_ml": 50,
			"price_tf": 112.5,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"rital-date": {
			"title": "Rital Date",
			"brand": "Versatile",
			"price": 55,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/riral_date_500x.jpg?v=1790357498",
			"gender": "Unisex",
			"collection_handle": "versatile",
			"price_ol": 55,
			"price_ol_ml": 30,
			"price_tf": 45.83,
			"price_tf_ml": 30,
			"concentration": "Extrait de Parfum"
		},
		"mango-sticky-rice": {
			"title": "Mango Sticky Rice",
			"brand": "d'Annam",
			"price": 130,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/mango_sticky_500x.jpg?v=1774364519",
			"gender": "Unisex",
			"collection_handle": "dannam",
			"price_ol": 130,
			"price_ol_ml": 50,
			"price_tf": 108.33,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"panorama": {
			"title": "Panorama",
			"brand": "Olfactive Studio",
			"price": 140,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/products/Olfactive_studio_Panorama_500x.jpg?v=1589662143",
			"gender": "Unisex",
			"collection_handle": "olfactive-studio",
			"price_ol": 140,
			"price_ol_ml": 100,
			"price_tf": 116.67,
			"price_tf_ml": 100,
			"concentration": "EdP"
		},
		"josephine": {
			"title": "Joséphine",
			"brand": "Rance 1795",
			"price": 110,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/josephine_500x.jpg?v=1779881662",
			"gender": "Unisex",
			"collection_handle": "rance-1795",
			"price_ol": 110,
			"price_ol_ml": 50,
			"price_tf": 91.67,
			"price_tf_ml": 50,
			"concentration": "EdP"
		},
		"lances": {
			"title": "Lances",
			"brand": "Otzlab",
			"price": 240,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/lances_500x.jpg?v=1781437392",
			"gender": "Unisex",
			"collection_handle": "otzlab",
			"price_ol": 240,
			"price_ol_ml": 40,
			"price_tf": 200,
			"price_tf_ml": 40,
			"concentration": "Extrait de Parfum"
		},
		"fawaki-fawaki": {
			"title": "Fawaki Fawaki",
			"brand": "Mabra",
			"price": 75,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/fawaki_500x.jpg?v=1750076161",
			"gender": "Unisex",
			"collection_handle": "mabra",
			"price_ol": 75,
			"price_ol_ml": 30,
			"price_tf": 62.5,
			"price_tf_ml": 30,
			"concentration": "EdP"
		},
		"ambre-de-noir": {
			"title": "Ambre de Noir",
			"brand": "Maksim Perfume",
			"price": 240,
			"image": "https://cdn.shopify.com/s/files/1/2129/6897/files/ambe_de_noir_500x.jpg?v=1793039606",
			"gender": "Unisex",
			"collection_handle": "maksim-perfume",
			"price_ol": 240,
			"price_ol_ml": 50,
			"price_tf": 200,
			"price_tf_ml": 50,
			"concentration": "Extrait de Parfum"
		}
	},
	"collections": {
		"cosy-comfort": {
			"title": "Cosy Comfort",
			"subtitle": "Warm, soft, enveloping fragrances",
			"category": "By Mood",
			"description": "A collection of fragrances that feel like a soft lamp-lit room: warm, tactile, and quietly addictive.",
			"products": [
				{
					"handle": "vanille-glace",
					"gift_description": "This belongs here because its vanilla feels like baked comfort rather than sheer sweetness, wrapped in brown sugar, toasted madeleine and cinnamon. Espresso keeps it from becoming cloying, while sandalwood and tonka give it a smooth, blanket-like finish."
				},
				{
					"handle": "ruby",
					"gift_description": "Ruby fits the theme through its plush, dessert-like heart, where cherry, red berries and pineapple are cushioned by whipped cream and brown sugar. Amber, musk and benzoin make the sweetness feel cozy and rounded instead of bright or airy."
				},
				{
					"handle": "vanhera",
					"gift_description": "Vanhera earns its place by turning vanilla into something hushed and warming, with Madagascar vanilla absolute softened by sandalwood and amber. Pepper, cardamom and cinnamon add a dry, spiced edge that makes the comfort feel deeper and more textured."
				},
				{
					"handle": "happy-nuts",
					"gift_description": "This is cosy comfort with a nutty, roasted heart: pistachio, sesame and caramelized nuts create a creamy gourmand glow. Tonka, vanilla and guaiac wood add softness, smoke and richness, giving the fragrance a snug, edible warmth."
				},
				{
					"handle": "sadonaso",
					"gift_description": "Sadonaso fits because it translates comfort into something intimate and close to the skin, with coffee, tobacco and tonka folded into sandalwood. The powdery softness keeps it cozy, while the woody and bodily undertones make it more private and magnetic than a simple gourmand."
				},
				{
					"handle": "nightfall-in-the-city-of-kings",
					"gift_description": "Nightfall in the City of Kings belongs in this set for its twilight softness: peach and cumin bring a warm skin-like effect over cedarwood. Turkish rose, amber and sandalwood add a velvety, dusky glow that feels like heat lingering after sunset."
				},
				{
					"handle": "dark-honey",
					"gift_description": "Dark Honey suits the theme through its dense, syrupy warmth, where baked peach and plum liqueur melt into rum and dark vanilla. Cinnamon and patchouli deepen the richness, while animalic honey gives it a rough, glowing sweetness that feels far from airy."
				},
				{
					"handle": "mooncake",
					"gift_description": "Mooncake | 月餅 fits as a memory of edible comfort, built around golden cake crust, salted-egg-yolk richness and tender crumbs. Honey and vanilla keep it soft and homely, making the fragrance feel like a quiet, nostalgic treat rather than a loud gourmand."
				},
				{
					"handle": "nebula-viridis",
					"gift_description": "Nebula Viridis earns its place by smoothing its brightness into warmth: lemon and bergamot are tempered by saffron, smoky woods and resin. Sandalwood, benzoin and amber round the edges, leaving a golden, calm finish that still feels gently radiant."
				}
			]
		},
		"fresh-escape": {
			"title": "Fresh Escape",
			"subtitle": "Clean, airy, holiday-feeling compositions",
			"category": "By Mood",
			"description": "A bright, salt-brushed escape where citrus, greens and airy woods feel like a clean horizon opening up.",
			"products": [
				{
					"handle": "masa",
					"gift_description": "Masa belongs here because its grapefruit, bergamot and ginger make the opening feel brisk and travel-ready, while Mirabelle plum and violet leaf keep it from reading too sharp. The amber, vanilla and woods add a sun-warmed finish that still stays light on the skin."
				},
				{
					"handle": "resonant",
					"gift_description": "Resonant fits this theme through its yuzu, ginger and petitgrain, which give the composition a cool, wind-swept clarity. The metallic accord and ozonic marine trace make it feel especially airy and modern, with guaiac wood and ambergris adding depth without heaviness."
				},
				{
					"handle": "sal-y-limon",
					"gift_description": "Sal y Limón earns its place with lemon, lime and bergamot sparkling over a sea-salt accord, capturing the easy brightness of a coastal breeze. Orange blossom, musk and patchouli keep the citrus clean but not thin, so the freshness lingers with a soft saline edge."
				},
				{
					"handle": "a-grove-by-the-sea",
					"gift_description": "A Grove By The Sea suits Fresh Escape because fig leaves, rosemary and sea salt create a green shoreline mood that feels open and restorative. The conifer resin, mineral clay and olive nuance give it a textured, natural freshness that stands apart from simpler citrus picks."
				},
				{
					"handle": "neroli-petitgrain",
					"gift_description": "Neroli & Petitgrain fits the theme because mandarin, neroli and petitgrain create a freshly rinsed brightness that reads effortless and clean. Orange blossom and a soft vanilla trace add a gentle warmth, giving this cologne a comforting clarity rather than a sharp citrus bite."
				},
				{
					"handle": "thirsty",
					"gift_description": "Thirsty fits this collection by turning apple, bamboo and galbanum into a crisp, hydrating effect that feels like cool skin after water. Fig, cedar and musk round it out with a smooth, clean dryness that keeps the scent bright rather than sweet."
				},
				{
					"handle": "portamarina",
					"gift_description": "Portamarina belongs in Fresh Escape for the way citrus, apple and sage are lifted by a sea-breeze accord, making the whole composition feel washed by morning air. The iris adds a powdery calm, while the salty texture and understated woods keep it airy and architectural."
				},
				{
					"handle": "pg16-jardins-de-kerylos",
					"gift_description": "PG16 Jardins de Kérylos suits the collection with its green fig, white peach and sycamore wood, which evoke a shaded garden after the heat has passed. Tuberose and musk soften the edges, making the freshness feel leafy, luminous and quietly elegant."
				},
				{
					"handle": "playground-1",
					"gift_description": "Playground belongs here because its bergamot, honeydew melon and marine accord deliver that open-air, chilled-water sensation at the heart of Fresh Escape. Jasmine, lily-of-the-valley and cashmere wood keep the profile transparent and soft, with just enough salt to suggest sea air."
				}
			]
		},
		"dark-smoky": {
			"title": "Dark & Smoky",
			"subtitle": "Smoke, leather, incense, evening character",
			"category": "By Mood",
			"description": "A curation of embers and shadow, where leather, incense, and smoke settle into fragrances that feel made for the hour after dusk.",
			"products": [
				{
					"handle": "moonlight-ceremony",
					"gift_description": "Moonlight Ceremony belongs here for the way birch tar and smoked black tea immediately sketch a fire-blackened atmosphere, then incense and leather deepen it. Saffron, guaiac wood, and labdanum keep the darkness warm rather than stark."
				},
				{
					"handle": "black",
					"gift_description": "Black earns its place through a scorched core of black pepper, incense, and birch tar that feels raw and atmospheric. Leather, liquorice, and dry woods make it more like soot and saddle than sleek refinement."
				},
				{
					"handle": "oud-imperial-extrait",
					"gift_description": "Oud Imperial Extrait fits the theme because its smoky oud is pulled into sharper relief by birch tar and ambery labdanum. The animalic depth, resinous glow, and dark leather nuance give it a smouldering, nocturnal gravity."
				},
				{
					"handle": "vi-et-armis",
					"gift_description": "Vi et Armis is a natural fit for this collection: black tea, pepper, and incense create a dense, tarred haze that reads immediately smoky. Oud, bitter woods, and its narcotic warmth make it one of the more challenging, maritime-minded choices."
				},
				{
					"handle": "corpus-equus",
					"gift_description": "Corpus Equus belongs in the lineup for its forceful leather accord, darkened by birch tar, incense, and smoke. That combination gives it a heated, animalic tension, with a restless energy that keeps the composition from feeling static."
				},
				{
					"handle": "honey-and-tar",
					"gift_description": "Honey and Tar fits the theme by turning birch tar and smoky leather into a sticky, shadowed accord rather than a purely austere one. Honey, vanilla, and resinous incense add a molasses-like glow that softens the rougher woods."
				},
				{
					"handle": "beso-de-fuego",
					"gift_description": "Beso de Fuego earns inclusion through its frankincense-led darkness, with cardamom and black pepper adding a dry, embered bite. Jasmine sambac, cedarwood, and cypress keep it nocturnal and charred rather than floral in the usual sense."
				},
				{
					"handle": "cruelle",
					"gift_description": "Cruelle suits this collection because smoky tobacco, frankincense, and oud build a brooding frame around the fruitier notes. Blackberry, plum, and a rubbery leather facet give it a plush but dangerous purple darkness."
				},
				{
					"handle": "cuoium",
					"gift_description": "Cuoium is here for its uncompromising leather heart, sharpened by cade, patchouli, and violet leaf. The smoky, animalic texture feels rugged and tactile, with labdanum adding a resinous shadow that lingers."
				}
			]
		},
		"soft-skin-scents": {
			"title": "Soft Skin Scents",
			"subtitle": "Musks, close-to-skin, delicate fragrances",
			"category": "By Mood",
			"description": "A study in near-silence: musks, powders, and milky textures that seem to bloom only when they touch skin.",
			"products": [
				{
					"handle": "cashmere-100",
					"gift_description": "Cashmere 100 belongs here for its cashmere-soft intimacy, where white musk and sandalwood blur into skin rather than project. Orris, honeysuckle, and humid mangrove wood give it a textured hush that feels gently worn-in."
				},
				{
					"handle": "white-rice",
					"gift_description": "White Rice fits the theme by turning the idea of skin scent into something tenderly edible and close, with a quiet savory thread. Nutty grain, pandan, and powdery orris set it apart, while white musk and jasmine keep it soft and restrained."
				},
				{
					"handle": "pg-41-le-musc-la-peau",
					"gift_description": "PG4.1 Le Musc & La Peau is almost a manifesto for this collection: seven musks are built to read as warm skin, not statement perfume. Tonka bean, cedar, and a trace of vanilla give its transparency a faintly creamy, woody contour."
				},
				{
					"handle": "the-ghost-in-the-shell",
					"gift_description": "The Ghost In The Shell earns its place by pairing futuristic clarity with a milky skin accord, making closeness feel clean and modern. Metallic yuzu, rain-washed florals, and pear-like fruit keep it airy and distinct from the more powder-led scents here."
				},
				{
					"handle": "les-cahiers-secrets",
					"gift_description": "Les Cahiers Secrets (Secret Diaries) suits the theme through its contrast of pressed-powder delicacy and body warmth, which makes the musk feel intimate rather than loud. Orris, heliotrope, and cumin create a dry-soft tension that sets it apart."
				},
				{
					"handle": "merina",
					"gift_description": "Merina belongs in this selection because its milky almond and iris composition settles like a refined second skin. The fennel's faint anise lift, jasmine, and woolly softness give it a quiet sensuality unlike the more floral musks in the group."
				},
				{
					"handle": "yawahada",
					"gift_description": "Yawahada (Soft skin) is a natural fit for the collection, built around the impression of warm skin cushioned by rice powder and musk. Pear, green notes, and a milky rose-jasmine heart make it softly sweet without losing its airy finish."
				},
				{
					"handle": "blanc-sada",
					"gift_description": "Blanc Sada fits this theme by translating skin closeness into a clean cosmetic veil of rice powder and iris. The straw and tatami notes add a dry, woven texture, giving it a quieter, more tactile character than the creamier picks."
				},
				{
					"handle": "intime-extime",
					"gift_description": "Intime-Extime belongs here because it balances intimacy and distance through powdery iris, milk, and musk, making the scent feel almost like skin remembered. White tea, incense, and Itauba wood add a cool lift and a subtle woody depth."
				}
			]
		},
		"sweet-but-sophisticated": {
			"title": "Sweet but Sophisticated",
			"subtitle": "Gourmand fragrances with depth and complexity",
			"category": "By Mood",
			"description": "A study in dessert notes with backbone: these gourmands balance sugar, toast, spice and woods so sweetness feels composed, not cloying.",
			"products": [
				{
					"handle": "vanille-glace",
					"gift_description": "Vanille Glacé belongs here because it takes vanilla and dresses it in structure: brown sugar syrup, cinnamon and toasted madeleine give it pastry richness, while espresso and sandalwood keep the sweetness taut."
				},
				{
					"handle": "jany",
					"gift_description": "Jany fits the theme by turning apple tart into a more nuanced gourmand, where baked fruit, cinnamon and buttery pastry are lifted by apricot, osmanthus and walnut for extra dimension."
				},
				{
					"handle": "happy-nuts",
					"gift_description": "NUTS earns its place through its roasted caramel core, but what makes it sophisticated is the savory contrast of pistachio, sesame and guaiac wood against tonka and vanilla creaminess."
				},
				{
					"handle": "vietnamese-coffee",
					"gift_description": "Vietnamese Coffee is a perfect fit because it frames sweetness through bitterness: condensed milk, dark roast coffee and bitter chocolate are sharpened by smoky facets and warm woods, giving the gourmand a darker profile."
				},
				{
					"handle": "celestial-object",
					"gift_description": "Celestial Object suits this collection by making edible notes feel close and restrained, with sugared almond, milky rice-paper softness and buttery popcorn nuance balanced by salt and tonka."
				},
				{
					"handle": "calisson",
					"gift_description": "Calisson belongs in the lineup for its confectionary elegance: bergamot and orange-blossom biscuit add brightness, while milky sandalwood, caramel and vanilla create a creamy finish with pastry depth."
				},
				{
					"handle": "vanilla-for-breakfast",
					"gift_description": "Vanilla For Breakfast fits because it is not just sweet vanilla, but layered vanilla: milky almond, creamy benzoin and roasted tonka add toasted breadth and a fuller, more textured gourmand shape."
				},
				{
					"handle": "davana-vanille",
					"gift_description": "Davana & Vanille earns inclusion by keeping the dessert idea light and lifted, with davana's fruity-herbal snap, lychee sweetness and a custard-like impression softened by sandalwood and tonka."
				},
				{
					"handle": "dominanti-santi",
					"gift_description": "Dominanti Santi fits the theme by pairing crème brûlée sweetness with contrast: orange blossom, vanilla and tonka are edged by salted skin and gourmand patchouli, giving the composition savory depth and glow."
				}
			]
		},
		"joyful-citrus": {
			"title": "Joyful Citrus",
			"subtitle": "Bright citrus and sunny mood",
			"category": "By Mood",
			"description": "A sunlit study in citrus, where peel, blossom, and breeze turn brightness into mood rather than mere freshness.",
			"products": [
				{
					"handle": "yuzu",
					"gift_description": "Yuzu belongs here because it makes citrus feel vivid and tactile, moving from astringent peel to sweet-sour pulp with bergamot, orange, and lime. The thyme twist keeps it tart, brisk, and lightly bitter instead of candy-like."
				},
				{
					"handle": "neroli-petitgrain",
					"gift_description": "Neroli & Petitgrain fits this collection through its clean, green citrus softness: mandarin, neroli, and petitgrain read like sunlight on leaves. Orange blossom and a whisper of vanilla give it a washed, comforting glow that still stays bright."
				},
				{
					"handle": "sal-y-limon",
					"gift_description": "Sal y Limón earns its place by turning citrus into a seaside sparkle, with lemon, lime, and bergamot lifted by a sharp salt accord. Orange blossom, musk, and patchouli make the brightness feel breezy and mineral rather than merely zesty."
				},
				{
					"handle": "resonant",
					"gift_description": "Resonant suits the theme because it pushes citrus into a cooler, more modern register, where yuzu and ginger flash against a metallic sheen. Petitgrain, guaiac wood, and ambergris add a marine-green depth that keeps the composition crisp and airy."
				},
				{
					"handle": "this-is-not-a-blue-bottle-1-6",
					"gift_description": "This is Not a Blue Bottle 1/.6 – Love fits the collection by pairing tart yuzu and grapefruit with orange blossom for a radiant, affectionate citrus-floral profile. Neroli, white musks, and vetiver soften the edges, giving the brightness a tender, close-to-skin finish."
				},
				{
					"handle": "kajal-iii",
					"gift_description": "Kajal III belongs here because its grapefruit and lemon soda opening feels like citrus in full daylight, sharpened by ginger and herbal lavender-rosemary facets. Neroli, amber, and Ambroxan add warmth and structure, so the freshness never turns thin."
				},
				{
					"handle": "elisa",
					"gift_description": "Elisa fits the theme through its candied bergamot and yuzu, which make the citrus feel cheerful without losing elegance. Davana, orange blossom, sandalwood, and benzoin add a soft, sun-dried sweetness that reads as calm and luminous."
				},
				{
					"handle": "still-life-in-rio",
					"gift_description": "Still Life in Rio earns its spot by taking citrus into a tropical register, where yuzu, mandarin, and ginger sparkle over creamy coconut. Rum and Brazilian copaiba give it a warm, woody hum that keeps the brightness playful and sun-baked."
				},
				{
					"handle": "en-plein-air",
					"gift_description": "En Plein Air belongs in Joyful Citrus because grapefruit, bergamot, and lemon are set against rain-damp white flowers, creating freshness with movement and air. Soft musk and an earthy undertow make the brightness feel open, vivid, and slightly wild."
				}
			]
		},
		"first-niche-gift": {
			"title": "First Niche Gift",
			"subtitle": "A safe, beautiful entry into niche perfumery",
			"category": "Gift Ideas",
			"description": "A thoughtful first step into niche perfumery: these are gifts that feel familiar at first wear, then reveal a more nuanced world.",
			"products": [
				{
					"handle": "pg-41-le-musc-la-peau",
					"gift_description": "Choose this when you want a first niche gift that feels like skin, not a statement; the white musk, tonka bean, and ylang-ylang keep it approachable while hinting at a more intimate, modern style. It suits a recipient who prefers subtle elegance for everyday wear or a private, close-to-the-body occasion."
				},
				{
					"handle": "ramune",
					"gift_description": "Gift this to someone who loves clean, cheerful scents but is ready for something with more personality than a department-store citrus. Lemon, aldehydes, and mint make it bright and easy to wear, while rose, lily-of-the-valley, and vanilla add a nostalgic softness that feels charming for birthdays or springtime gifting."
				},
				{
					"handle": "santal",
					"gift_description": "This is a smart bridge from mainstream freshness to niche craft, especially for someone who likes their gifts refined rather than showy. Sandalwood, frankincense, and black pepper give it a crisp, composed character that works beautifully for a man or woman who wants something polished for work or evening dinners."
				},
				{
					"handle": "cashmere-100",
					"gift_description": "A strong choice if you want a first niche perfume that whispers luxury through texture rather than volume. Orris, white musk, and sandalwood create a cashmere-like smoothness, with honeysuckle and vanilla adding a gentle warmth that makes it ideal for someone who likes understated, comforting gifts."
				},
				{
					"handle": "afrique-en-fleurs",
					"gift_description": "This fits the theme because it introduces niche perfumery through light, airy refinement rather than intensity. Orange, ginger, and chamomile give it a sunlit lift, while rose, iris, and sandalwood add a graceful depth that suits a recipient who enjoys elegant daytime wear."
				},
				{
					"handle": "isla-bohemia",
					"gift_description": "Pick this for someone moving beyond generic sweet scents into something creamier and more composed. Icy pear, bergamot, and iris keep the opening bright and accessible, while vanilla, sandalwood, and musk give it a mellow finish that feels easy to love as a first serious fragrance."
				},
				{
					"handle": "figlia-di-eva",
					"gift_description": "This is a distinctive stepping-stone scent for a recipient who likes brightness but wants something more textured than a simple freshie. Bergamot, green fruit, and blue oud create a striking contrast, then tonka and white musk round it out so the gift feels both accessible and intriguingly different."
				},
				{
					"handle": "tobacco-tuberose",
					"gift_description": "Choose this when you want to introduce someone to niche through drama that still feels wearable. Bitter violet leaf, tobacco smoke, and leather give it a moody, bookish depth, while the tuberose heart keeps it velvety enough for an evening gift or someone drawn to more characterful perfumes."
				},
				{
					"handle": "mad",
					"gift_description": "A good gift for the person who wants freshness with a little edge, this one shows how niche can feel clean without being plain. Blackcurrant, vetiver, and green woods sharpen the character, while the soft floral warmth and smooth musk make it wearable for someone with a modern, adventurous taste."
				}
			]
		},
		"can-t-go-wrong-gifts": {
			"title": "Can't-Go-Wrong Gifts",
			"subtitle": "Universally appealing, safe choices",
			"category": "Gift Ideas",
			"description": "A shortlist of fragrances that feel easy to love at first wear, yet still give your gift a touch of character and discernment.",
			"products": [
				{
					"handle": "rose-shiso",
					"gift_description": "Choose this when you want a rose that feels approachable but not plain: the shiso’s minty-green lift and iris-cedar frame keep the floral heart airy, so it suits a recipient you know only broadly. It reads as a safe gift with enough freshness and woodiness to feel considered for birthdays or thank-yous."
				},
				{
					"handle": "pg-41-le-musc-la-peau",
					"gift_description": "This is a smart zero-risk pick for someone who prefers clean, skin-close scents or may wear fragrance lightly, because the musks stay transparent rather than loud. Ylang-ylang, tonka bean and a touch of vanilla add warmth and polish, making it an easy gift for a partner, sibling, or colleague."
				},
				{
					"handle": "concrete",
					"gift_description": "Gift this if you want something modern and broadly wearable, but with a twist that keeps it from feeling generic: the sandalwood is rendered smooth and mineral, while rose oxide and warm spices add lift. Its clean density makes it a confident present for someone who likes understated style."
				},
				{
					"handle": "afrique-en-fleurs",
					"gift_description": "This fits the collection because it is bright, friendly, and easy to please, with orange and ginger giving immediate charm before the powdery rose and iris settle in. Sandalwood and vanilla soften the finish, so it works well as a safe gift for someone who enjoys floral scents without heaviness."
				},
				{
					"handle": "neon-night",
					"gift_description": "A very low-stakes gift for someone who likes fresh, upbeat fragrances: bergamot and mandarin make the opening instantly likable, while green tea keeps it crisp and unobtrusive. Peony, white flowers and smooth guaiac wood give it enough personality to feel thoughtful without being challenging."
				},
				{
					"handle": "the-empress-kiss",
					"gift_description": "Pick this when you want a gift that feels generous and glamorous but still easy to wear, since saffron, cardamom and clove give the rose-and-oud heart a familiar spicy glow rather than a difficult edge. Raspberry, caramel and vanilla make it especially suitable for a recipient who likes a touch of sweetness."
				},
				{
					"handle": "ambre-royal",
					"gift_description": "This is a strong can't-go-wrong choice for a fragrance gift because it opens with bright bergamot and floral clarity before settling into cedarwood, patchouli and amber warmth. It feels refined without being austere, so it suits someone whose tastes you want to respect without overcommitting to a niche direction."
				},
				{
					"handle": "summer-street",
					"gift_description": "Choose this for a recipient who likes cheerful, easygoing scents: blackcurrant and apricot jam give it instant appeal, while muguet and honeysuckle keep the bouquet light and familiar. Benzoin, vanilla and honey add a soft golden finish that makes it feel gift-friendly for almost any occasion."
				},
				{
					"handle": "malli-nadu",
					"gift_description": "This belongs here because jasmine and tuberose are rendered with enough mandarin sparkle and davana brightness to stay inviting, not overpowering. Sandalwood and vetiver smooth the white-flower richness, making it a safe yet stylish present for someone who enjoys elegant florals."
				}
			]
		},
		"statement-gifts": {
			"title": "Statement Gifts",
			"subtitle": "Bold fragrances with wow-factor",
			"category": "Gift Ideas",
			"description": "For the gift that lands like a declaration, these are fragrances chosen to make them feel singular, prized, and impossible to forget.",
			"products": [
				{
					"handle": "moonlight-ceremony",
					"gift_description": "Choose Moonlight Ceremony when you want a present that feels ceremonial and a little dangerous—perfect for someone who loves dramatic evenings or a milestone birthday. Birch tar, smoked black tea, and incense give it its shadowed signature, while leather and guaiac wood make the gesture feel richly considered."
				},
				{
					"handle": "myrrh-blood-drop",
					"gift_description": "No. 8 – Drop of Blood suits the recipient who appreciates gifts with nerve: it is bold, rarefied, and a little uncompromising, ideal for an anniversary or a collector. Mandarin, saffron, and a metallic flicker cut through the resinous core, before oud and bittersweet myrrh leave a woody, leathery trail."
				},
				{
					"handle": "au-pied-du-rosier",
					"gift_description": "Au Pied du Rosier is the one to give when you want to surprise someone with elegance that still has bite—lovely for a partner, mother, or anyone who enjoys rose with attitude. Honeyed Grasse rose, new-trainer leather, and saffron create the contrast, while oud adds depth and keeps the bloom from feeling dainty."
				},
				{
					"handle": "bohemian",
					"gift_description": "Bohemian makes sense as a statement gift for someone who likes their style rugged, smoky, and self-assured—think a significant birthday or a person with a rebellious streak. Leather, incense, and smoke set the tone, then white pepper, vetiver, and patchouli give it a rough-hewn richness that feels unmistakably bold."
				},
				{
					"handle": "eye-contact",
					"gift_description": "Eye Contact is an excellent pick when the gift should feel flirtatious and charged, especially for a date-night devotee or romantic occasion. Pink pepper, saffron, and supple leather give it instant impact, while oud, black amber, and musk keep the finish warm and intimate."
				},
				{
					"handle": "fauno",
					"gift_description": "Fauno is the choice for gifting someone who loves depth, history, and a fragrance with presence—ideal for a connoisseur or a special celebration. Smoky tobacco, frankincense, and leather evoke a torchlit scene, and juniper with clary sage adds an aromatic lift that sets it apart from the heavier woods here."
				},
				{
					"handle": "oud-imperial-extrait",
					"gift_description": "Oud Imperial Extrait is the most fitting gift when you want to signal real esteem, since its extrait concentration feels especially luxurious for a major occasion. Smoky oud, birch tar, and labdanum create the dark core, but the rounder, ambery finish gives it a smoother, more opulent authority than the Eau de Parfum."
				},
				{
					"handle": "veles",
					"gift_description": "Veles is a strong choice for someone who likes their gifts wild, elemental, and a touch offbeat—perfect for an outdoorsy spirit or winter celebration. Rhubarb, saffron, and tarred leather make it unusually vivid, while amber and patchouli deepen the smoky woodiness into something primal."
				},
				{
					"handle": "athanor",
					"gift_description": "Athanor belongs in the most serious gift category: it says the recipient is worth something truly special, whether for an important anniversary or a once-in-a-lifetime gesture. Sulphur, saffron, and incense make it startlingly distinctive, then guaiac wood and dark amber turn that heat into a dense, resinous glow."
				}
			]
		},
		"little-luxuries": {
			"title": "Little Luxuries",
			"subtitle": "Special but accessible gifts",
			"category": "Gift Ideas",
			"description": "Small luxuries with real presence: these are the gifts that feel like a treat, yet stay easy to give.",
			"products": [
				{
					"handle": "santorini",
					"gift_description": "Santorini is a lovely choice when you want something small but precious that still feels like a true indulgence. The green fig, milky coconut, and soft orris make it ideal for a recipient who likes freshness with a creamy, giftable ease."
				},
				{
					"handle": "1993",
					"gift_description": "1993 suits the friend or partner who enjoys a more dramatic present without drifting into excess. Its peach, dark chocolate, and earthy patchouli give this little bottle a vintage swagger that feels especially right for a confident birthday or evening gift."
				},
				{
					"handle": "2025-bestsellers-for-him",
					"gift_description": "This set is perfect when you want to let him explore without committing to a full bottle, so it fits the collection’s treat-size, luxurious idea beautifully. The marine brightness, smoked tobacco, and dusty resins offer enough contrast to feel special while still remaining approachable for everyday wear."
				},
				{
					"handle": "vanille-creme",
					"gift_description": "Vanille Creme belongs here because it turns a modest gift into a genuinely indulgent dessert-like gesture. Bergamot, caramel, and buttery vanilla custard make it an easy win for anyone who loves sweet fragrances and appreciates a cozy, celebratory present."
				},
				{
					"handle": "blueforest",
					"gift_description": "Blueforest is a smart pick when you want something understated that still feels carefully chosen. Dry cedarwood, black pepper, and airy musk give it a crisp, tactile character that works well for someone who prefers clean woods over louder perfumes."
				},
				{
					"handle": "fiery-fig-24-05",
					"gift_description": "Fiery Fig 24.05 makes a beautiful small luxury because it feels sunlit and refined rather than oversized. Fresh fig leaves, creamy coconut, and fig-tree bark give the recipient a Mediterranean warmth that suits summer gifting or a thoughtful thank-you."
				},
				{
					"handle": "figo",
					"gift_description": "Figo fits this collection by making fig feel polished yet easy to wear as a gift. The orange brightness, milky fruit, and guaiacwood-amber base create a balanced profile that feels special for someone who likes fresh scents with a gentle woody glow."
				},
				{
					"handle": "niche-discovery-box",
					"gift_description": "This discovery box is the most flexible little luxury in the collection, ideal when you want the gift to feel generous without guessing too hard. Bergamot, lavender, peppermint, frankincense, and hay give the recipient a varied introduction to niche perfume, perfect for a birthday or first-fragrance present."
				},
				{
					"handle": "patchouli-labdanum",
					"gift_description": "Patchouli & Labdanum suits the small-but-precious theme because it delivers depth in a compact, accessible format. The smoky patchouli, resinous labdanum, and leather-like warmth make it a thoughtful pick for someone who likes earthy scents with a quietly confident edge."
				}
			]
		},
		"gifts-under-50": {
			"title": "Gifts Under £50",
			"subtitle": "Beautiful gifts without breaking the bank",
			"category": "Gift Ideas",
			"description": "Thoughtful gifts with room to delight: these picks feel generous in the hand and gentle on the budget.",
			"products": [
				{
					"handle": "2025-bestsellers-for-her",
					"gift_description": "This suits a gift for someone who loves variety, because nine crowd-pleasing styles let you tailor the present without overspending. The mix of milky gourmands, honeyed softness and bright fruity florals makes it feel abundant, while still staying comfortably under budget."
				},
				{
					"handle": "patchouli-labdanum",
					"gift_description": "Choose this for a recipient who appreciates a more grown-up, quietly confident scent rather than something sugary or obvious. Patchouli, labdanum and smoky amber-wood create a grounded, leathered warmth that feels special and substantial for the price."
				},
				{
					"handle": "exploration-kit",
					"gift_description": "This is a smart gift when you want a range of moods in one box, especially for someone who likes to test before settling on a signature. Cedarwood, iso e super and marshmallow give it airy woods, a clean modern lift and a soft sweetness that feels generous without pushing the spend."
				},
				{
					"handle": "j-scent-discovery-set",
					"gift_description": "Pick this for a fragrance lover who enjoys discovering something a little different, making it ideal for birthdays or a thoughtful thank-you. Green grass, peppermint and yuzu bring a crisp, vivid edge, while violet and vanilla add a softer finish so the set feels varied and gift-worthy."
				}
			]
		},
		"gifts-under-100": {
			"title": "Gifts Under £100",
			"subtitle": "The sweet spot of quality and value",
			"category": "Gift Ideas",
			"description": "A smart gifting edit for when you want niche character, quality, and a thoughtful price tag to feel perfectly aligned.",
			"products": [
				{
					"handle": "1993",
					"gift_description": "Choose 1993 for a recipient who likes their gifts with bite: the peach-chocolate contrast and earthy patchouli make it feel more assured than a safe crowd-pleaser. Its vintage chypre structure suits a birthday or milestone present when you want quality niche without overspending."
				},
				{
					"handle": "cherry-passion",
					"gift_description": "Cherry Passion is the easy win for someone who enjoys cheerful, romantic scents and a gift that feels instantly joyful. The ripe cherry-strawberry duo, sweet almond, and creamy jasmine-rose heart give it a more playful profile than the other bottles here."
				},
				{
					"handle": "fig-lemonade",
					"gift_description": "Pick Fig Lemonade when you want a present that feels fresh, modern, and effortless to wear in warm weather. The sparkling lemon, green fig leaves, and blackcurrant-like buchu keep it distinct, while cedarwood adds just enough structure for a thoughtful under-£100 gift."
				},
				{
					"handle": "vanille-creme",
					"gift_description": "Vanille Creme is the right gift if you know the recipient loves comfort scents and you want something indulgent without going over budget. Bergamot, caramel, and buttery vanilla custard make it a standout gourmand in this collection, especially for cozy evenings or a sweet-toothed fragrance fan."
				},
				{
					"handle": "blueforest",
					"gift_description": "Blueforest suits the person who prefers clean, understated fragrances and would appreciate something polished for daily wear. Its dry cedarwood, black pepper, and soft musk give it a crisp, woodsy profile that feels considered rather than flashy."
				},
				{
					"handle": "cloud-essence-28-08",
					"gift_description": "Cloud Essence 28.08 makes a lovely gift for someone drawn to soft, comforting scents with a quietly distinctive twist. Milk, rice powder, and mimosa create the gentle signature here, while white musk and sandalwood keep it serene and giftable for almost any occasion."
				},
				{
					"handle": "figo",
					"gift_description": "Figo is ideal if you want to gift a fig fragrance that feels fresh rather than heavy, with a relaxed elegance that works year-round. Orange brightness, milky fig, and guaiacwood set it apart, making it a neat choice for someone who likes subtle sophistication."
				},
				{
					"handle": "series-3-incense-avignon",
					"gift_description": "Series 3: Incense | Avignon is the best pick for a recipient who enjoys contemplative, atmospheric perfumes and doesn't mind something a little austere. Incense, elemi, and damp-stone minerality give it a cathedral-like feel, making it a memorable niche gift without pushing past the £100 sweet spot."
				},
				{
					"handle": "osmanthus-oolong",
					"gift_description": "Osmanthus & Oolong fits the person who likes tea scents with a soft floral lift and a calm, refined mood. Apricot-tinged osmanthus, oolong tea, and guaiac wood make it feel airy yet grounded, which is ideal when you want quality niche without overthinking the choice."
				}
			]
		},
		"gifts-under-150": {
			"title": "Gifts Under £150",
			"subtitle": "Premium gifts for a special occasion",
			"category": "Gift Ideas",
			"description": "A smartly priced edit of niche gifts that feel special occasion-ready, with enough character to impress without crossing the line into extravagance.",
			"products": [
				{
					"handle": "565",
					"gift_description": "Choose 565 when you want a gift that feels celebratory and a little daring: its candied bergamot, dark musk and incense-shadowed woods give the recipient something clearly niche, yet still comfortably under £150."
				},
				{
					"handle": "a-pas-de-loup",
					"gift_description": "À Pas De Loup suits a gift for someone who prefers quiet luxury over showiness, with damp earth, mushrooms and oakmoss making it a thoughtful woodland choice, lifted by clary sage and benzoin warmth."
				},
				{
					"handle": "afternoon-tea",
					"gift_description": "Afternoon Tea is a polished present for the tea lover who also enjoys florals, because its lemon, maté and champaca feel refined and gift-worthy without becoming excessive, while vetiver and jasmine add depth."
				},
				{
					"handle": "arashiyama",
					"gift_description": "Arashiyama is ideal if you want to give calm elegance: bamboo leaves, cut grass and rain-soaked earth make it a serene, modern niche gift, with violet and bamboo wood keeping the greenery soft rather than sharp."
				},
				{
					"handle": "deer",
					"gift_description": "Deer works beautifully for the person who likes their gifts with backbone, since tobacco, green coffee and hay give it a rugged niche feel, while cedar, leather and herbs keep the profile distinctive and masculine-leaning."
				},
				{
					"handle": "milk",
					"gift_description": "Milk Personal is the safer premium gourmand gift when you want something intimate rather than loud, because marshmallow, tonka and skin musk stay close to the wearer, with white cedar and mahogany adding a grown-up edge."
				},
				{
					"handle": "paper-soap",
					"gift_description": "Soap Paper is a lovely gift for someone who appreciates nostalgia and cleanliness in one bottle, as aldehydes, lavender and lily-of-the-valley recreate the charm of fresh soap while musk keeps it softly intimate."
				},
				{
					"handle": "pg31-tonka-bodycon",
					"gift_description": "PG31 Tonka Bodykon fits this collection as the more indulgent-but-still-sensible choice: rum, apple and smoky tonka create a rich niche impression, while licorice and woody undertones stop it from feeling sugary."
				},
				{
					"handle": "rouge-karkade",
					"gift_description": "Rouge Karkade is the standout if you want a gift with presence and polish, because hibiscus tea, pomegranate and buchu bring vivid lift, while vanilla, cedar and oud smoke make it feel unmistakably special."
				}
			]
		},
		"last-minute-gifts": {
			"title": "Last-Minute Gifts",
			"subtitle": "Sample sets, discovery sets — quick and thoughtful",
			"category": "Gift Ideas",
			"description": "For the gift you had to choose fast, these discovery sets look considered, generous, and just personal enough to seem planned.",
			"products": [
				{
					"handle": "2025-bestsellers-for-her",
					"gift_description": "A smart pick when you need to look thoughtful in a rush: this set leans into cosy florals and soft woods, so it suits someone who likes easy-to-love scents. The milky sweetness, peachy fruit, and powdery amber-vanilla finish make it feel like a gift chosen with real care for a partner, sister, or close friend."
				},
				{
					"handle": "sample-set-1",
					"gift_description": "This is the one to send when you want a present that feels a little more distinctive than a single bottle. Angelica root, chamomile, and pine give it a cool herbal lift, while incense and sandalwood add depth, so it reads as a clever last-minute gift for someone who likes unusual, quietly refined fragrances."
				},
				{
					"handle": "dark-drunk-discovery-kit",
					"gift_description": "If you need a gift that says you know their taste without overexplaining it, this discovery kit does the job. The dark-fruit bite, boozy richness, and smoky-woody edge make it feel bold and immediate, so it suits a recipient who enjoys statement scents and makes your hurried choice seem deliberately daring."
				},
				{
					"handle": "j-scent-discovery-set",
					"gift_description": "A tidy, elegant choice for someone who prefers freshness with personality, this set feels considered even when bought at the eleventh hour. Peppermint, green grass, and yuzu keep it bright and lively, while geranium, violet, and vanilla soften the edges for a gift that works well for a friend, sibling, or spring birthday."
				},
				{
					"handle": "oddity-discovery-set",
					"gift_description": "Choose this when you want your quick purchase to land as modern and polished. Blackcurrant, mandarin, and yuzu give it a sparkling opening, while orris, sandalwood, and vetiver add a cool, textured finish that makes the set feel like a well-judged present for someone who likes contemporary, slightly offbeat perfumes."
				}
			]
		},
		"for-the-curious-nose": {
			"title": "For the Curious Nose",
			"subtitle": "For adventurous fragrance explorers",
			"category": "Persona",
			"description": "A cabinet of fragrant oddities for the nose that prefers detours, daring materials, and the thrill of smelling something slightly wrong in a fascinating way.",
			"products": [
				{
					"handle": "osang",
					"gift_description": "Osang belongs here because it leans into the feral side of perfumery, where fenugreek, honey, and smoky myrrh feel more ritual than refinement. Pepper, incense, and ashen iris make it a restless study in resin, heat, and animalic shadow."
				},
				{
					"handle": "cavallo-di-fuoco",
					"gift_description": "火马 - Cavallo di fuoco fits this collection by turning aromatic freshness into something mischievous and strange, with mint, celery, and pomelo colliding in unexpected ways. Ginger, osmanthus, and honeyed Chinese oud give it a vivid, smoky texture that rewards the adventurous nose."
				},
				{
					"handle": "black-oregano",
					"gift_description": "Black Oregano earns its place through its refusal to behave like a standard leather scent: lime and rosemary cut through a blast of sun-baked oregano. Sandalwood, vetiver, and oud push it into darker, rougher territory with a dry, herbal bite."
				},
				{
					"handle": "rouge",
					"gift_description": "Rouge is chosen for the way it makes beetroot smell like an idea rather than a note, pairing earthy sweetness with dry incense and metallic geranium. Pink pepper, ginger, and labdanum give it a savory, off-kilter edge that suits a curious explorer."
				},
				{
					"handle": "boar",
					"gift_description": "Boar belongs in this lineup because it treats the forest floor as a fragrance material, mixing apple with mushroom, truffle, and conifer smoke. Leather, vetiver, and fur-like animalic warmth make it feel wild, damp, and deliberately untamed."
				},
				{
					"handle": "indian-grass",
					"gift_description": "Indian Grass fits the theme through its unusual cannabis accord, which is propped up by coriander, elemi, and smoky guaiac wood. Incense and patchouli add a woody, slightly animalic tension that makes the composition feel experimental without losing its green pulse."
				},
				{
					"handle": "monopteros",
					"gift_description": "Monopteros is a perfect oddity for this collection, using coconut, icy aldehydes, and coffee in a combination that feels both playful and disorienting. Cardamom and neroli sharpen the contrast, while its woody base adds a sly, offbeat sensuality."
				},
				{
					"handle": "v-i-t-r-i-o-l",
					"gift_description": "V.I.T.R.I.O.L. is here because it goes past mere weirdness into full confrontation, using civet, decaying cucumber, and burnt rubber as the core of its concept. Rose, violet leaf, and olive oil make the beauty-versus-foulness tension feel intentionally abrasive and unforgettable."
				},
				{
					"handle": "dawn-whispers",
					"gift_description": "Dawn Whispers fits this collection by showing that novelty can be quiet as well as shocking, with ozone and metallic notes cooling into milk, white musk, and pale woods. Its transparent amber structure feels alien yet comforting, a strange soft landing for the curious nose."
				}
			]
		},
		"for-the-minimalist": {
			"title": "For the Minimalist",
			"subtitle": "Clean, calm, elegant fragrances",
			"category": "Persona",
			"description": "A study in clean lines and soft light: these scents stay close, composed, and quietly refined.",
			"products": [
				{
					"handle": "rainy-city",
					"gift_description": "Rainy City belongs here because its black tea and green tea clarity reads like a pared-back wardrobe for the skin, while vetiver and transparent woods keep the composition crisp. The Earl Grey steam effect and rain-darkened urban feel add just enough detail without disturbing its restraint."
				},
				{
					"handle": "flow",
					"gift_description": "Flow fits the theme through its airy transparency and gentle structure, making simplicity feel intentional rather than bare. Lily of the valley, green herbs, and a soft ambergris trace give it a clean lift, while sandalwood and cedar keep the line neat and calm."
				},
				{
					"handle": "seychelles-breeze",
					"gift_description": "Seychelles Breeze suits this collection because its fresh greens and light floral brightness feel uncluttered and easy to wear. Freshly cut grass, freesia, and orange blossom are framed by airy musk and ambroxan, giving it a sunlit clarity that never turns heavy."
				},
				{
					"handle": "lumire-blanche",
					"gift_description": "Lumière Blanche earns its place through a minimalist balance of cool spice and soft creaminess, like a white room warmed by light. Cardamom, almond milk, and creamy sandalwood create a smooth, quiet contour, with tonka bean adding a gentle, skin-close finish."
				},
				{
					"handle": "albatros",
					"gift_description": "Albatros fits the minimalist brief by turning marine freshness into something spare, open, and precise. Sea salt, cedarwood, and vetiver provide a clean architectural frame, while the aquatic foam, rose, and soft musk keep it luminous rather than ornate."
				},
				{
					"handle": "vetiver-santal",
					"gift_description": "Vetiver Santal belongs in this edit because it pairs brightness and depth with unusual discipline, never straying far from the skin. Bergamot, Haitian vetiver, and New Caledonian sandalwood are rounded by benzoin and white musk, giving the scent a dry warmth and tidy elegance."
				},
				{
					"handle": "pg191-neroli-ad-astra",
					"gift_description": "PG19.1 Neroli ad Astra suits the theme with its transparent neroli core and measured use of citrus and wood. Mandarin, green geranium, and agave flower bring a crisp, pale brightness, while vetiver lends a powdery, controlled finish."
				},
				{
					"handle": "in-this-world",
					"gift_description": "In This World fits because it feels like quiet clothing for the senses: fresh, tactile, and close to the body. Fig, blood orange, and violet stay airy and green, while lavender, sage, and tonka wood give it a calm, lived-in softness."
				},
				{
					"handle": "pomelo-oolong",
					"gift_description": "Pomelo Oolong belongs here for its bright, uncluttered composition, where tea and citrus stay in clear focus. Pomelo, oolong, and osmanthus are lifted by white musk, with a green woody edge that leaves the fragrance serene and composed."
				}
			]
		},
		"for-the-maximalist": {
			"title": "For the Maximalist",
			"subtitle": "Rich, bold, attention-grabbing fragrances",
			"category": "Persona",
			"description": "A cabinet of scent in full voice: these are the fragrances that enter first, linger longest, and never apologize for taking up space.",
			"products": [
				{
					"handle": "osang",
					"gift_description": "Osang belongs here because its animalic extrait style turns volume into atmosphere: fenugreek, molten honey and smoky myrrh create a feral richness, while pepper, incense and ashen iris sharpen the drama."
				},
				{
					"handle": "moonlight-ceremony",
					"gift_description": "Moonlight Ceremony fits the maximalist brief by stacking smoke on smoke until the air feels crowded with presence: birch tar, smoked black tea and incense are brightened by saffron, then sunk into amber and labdanum."
				},
				{
					"handle": "cavallo-di-fuoco",
					"gift_description": "火马 - Cavallo di fuoco earns its place through restless contrast and sheer audacity: mint, celery and pomelo make the opening vivid, while ginger, pepper and honeyed Chinese oud push it into smoky, leathery excess."
				},
				{
					"handle": "myrrh-blood-drop",
					"gift_description": "No. 8 – Drop of Blood suits this collection because it refuses subtlety, moving from mandarin and saffron into incense, patchouli and oud with a metallic flash that gives the resinous base extra bite."
				},
				{
					"handle": "oud-imperial-extrait",
					"gift_description": "Oud Imperial Extrait is maximalism in its most forceful register, with smoky oud, ambery labdanum and birch tar creating a dense leathered aura that feels bigger, darker and more commanding than the Eau de Parfum."
				},
				{
					"handle": "eye-contact",
					"gift_description": "Eye Contact fits the theme by turning spice and leather into instant theatre: pink pepper and saffron spark off supple leather, then oud, black amber and musk build a warm trail with real projection."
				},
				{
					"handle": "velvet-bold",
					"gift_description": "Velvet | Bold belongs in this lineup because it amplifies every facet of the original idea: saffron, rose and vanilla are wrapped in smoky leather, while patchouli, amber and labdanum give it a broad, shadowy sweep."
				},
				{
					"handle": "skin-fetish",
					"gift_description": "Skin Fetish is here for its unapologetic contrast of lush fruit and haze: dark cherry, rose and incense create a plush opening, then oud, amber and woods pull it into a deeper, more provocative register."
				},
				{
					"handle": "andevalo",
					"gift_description": "Andevalo fits the maximalist mood by pairing juicy brightness with heavy resinous depth: strawberry, raspberry and black cherry glow against labdanum, leather and patchouli, leaving a smoky amber trail with plenty of force."
				}
			]
		},
		"for-the-collector": {
			"title": "For the Collector",
			"subtitle": "Rare, distinctive, conversation-starting fragrances",
			"category": "Persona",
			"description": "A cabinet of scents for the serious collector: rare materials, sharp contrasts, and compositions that refuse to stay polite.",
			"products": [
				{
					"handle": "iris-altesse",
					"gift_description": "Iris Altesse belongs here because it treats iris as a study in contrast rather than comfort, pairing chalky leather and cool extrait density with orange blossom brightness. Jasmine sambac and cassis give it a crisp, slightly unexpected lift that keeps this classic material feeling newly acquired."
				},
				{
					"handle": "myrrh-blood-drop",
					"gift_description": "No. 8 – Drop of Blood earns its place through its uncompromising darkness: a metallic flash, saffron bite, and mandarin opening lead into incense, patchouli, oud, and myrrh. That resinous, woody, leathery trail makes it the sort of bottle a collector keeps for its tension and rarity."
				},
				{
					"handle": "cambodian-oud",
					"gift_description": "Cambodian Oud fits the theme by putting a distinctive natural oud at full volume, without sanding down its smoke or rough-hewn character. The leathery depth and woody haze make it a serious material-led composition, prized for authenticity over polish."
				},
				{
					"handle": "castiamanti",
					"gift_description": "Castiamanti reads like a collector’s piece because it layers spice and floral richness into something weighty, warm, and unusually detailed. Myrtle, saffron, honeyed rose, incense, and labdanum are sharpened by vanilla and oud, giving it a resinous complexity that rewards repeated wear."
				},
				{
					"handle": "high-to-lotus",
					"gift_description": "High to Lotus belongs in a serious collection for its strange, green architecture: artichoke, watery lotus, and seaweed create a briny, almost alien opening. Peppermint, anise florals, oud, and sandalwood push it into a dark leathered territory that few fragrances attempt."
				},
				{
					"handle": "nuit-de-baklite",
					"gift_description": "Nuit de Bakélite fits this collection by celebrating tuberose in its most severe, stalk-driven form rather than its lushest one. Galbanum, carrot seed, cardamom, and leather create a dry, green tension that makes it a standout for anyone who values edge over softness."
				},
				{
					"handle": "rosa-umami-v2",
					"gift_description": "Rosa umami V2 is here because it challenges the idea of rose with a savory, fermented profile that feels genuinely uncommon. Miso, seaweed, olive, and black tea set up a dark bouquet that makes the flower feel earned, not decorative."
				},
				{
					"handle": "a-masquerade",
					"gift_description": "A Masquerade suits the theme through its layered duality: raspberry and tuberose bring brightness, while leather, oud, and rum deepen it into something more secretive. The result is a chypre with enough contrast and intrigue to hold attention in a well-built wardrobe."
				},
				{
					"handle": "athanor",
					"gift_description": "Athanor earns its place as a collector's bottle by embracing an unusual sulphuric spark and turning it into smoke, heat, and transformation. Saffron, incense, guaiac wood, and dark amber give it a molten, resinous gravity that feels singular rather than merely rich."
				}
			]
		},
		"for-the-romantic": {
			"title": "For the Romantic",
			"subtitle": "Soft, sensual, warm fragrances",
			"category": "Persona",
			"description": "A tender bouquet for the heart-led: fruit, petals, and warm musks gathered into a soft-focus romance.",
			"products": [
				{
					"handle": "lamar",
					"gift_description": "Lamar belongs here because its lush fruit-and-flower abundance feels openly affectionate, like a romantic gesture translated into scent. Juicy pineapple, rose, and jasmine are cushioned by vanilla, amber, and musk, giving the sweetness a warm, embracing glow."
				},
				{
					"handle": "guava-sorbet",
					"gift_description": "Guava Sorbet fits this theme through its bright, affectionate sweetness that never turns sharp, staying dreamy and gently flirtatious instead. Guava, passionfruit, and mandarin lead into osmanthus, jasmine, and creamy vanilla, with a soft tropical finish that reads tender rather than loud."
				},
				{
					"handle": "helene",
					"gift_description": "Hélène suits the collection because it balances buoyant charm with a warm, sentimental undercurrent, making the floral-fruity composition feel softly romantic. Pear, peach blossom, and magnolia sparkle above amber, tonka bean, and patchouli, which round the fragrance into a gentle, lived-in warmth."
				},
				{
					"handle": "asoral",
					"gift_description": "Asoral earns its place through its dewy rose heart, which feels intimate and vulnerable rather than dramatic. Turkish and Lani roses, magnolia, and peony are lifted by tea, liquorice, and hay nuances, creating an airy bouquet with a plush, quietly loving finish."
				},
				{
					"handle": "ensang-noir",
					"gift_description": "Ensang Noir fits this collection as the shadowed counterpart to its softer florals, proving romance can be moody as well as tender. Dark rose, metallic aldehydes, and magnolia are deepened by opoponax, patchouli, and smoked woods, giving it a brooding elegance that still keeps the heart of the flower intact."
				},
				{
					"handle": "juice-personal",
					"gift_description": "Juice | Personal belongs here because it captures the private side of romance: close, delicate, and softly glowing against the skin. Raspberry and rhubarb are filtered through rose, airy woods, and musk, so the fruit feels like a whisper rather than a shout."
				},
				{
					"handle": "koiame",
					"gift_description": "恋雨 Koiame (It's Raining Love) fits the theme by turning rain-kissed freshness into a memory of affection and quiet longing. Mandarin, cassis, and apple brighten magnolia, jasmine, and water lily, while peach, cedarwood, and musk leave a clean, wistful trail."
				},
				{
					"handle": "marbella",
					"gift_description": "Marbella suits this collection because its peachy floral warmth feels sunlit, affectionate, and easy to love. Peach, rose, and jasmine are accented by pink pepper and amber, giving the composition a radiant softness with a graceful, romantic tilt."
				},
				{
					"handle": "purple-eclipse",
					"gift_description": "Purple Eclipse belongs in this selection for its plush rose core, which feels dreamy and softly opulent rather than severe. Apple, blackcurrant, and pink pepper bring sparkle, while heliotrope, honeyed white flowers, amber, vanilla, and musk wrap the scent in a velvety finish."
				}
			]
		},
		"for-the-trend-lover": {
			"title": "For the Trend-Lover",
			"subtitle": "Modern, fashionable, social-friendly fragrances",
			"category": "Persona",
			"description": "A feed-ready edit of glossy fruits, boozy warmth and woods with just enough edge to feel right now.",
			"products": [
				{
					"handle": "ziveli",
					"gift_description": "Živeli belongs here because its plum brandy, raspberry and saffron feel instantly current, while honeyed leather and tobacco give it the kind of bar-side swagger that reads social and now."
				},
				{
					"handle": "agrani",
					"gift_description": "Agrani fits the trend-led brief by turning cherry and almond into a bold, camera-ready statement, then sharpening it with saffron, pipe tobacco and Burmese oud for a more niche, talked-about contrast."
				},
				{
					"handle": "ruby",
					"gift_description": "Ruby lands in this collection for its cherry, red berries and pineapple burst, a sweet profile that feels very of-the-moment; whipped cream, brown sugar and benzoin keep it plush without losing its playful pop."
				},
				{
					"handle": "skin-fetish",
					"gift_description": "Skin Fetish fits the theme by pairing dark cherry with incense and rose, a contrast that feels bold and very now; oud, amber and woods push it into a smoky, leathery lane with real attitude."
				},
				{
					"handle": "vettii",
					"gift_description": "Vettii suits the theme by taking bright fig and quince into a richer, more fashion-forward register, where saffron, amber and myrrh add a luxe depth and oud, sandalwood and tolu balsam keep it distinctly elevated."
				},
				{
					"handle": "andevalo",
					"gift_description": "Andevalo earns its place through the strawberry, raspberry and black cherry trio, a juicy combination that feels immediately contemporary, then gets deepened by labdanum, leather and patchouli for a darker, reel-worthy finish."
				},
				{
					"handle": "vietnamese-coffee",
					"gift_description": "Vietnamese Coffee fits this edit because coffee scents are having a clear moment, and this one leans into that with dark roast, condensed milk and bitter chocolate before tonka and warm woods smooth it into something very wearable."
				},
				{
					"handle": "drunk-saffron",
					"gift_description": "DRUNK SAFFRON matches the collection’s zeitgeist with its cognac-spiked saffron opening, a boozy profile that feels current and conversation-starting; supple leather, patchouli and vanilla add warmth without softening the edge."
				},
				{
					"handle": "closeup",
					"gift_description": "Close-Up belongs here for its green coffee and boozy cherry opening, a combo that feels sharp and modern rather than traditional, then tobacco, cedar and bright musks give it a cleaner, more streetwise finish."
				}
			]
		},
		"sandalwood-gifts": {
			"title": "Sandalwood Gifts",
			"subtitle": "Creamy, warm sandalwood-centred fragrances",
			"category": "By Olfactive Direction",
			"description": "For the gift-giver seeking warmth with elegance, these sandalwood-led scents range from quiet woods to smoky comforts and luminous skin-close blends.",
			"products": [
				{
					"handle": "a-pas-de-loup",
					"gift_description": "Choose this for a recipient who prefers a more contemplative gift: the sandalwood here bridges mushroomy damp earth, oakmoss, and benzoin, making the forest feel tender rather than rugged."
				},
				{
					"handle": "entre-ciel-et-mer",
					"gift_description": "A good pick for someone who loves crisp, coastal fragrances but wants comfort underneath; sandalwood rounds out sea salt, algae, and ambergris, while cedar and lavender keep it brisk."
				},
				{
					"handle": "hey-bitch",
					"gift_description": "This suits a bolder gift recipient because sandalwood smooths the contrast between bitter leather, tart rhubarb, and sueded iris, with vanilla adding a creamy finish that keeps the drama wearable."
				},
				{
					"handle": "japanese-whiskey",
					"gift_description": "Gift this to someone who enjoys refined, cozy spirits-inspired scents; sandalwood links toasted malt, chestnut, and mizunara oak, while the smoky whisky accord and green aromatic edge give it character."
				},
				{
					"handle": "mauna",
					"gift_description": "An elegant choice for a person who likes incense with warmth: sandalwood softens precious resins and tonka, while ginger and honeyed facets keep the composition luminous and easy to gift."
				},
				{
					"handle": "moko",
					"gift_description": "Ideal for a nature-loving recipient, this uses sandalwood to anchor green fig leaf, raspberry, and lily-of-the-valley, so the wet-earth freshness feels fuller and more lasting."
				},
				{
					"handle": "pg-111-indian-wood",
					"gift_description": "This is the thoughtful gift for someone who appreciates contrast, because sandalwood settles peppermint, cardamom, and nutmeg into a cool-then-warm woody trail with vetiver and oakmoss."
				},
				{
					"handle": "smokin-gun",
					"gift_description": "Best for a recipient who likes smoky scents that still feel approachable: sandalwood tempers peated whisky, juniper, and incense, while cedar and oak add a campfire-like depth."
				},
				{
					"handle": "montabaco",
					"gift_description": "A strong choice for someone drawn to textured woods with lift; sandalwood softens tobacco leaf, leather, and black tea, while juniper, cardamom, and ambergris keep the gift airy rather than heavy."
				}
			]
		},
		"neroli-orange-blossom-gifts": {
			"title": "Neroli & Orange Blossom Gifts",
			"subtitle": "Luminous neroli and orange blossom compositions",
			"category": "By Olfactive Direction",
			"description": "For the gift-giver seeking a bright, gracious fragrance, these neroli and orange blossom compositions feel like sunlight bottled for every kind of recipient.",
			"products": [
				{
					"handle": "neroli-petitgrain",
					"gift_description": "Choose this for someone who loves a clean, comforting gift with a nostalgic tilt; the mandarin, tender neroli and leafy petitgrain make it feel especially apt for everyday wear. The airy orange blossom and gentle vanilla give it a soft, reassuring finish that sets it apart from sharper citrus options."
				},
				{
					"handle": "tennis-club",
					"gift_description": "This suits the recipient who prefers an elegant, giftable scent with a vacation mood rather than a heavy floral. Green neroli, salty marine air and crisp petitgrain keep it brisk, while the sunlit florals and understated woody musk make it ideal for a summer birthday or a polished daytime present."
				},
				{
					"handle": "hiraeth",
					"gift_description": "Give this to someone who likes their florals with depth and a little soul; it fits the theme through its bright neroli at the center. Jasmine, frankincense and oakmoss add a more reflective character than the other picks, while the sweet amber softens it into something quietly personal."
				},
				{
					"handle": "flower-frost-19-12",
					"gift_description": "A good choice for a gift recipient who enjoys freshness with a cool edge, this belongs here for its citrus-led brightness and floral clarity. The frost-bright ozonic accord, magnolia and lily of the valley make it feel more airy and crystalline than the warmer orange blossom fragrances in the collection."
				},
				{
					"handle": "lacura",
					"gift_description": "This is the one to gift when you want the orange blossom theme to feel lush and generous; it leans into bitter orange, neroli and orange blossom more fully than most. Velvety musks, soft woods and a caramel-vanilla glow give it a tender richness that feels especially special as a celebration present."
				},
				{
					"handle": "pg191-neroli-ad-astra",
					"gift_description": "Pick this for someone drawn to refined, transparent florals with a slightly unusual twist; it fits the collection through its clean neroli profile. Mandarin, green geranium and agave flower give it a distinctive snap, while vetiver and dry powdery spice keep it more restrained than the sweeter options."
				},
				{
					"handle": "seychelles-breeze",
					"gift_description": "A thoughtful gift for someone who likes a breezy, easygoing scent, this matches the theme with its floral heart of orange blossom and airy citrus-green lift. Freshly cut grass, wet greens and ambroxan make it feel more coastal and casual than the more classic neroli blends."
				},
				{
					"handle": "this-is-not-a-blue-bottle-1-6",
					"gift_description": "This is the gift for a romantic or affectionate recipient, since its citrus-floral brightness is softened into something intimate rather than loud. Yuzu, grapefruit and orange blossom give it a sparkling opening, while white musks and vetiver make the finish feel close and gently tactile."
				},
				{
					"handle": "aurantium-jasmine",
					"gift_description": "Choose this for someone who enjoys orange blossom with a fuller floral arc and a little more presence. Neroli, mandarin and dewy lily-of-the-valley lead into jasmine sambac and warm musk, making it one of the most expansive and luminous gifts in the set."
				}
			]
		},
		"cherry-red-fruits": {
			"title": "Cherry & Red Fruits",
			"subtitle": "Juicy cherry and red fruit fragrances",
			"category": "By Olfactive Direction",
			"description": "This collection explores cherry and red fruits at their most vivid: ripe, glossy, and often shadowed by woods, spice, or smoke.",
			"products": [
				{
					"handle": "ziveli",
					"gift_description": "Živeli belongs here because its raspberry and plum-brandy brightness reads like dark red fruit poured into a glass, not just a sweet accent. Honeyed leather, tobacco, and saffron give it a boozy, textured depth that keeps the fruit firmly in the cherry-and-red-fruit family."
				},
				{
					"handle": "chateau-de-velours",
					"gift_description": "Château de Velours fits this theme by layering raspberry, blackcurrant, and wild strawberry into a lush red-fruit core. Davana, red wine, and dark chocolate make the berries feel plush and ripe, while oakmoss adds a drier, earthier contrast."
				},
				{
					"handle": "ruby",
					"gift_description": "Ruby earns its place through a candy-bright fruit profile built around cherry, red berries, and pineapple. Whipped cream, brown sugar, and benzoin turn that fruit into a glossy gourmand, with amber and musk giving it a fuller glow than the average berry scent."
				},
				{
					"handle": "andevalo",
					"gift_description": "Andevalo is a strong match because strawberry, raspberry, and black cherry sit at the center of its composition. Labdanum, leather, and dark woods pull those fruits into a smoky amber register, making the red-fruit note feel dense and shadowed."
				},
				{
					"handle": "tokyo-nights",
					"gift_description": "Tokyo Nights fits the collection through its jammy plum and plum-wine accord, which reads as deep red fruit rather than a generic sweet note. Tobacco, osmanthus, and amber add a soft glow and a faint smokiness that set it apart from brighter fruit scents."
				},
				{
					"handle": "juice-balanced",
					"gift_description": "Juice belongs here for its vivid raspberry and strawberry heart, sharpened by tart rhubarb. Bulgarian rose, cyclamen, and amberwood give the fruit a crisp, floral lift and a clear outline, keeping the effect fresh rather than syrupy."
				},
				{
					"handle": "jany",
					"gift_description": "Jany fits the theme by translating ripe fruit into a baked, red-tinged dessert mood, with apple tart and apricot leading the sweetness. Cinnamon, caramel, and buttery pastry make it feel fruit-forward in a warm, cooked way, distinct from the juicier picks here."
				},
				{
					"handle": "fraisi",
					"gift_description": "Fraisi earns inclusion because strawberry and red berries are its opening statement, then stay visible through the vanilla-almond creaminess. Thai oud adds an herbal, woody twist that keeps the fruit from becoming too neat or predictable."
				},
				{
					"handle": "indecent-cherry",
					"gift_description": "INDECENT CHERRY is the clearest cherry-focused scent in the set, built around sweet-and-sour cherry at the center. Almond, rose, and mimosa give it a tart floral sheen, while vanilla, amber, and benzoin deepen the fruit into a richer red-fruit trail."
				}
			]
		},
		"tea-scents": {
			"title": "Tea Scents",
			"subtitle": "Elegant tea-inspired compositions",
			"category": "By Olfactive Direction",
			"description": "A tea collection drawn from steam, leaf, and ritual, where each composition teases a different facet of the cup: vegetal, smoky, milky, or bright.",
			"products": [
				{
					"handle": "matcha-soft-serve",
					"gift_description": "This belongs here because it treats tea as something tactile and edible, letting matcha’s grassy umami meet green tea, milk, and vanilla. The waffle-cone accord gives the theme a Japanese dessert angle that makes the tea feel softly gourmand rather than purely herbal."
				},
				{
					"handle": "rainy-city",
					"gift_description": "It fits the collection by capturing tea in motion: black tea’s warmth, green tea’s clarity, and the misty atmosphere that makes the note feel lived-in. Vetiver, transparent woods, and an Earl Grey steaminess keep it firmly in the tea lane while giving it an urban, rain-washed character."
				},
				{
					"handle": "osmanthus-oolong",
					"gift_description": "This earns its place by pairing tea with floral fruit in a way that still feels brewed, not perfumed. Osmanthus, oolong, and guaiac wood create a smooth apricot-and-tea dialogue, while frangipani and orange blossom brighten the composition without breaking its calm."
				},
				{
					"handle": "culot-the",
					"gift_description": "Culot Thé fits because it explores tea from an unexpected angle, using green tea as the frame for a savory, almost mischievous twist. Jasmine, apricot, and the sharp flicker of ginger, garlic, and wasabi make it the most playful interpretation in the set."
				},
				{
					"handle": "tea-rock-n-roll",
					"gift_description": "It belongs in a tea-themed selection because it translates tea-time into texture and attitude, with bergamot tea and smoky maté at the center. The leathery edge and tonka finish push the blend beyond a simple brew, giving the cup a rockier, more textured profile."
				},
				{
					"handle": "leaf-crush",
					"gift_description": "This is here for its raw, leaf-first take on tea, where bruised greenery feels as important as the infusion itself. Bitter green tea, wild thyme, and yuzu are sharpened by neroli, then grounded by honey, oakmoss, and patchouli for a vivid botanical reading."
				},
				{
					"handle": "beso-pasion",
					"gift_description": "It fits the theme by showing tea as part of a sunlit landscape rather than a formal drink, with green foliage and fig woven through the composition. Citrus and ginger add brightness, while amber and musk give the tea accord a warm, coastal drift."
				},
				{
					"handle": "chaco",
					"gift_description": "Chaco earns its place through its polished green-tea structure, where bergamot, petitgrain, and maté create a brisk, cologne-like opening. Cedar and guaiac wood add a salty-smoky depth that keeps the tea accord crisp but never thin."
				},
				{
					"handle": "moonmilk",
					"gift_description": "This closes the collection with tea in its most intimate register: black tea, lime, and black pepper wrapped in milky sandalwood. The powdery woods and soft leather impression make the brew feel hushed and close to the skin, rather than sweet or airy."
				}
			]
		},
		"incense-resin": {
			"title": "Incense & Resin",
			"subtitle": "Sacred, smoky, resinous compositions",
			"category": "By Olfactive Direction",
			"description": "A procession of smoke, sap, and sanctified darkness, where resins glow, smolder, and leave a hushed trail of ritual.",
			"products": [
				{
					"handle": "osang",
					"gift_description": "Osang belongs here for its fierce myrrh core and thick incense smoke, which give the composition a ritualistic gravity. Fenugreek, molten honey, and ashen iris add a feral sweetness and a charred floral edge that keep it from settling into simple amber warmth."
				},
				{
					"handle": "liquid-incense",
					"gift_description": "Liquid Incense fits the collection by capturing frankincense in its bright, pre-combustion state, as if the resin is still catching the first light. Bergamot, mineral warmth, and dry woods keep the scent lucid, while subtly animalic oud gives the incense a deeper shadow."
				},
				{
					"handle": "sand",
					"gift_description": "Sand earns its place by turning sandalwood into a resinous, smoke-touched study rather than a polished wood. Banana nuance, cardamom, and benzoin make the familiar material feel off-kilter and alive, with incense threading through the composition like a faint temple haze."
				},
				{
					"handle": "sunken-tobacco",
					"gift_description": "Sunken Tobacco suits this theme through its salt-worn tobacco and smoky resins, which evoke something weathered by time and air. Juniper, earthy leaves, and dry woods add a savory green bite that makes the warmth feel stern and sea-dark rather than sweet."
				},
				{
					"handle": "berbera",
					"gift_description": "Berbera is a natural fit because it builds its whole structure from frankincense, myrrh, and opoponax, the classic materials of smoke and sacred resin. Pepper, cola-like resin, and leathery old-wood nuances sharpen the profile into something charred, forceful, and unmistakably incense-led."
				},
				{
					"handle": "volcano",
					"gift_description": "Volcano belongs in this collection for the way its rose is carried on a plume of incense and warm resins, as if floral light were rising through smoke. Ginger, nutmeg, and dark woods add heat and depth, giving the fragrance a molten, eruptive character."
				},
				{
					"handle": "woodoo",
					"gift_description": "Woodoo fits the theme with its green incense and balsamic resins, which place the scent squarely in smoky, wooded territory. Peppery ginger, cypriol, and powdery iris create a shadowed contrast, while musk lends a quiet animalic hum beneath the cedar."
				},
				{
					"handle": "moonlight-ceremony",
					"gift_description": "Moonlight Ceremony is made for this collection: birch tar, incense, and smoked black tea create a nocturnal ritual atmosphere from the first spray. Leather, guaiac wood, and saffron add a dry, ember-like tension, while amber and labdanum round the edges without dispelling the smoke."
				},
				{
					"handle": "bois-dascse",
					"gift_description": "Bois d’Ascese belongs here because frankincense, tobacco, and charred timber form its ascetic center. The peaty whisky accord, oak, and dry woods give it a stern, contemplative depth that feels more like embers in a chapel than a plush amber."
				}
			]
		},
		"vanilla-beyond-vanilla": {
			"title": "Vanilla Beyond Vanilla",
			"subtitle": "Complex, layered vanilla compositions",
			"category": "By Olfactive Direction",
			"description": "Vanilla here is never plain: it is browned, salted, smoked, boozy, and folded into textures that feel baked, milky, or shadowed.",
			"products": [
				{
					"handle": "anti-blues",
					"gift_description": "This belongs because it treats vanilla as a dark gourmand spine rather than a simple sweetness, letting smoky incense, tobacco, and saffron push it into shadow. The chocolate, leathery vetiver, and resinous warmth make it a study in vanilla with bite."
				},
				{
					"handle": "mooncake",
					"gift_description": "It fits the theme by showing vanilla as memory and pastry at once, wrapped around salted egg yolk, honey, and cake crumbs. That golden crust note and softly sweet edible core give the vanilla a nostalgic, layered character."
				},
				{
					"handle": "vanilla-for-breakfast",
					"gift_description": "This is a textbook example of vanilla expanded into architecture: four vanilla materials are bolstered by milky almond, creamy benzoin, and roasted tonka. Those toasted, lactonic, and resinous facets make it more than dessert—it is vanilla in full volume."
				},
				{
					"handle": "bake",
					"gift_description": "Bake earns its place by pairing vanilla with citrus lift, so the sweetness never sits still. Lemon zest, airy cupcake crumb, and praline add a bright, buttery contrast that keeps the composition playful rather than flat."
				},
				{
					"handle": "far",
					"gift_description": "This selection needs Far because it proves vanilla can feel airy and brisk, not just rich. Yuzu, lime, fluffy cream, and a biscuit-like butteriness turn the vanilla into a citrus dessert with a clean, buoyant finish."
				},
				{
					"handle": "god-bless-cola",
					"gift_description": "God Bless Cola fits by taking vanilla into fizzy, snacky territory instead of classic creaminess. The cola sparkle, caramel popcorn, and metallic-can nuance give it a playful, slightly savory twist that separates it from the more pastry-led vanillas here."
				},
				{
					"handle": "vanille-creme",
					"gift_description": "Vanille Creme belongs for its custard-like take on the note, where vanilla is thickened by bergamot, caramel, and buttery pastry facets. The sugar, musk, and creamy texture make it feel like a spoonable vanilla rather than a sheer accord."
				},
				{
					"handle": "celestial-object",
					"gift_description": "It suits this collection because it makes vanilla intimate and weightless at the same time, cushioned by sugared almond, milky warmth, and rice-paper popcorn nuance. A pinch of salt and warm tonka keep the sweetness from becoming one-dimensional."
				},
				{
					"handle": "dark-honey",
					"gift_description": "Dark Honey closes the theme by pushing vanilla into boozy, animalic territory, where dark vanilla is drenched in rum, plum liqueur, and baked peach. Cinnamon and patchouli deepen the density, while the feral honey note gives the whole composition a wilder edge."
				}
			]
		},
		"fig-green-herbaceous": {
			"title": "Fig, Green & Herbaceous",
			"subtitle": "Green, leafy, herbal compositions",
			"category": "By Olfactive Direction",
			"description": "A study in leaf and stem, where fig, herbs and green aromatics sketch landscapes that feel freshly cut, sunlit and alive.",
			"products": [
				{
					"handle": "matcha-mist",
					"gift_description": "Brume Matcha belongs here for its tea-like greenness: roasted matcha, basil and peppermint make the composition feel clipped from a herb garden rather than a dessert tray. Cedarwood and geranium keep it cool, dry and quietly contemplative."
				},
				{
					"handle": "a-grove-by-the-sea",
					"gift_description": "A Grove By The Sea earns its place through the meeting of fig leaf, rosemary and conifers, which gives the scent a coastal green character with savory lift. Sea salt, mineral clay and olive nuance make this more like a windswept grove than a sweet fig."
				},
				{
					"handle": "play-green",
					"gift_description": "Play Green fits the theme with its brisk mint, basil and juniper core, a trio that reads sharply leafy and aromatic. Lime and lentisk sharpen the edges, while vetiver adds an earthy finish that keeps the whole composition vivid and direct."
				},
				{
					"handle": "you-or-someone-like-you",
					"gift_description": "You or Someone Like You is a perfect match for this collection because its transparency comes from mint, spearmint and shiso, creating a cool vegetal brightness. Eucalyptus and violet leaf add a crisp, slightly bitter green tone that feels modern and spare."
				},
				{
					"handle": "in-this-world",
					"gift_description": "In This World belongs here for the way fig is rendered as airy and botanical, not ripe or jammy, with lavender, sage and violet lending a garden-fresh clarity. Blood orange and tonka wood give it a soft glow and a hay-like warmth that stays close to the skin."
				},
				{
					"handle": "fig-lemonade",
					"gift_description": "Fig Lemonade fits the theme by turning fig leaf into something sparkling and tart, with Italian lemon and bergamot giving the green notes a fizzy lift. Buchu adds a blackcurrant-like herbal twist, and cedarwood keeps the sweetness from drifting away."
				},
				{
					"handle": "hakka-japanese-mint",
					"gift_description": "Hakka (Japanese Mint) suits this collection because Japanese mint drives the scent’s clean, leafy chill, placing it squarely in aromatic green territory. Jasmine, lily and lotus soften the edges, while cedarwood and white musk give the freshness a calm frame."
				},
				{
					"handle": "masa",
					"gift_description": "Masa works in this set by pairing bright citrus with violet leaf, which introduces a green, slightly leathery nuance beneath the sparkle. Ginger, cardamom and amber add warmth and body, making this the most polished, structured green in the group."
				},
				{
					"handle": "provence",
					"gift_description": "Provence belongs in the collection for its classic herb bouquet: lavender, clary sage, hyssop and spearmint create a vivid aromatic landscape. Pepper and warm woods round it out, giving the composition a sun-baked depth that still feels herbaceous and clear."
				}
			]
		},
		"salt-sea-mineral": {
			"title": "Salt, Sea & Mineral",
			"subtitle": "Marine, mineral, salty compositions",
			"category": "By Olfactive Direction",
			"description": "A shoreline of salt, stone, and spray: these scents trace the mineral edge where ocean air meets skin.",
			"products": [
				{
					"handle": "entre-ciel-et-mer",
					"gift_description": "It belongs here for its icy algae-and-salt profile, which reads like open water made tangible. Lavender, pear, and white thyme add a brisk aromatic lift, while ambergris and dry cedar give it that faintly animalic, sea-spray finish."
				},
				{
					"handle": "falaise-49-5-n",
					"gift_description": "This fits the collection by pairing tart fruit with the grit of a cliffside coast, making the salt feel weathered rather than airy. Wild blackberry, sea spray, and sun-warmed pebbles are sharpened by vetiver and mineral musk for a vivid, windswept effect."
				},
				{
					"handle": "sous-le-pont-mirabeau",
					"gift_description": "Its place in the theme comes from translating the Seine into a river-mineral accord rather than a conventional aquatic. Fig, violet leaf, and airy water notes are grounded by cedar, sandalwood, and a trace of incense, giving it a misty urban shoreline mood."
				},
				{
					"handle": "beso-de-sal",
					"gift_description": "A natural fit for Salt, Sea & Mineral because the saline accord is framed as landscape: pink salt flats, driftwood, and island air. Lemon, mandarin, and shiso keep it bright and zesty, while sage and cedarwood make the freshness feel dry and mineral."
				},
				{
					"handle": "breathe-out-01-02",
					"gift_description": "This fragrance earns its spot by fusing cold altitude with marine steam, so the salt reads almost like frozen air. Elemi, incense, and sandalwood create a resinous, stony structure, while frangipani adds a softer tropical flash."
				},
				{
					"handle": "sydney-rock-pool",
					"gift_description": "It fits the theme through the sensation of salt-warmed skin and sandstone after a swim, not just through marine notes. Coconut, driftwood, and sandalwood give it a sunlit woody core, while sea salt and narcissus add a clean coastal glow."
				},
				{
					"handle": "sal-y-limon",
					"gift_description": "This one is here because it turns citrus into a saline shoreline, making the mineral facet as important as the brightness. Lemon, lime, and bergamot fizz over sea salt, then orange blossom, musk, and patchouli keep the finish dry and breezy."
				},
				{
					"handle": "803",
					"gift_description": "A strong match for the collection, since it captures patchouli in a frosted coastal register rather than an earthy one. Ginger, pink pepper, and juniper snap against marine mineral notes, while the patchouli leaf underneath gives the scent its dark, woody counterpoint."
				},
				{
					"handle": "playground-1",
					"gift_description": "It suits this theme by rendering marine freshness with an almost glassy clarity, like cold air over water. Honeydew melon, bergamot, and sea-salt accord stay luminous, while jasmine, lily-of-the-valley, cashmere wood, and musk soften the edges."
				}
			]
		},
		"spring-gifting": {
			"title": "Spring Gifting",
			"subtitle": "Light florals and fresh green compositions for spring",
			"category": "By Season",
			"description": "For a spring gift, choose fragrances that feel like first leaves, open windows, and flowers just waking in the light.",
			"products": [
				{
					"handle": "seychelles-breeze",
					"gift_description": "Seychelles Breeze suits a spring gift for someone who loves effortless freshness, because its cut grass and wet greens make the floral heart feel newly picked rather than sweet. Freesia, orange blossom, and airy musk keep it bright, coastal, and easy to wear for daytime celebrations."
				},
				{
					"handle": "dancing-light",
					"gift_description": "Dancing Light is ideal when you want a gift with a cooler, more modern spring mood, as icy mint and Siberian pine needles give the florals a brisk lift. Jasmine, neroli, and soft sandalwood make it especially fitting for someone who prefers crisp elegance over sugary bloom."
				},
				{
					"handle": "muguet-fleuri",
					"gift_description": "Muguet Fleuri belongs in this collection because lily-of-the-valley is the very emblem of spring gifting, here made more vivid by violet leaves and wild grasses. The mossy greenery and creamy drydown give it a tender, intimate feel for a close friend or romantic recipient."
				},
				{
					"handle": "bamboo",
					"gift_description": "Bamboo works beautifully as a spring present for someone drawn to calm, natural scents, since its green leaves and lily-of-the-valley feel like a walk through fresh growth after rain. Jasmine, geranium, and vetiver add a clean, earthy balance that keeps it from reading too delicate."
				},
				{
					"handle": "matcha-soft-serve",
					"gift_description": "Matcha Soft Serve is a thoughtful gift for the person who likes freshness with a playful twist, because its green tea and matcha core still feels airy while the milk and vanilla soften it. The waffle-cone accord adds a distinctive, cheerful gourmand note that makes it stand apart from the floral picks here."
				},
				{
					"handle": "neon-night",
					"gift_description": "Neon Night fits this spring gifting theme for someone who wants brightness with a little edge, as bergamot and mandarin keep the opening sparkling and easygoing. Green tea, peony, and white flowers give it a clean floral profile, while guaiac wood adds a smooth, modern finish."
				},
				{
					"handle": "juli",
					"gift_description": "Juli is a lovely choice when you want a gift that feels generous and pretty without being overly formal, since pear and cassis blossom make the rose seem juicy and alive. Violet leaf and spearmint bring a fresh green snap, and the sandalwood-iris base leaves a soft skin-close trail."
				},
				{
					"handle": "shaft-of-light",
					"gift_description": "Shaft of Light (Sunbeams) suits a spring gift for someone who enjoys gentle, optimistic scents, because lemon and grapefruit brighten the creamy magnolia and gardenia without overwhelming them. Oakmoss, patchouli, and sandalwood give it a wooded depth that makes the floral freshness feel grounded and serene."
				},
				{
					"handle": "flower-frost-19-12",
					"gift_description": "Flower Frost 19.12 is a strong pick for a recipient who likes spring florals with a cool, airy twist, since its citrus and green notes feel like the season’s first chill morning. Magnolia, jasmine, and lily of the valley soften that frostiness, while musk and cedar add a quietly warm close."
				}
			]
		},
		"summer-in-a-bottle": {
			"title": "Summer in a Bottle",
			"subtitle": "Sun-drenched, holiday-ready scents",
			"category": "By Season",
			"description": "A sunlit shoreline in scent: citrus, salt, fruit and skin-soft warmth gathered like a summer notebook from the coast.",
			"products": [
				{
					"handle": "sal-y-limon",
					"gift_description": "Sal y Limón belongs here because it nails the collection’s bright, seaside opening: fizzy lemon, lime and bergamot are sharpened by a crisp sea-salt accord. Orange blossom and a musky-patchouli base keep the sparkle feeling like Mediterranean air, not candy."
				},
				{
					"handle": "32-c",
					"gift_description": "32°C fits this theme by translating heat itself into perfume, with jasmine sambac, tiare and coconut wrapped in milky sandalwood and vanilla. The white tea and ginger add a breezy lift, making it feel like sun-warmed skin after a dip rather than a heavy tropical blend."
				},
				{
					"handle": "a-grove-by-the-sea",
					"gift_description": "A Grove By The Sea earns its place through its Adriatic greenness: fig leaves, rosemary and conifers sketch a coastal grove, while sea salt keeps the air briny and open. Mineral, clay and olive nuances give it a sun-baked, landscape-like realism that sets it apart."
				},
				{
					"handle": "fiery-fig-24-05",
					"gift_description": "Fiery Fig 24.05 suits the collection because it captures late-day summer with fresh fig leaves, ripe fruit and creamy coconut. Fig milk, bark and cedar give it a warm, leafy depth, so it reads as a glowing Mediterranean dusk instead of a simple fruity scent."
				},
				{
					"handle": "beso-pasion",
					"gift_description": "Beso Pasión fits the theme with its beachside balance of brightness and warmth: citrus and ginger flash at the top, then fig and green foliage bring a breezy Mediterranean feel. Amber, musk and woods turn it into the scent of sunlit grass and fading island light."
				},
				{
					"handle": "kolada",
					"gift_description": "Kolada belongs in this lineup because it turns vacation sweetness into something vivid and modern, with pineapple, coconut milk and warm amber at the center. The metallic lily-of-the-valley and mineral bite keep it from becoming syrupy, giving the composition a sparkling edge."
				},
				{
					"handle": "in-love-with-the-cocos",
					"gift_description": "In Love With the Cocos fits this collection through its soft, skin-close tropicality: lemon and bergamot open into coconut water, creamy jasmine and luminous neroli. Amber, musk and ambrette leave a warm, intimate trail that feels like salt air on bare shoulders."
				},
				{
					"handle": "portamarina",
					"gift_description": "Portamarina suits the theme by evoking a coastal morning, where citrus, apple and sage meet a sea-breeze accord. Iris adds a powdery softness, while the overall mineral clarity makes it feel like bright summer light on stone."
				},
				{
					"handle": "sous-le-pont-mirabeau",
					"gift_description": "Sous Le Pont Mirabeau belongs here because it offers a cooler, more reflective side of summer, with fig, violet leaf and aquatic notes flowing through cedar and sandalwood. Soft incense and musks give it a river-washed depth that still feels airy and seasonal."
				}
			]
		},
		"sunshine-gifts": {
			"title": "Sunshine Gifts",
			"subtitle": "Sun-kissed, radiant, warm-weather fragrances",
			"category": "By Season",
			"description": "A gift collection that bottles the feeling of a warm day well spent: bright, breezy scents with just enough glow to make someone feel thought of.",
			"products": [
				{
					"handle": "still-life-in-rio",
					"gift_description": "Choose this for the friend who loves a holiday mood with a little sparkle, since the yuzu, mandarin and ginger make it feel instantly sunlit and gift-ready. The coconut, pepper and rum give it a tropical twist that sets it apart from simpler citrus picks."
				},
				{
					"handle": "aurantium-jasmine",
					"gift_description": "This suits a recipient who enjoys florals that feel radiant rather than sugary, making it a lovely present for spring birthdays or a daytime celebration. Orange blossom, neroli and lily-of-the-valley keep it vivid, while jasmine sambac, musk and guaiacwood add a graceful depth."
				},
				{
					"handle": "divina-flora",
					"gift_description": "Pick this when you want a gift that feels elevated and a bit artistic, especially for someone who likes flowers with a modern edge. Lemongrass, passion fruit and peony give it brightness, while coniferous greens, sandalwood and vanilla make it more textured than a typical floral."
				},
				{
					"handle": "tennis-club",
					"gift_description": "This is the one for someone who wants a fresh, sporty fragrance gift with a holiday-in-Europe feel. Neroli, petitgrain and marine salt make it crisp and breezy, and the citrus, floral accents and woody musk keep it grounded and easy to wear."
				},
				{
					"handle": "elisa",
					"gift_description": "A thoughtful choice for a recipient who likes comfort in a bottle, especially if you want something friendly and easy to give. Candied bergamot, yuzu and orange blossom create a soft glow, while davana, sandalwood and benzoin add a cozy, skin-close finish."
				},
				{
					"handle": "firenze",
					"gift_description": "Gift this to someone who loves classic citrus with a cultured twist, ideal for a summer trip, graduation or a polished everyday wear. Bergamot, bitter orange and lemon keep it bright, while sandalwood and vetiver bring a drier, more elegant backbone."
				},
				{
					"handle": "sun-bleached",
					"gift_description": "This fits a gift recipient who prefers clean, sunlit fragrances that feel effortless rather than loud. Citrus and ginger give it lift, ylang-ylang, jasmine and magnolia soften the edges, and white musk with Ambroxan leave that fresh-laundry impression."
				},
				{
					"handle": "neroli-petitgrain",
					"gift_description": "Choose this for someone who finds comfort in crisp citrus and green notes, especially if you want a budget-friendly gift that still feels special. Mandarin, neroli and petitgrain lead the way, while orange blossom and vanilla add a gentle warmth that feels familiar."
				},
				{
					"handle": "in-love-with-the-cocos",
					"gift_description": "This is the best pick for a gift aimed at someone who likes tropical scents that stay soft and intimate instead of beachy and loud. Lemon, bergamot and coconut water brighten it, while jasmine, orange blossom, amber and musk give it a warm, close-to-skin finish."
				}
			]
		},
		"autumn-mood": {
			"title": "Autumn Mood",
			"subtitle": "Warm spices, amber and golden-hour compositions",
			"category": "By Season",
			"description": "A curation for the hour when spice glows, amber deepens, and the air turns honeyed at the edge of dusk.",
			"products": [
				{
					"handle": "this-is-not-a-blue-bottle-1-4",
					"gift_description": "This fits Autumn Mood because davana, opoponax and benzoin make its amber feel like late light caught in velvet. Cardamom and labdanum add spice and resin, while tonka gives the composition a soft, powdery warmth."
				},
				{
					"handle": "tonkade",
					"gift_description": "It belongs here for the way vanilla and tonka turn gourmand comfort into something duskier, with incense and dry cedar keeping it in autumn territory. Candied fruit, Cashmeran and patchouli create a milky sweetness edged by shadow."
				},
				{
					"handle": "ziveli",
					"gift_description": "Živeli suits this theme through plum brandy, saffron and tobacco, a trio that feels like a fireside toast after sunset. Raspberry and honeyed leather add richness, while tonka and labdanum anchor the scent in warm ambered depth."
				},
				{
					"handle": "drunk-lovers",
					"gift_description": "This is a perfect Autumn Mood pick because cognac, cinnamon and amber give it the glow of a room lit from within. Red berries and juniper keep it lively, while vanilla and resinous woods push it into a darker, spiced warmth."
				},
				{
					"handle": "montabaco",
					"gift_description": "Montabaco fits the collection by pairing tobacco leaf and cardamom with tea and leather, evoking crisp air warmed by a golden interior. Juniper, sandalwood and ambergris lend it a breezy dryness that sets it apart from heavier autumn scents."
				},
				{
					"handle": "patchouli-cannelle",
					"gift_description": "Patchouli Cannelle earns its place through cinnamon, clove and patchouli, which create a classic fall-spice core with earthy weight. Frankincense, myrrh and vanilla soften the edges, while ambergris gives the whole blend a hushed, woolly finish."
				},
				{
					"handle": "vanille-mafonja",
					"gift_description": "Vanille Mafonja belongs in Autumn Mood because its vanilla is not sugary but smoldering, lifted by cinnamon, pink pepper and nutmeg. Myrrh, incense and dark amber add a resinous glow, while ylang-ylang brings a creamy contrast."
				},
				{
					"handle": "le-mat",
					"gift_description": "Le Mat fits this theme with rose, immortelle and dark spices that feel windswept yet warmly lit, like autumn turning strange at twilight. Patchouli and balsamic sweetness give it earthy depth, while the chypre structure keeps it tense and vivid."
				},
				{
					"handle": "wolf-extrait",
					"gift_description": "Wolf Extrait suits Autumn Mood by turning pine, moss and incense into a forest scene warmed by amber and leather. Spiced plum and cardamom flash through the darkness, giving the scent a brisk, resin-rich autumn character."
				}
			]
		},
		"winter-warmers": {
			"title": "Winter Warmers",
			"subtitle": "Deep, warm, comforting winter fragrances",
			"category": "By Season",
			"description": "A cabinet of winter embers: dark woods, smoldering resins, and edible warmth gathered for the coldest hours.",
			"products": [
				{
					"handle": "honey-and-tar",
					"gift_description": "Honey and Tar belongs here because it captures winter’s most tactile comfort: sticky honey, birch tar smoke, and worn leather. Vanilla, resinous incense, and dark woods keep the sweetness grounded and heat-soaked."
				},
				{
					"handle": "lu-bu",
					"gift_description": "Lü Bu fits the collection by turning winter into a tense, glowing haze of clove, smouldering paper, and benzoin. Metallic gunpowder, powdery iris, and dark woods give it a cold-spark contrast that still feels warm at the core."
				},
				{
					"handle": "skin-fetish",
					"gift_description": "Skin Fetish earns its place through the contrast of dark cherry, incense, and rose wrapped in oud. Amber, leather, and woods make the sweetness feel shadowed and heat-burnished rather than bright."
				},
				{
					"handle": "milk-bold",
					"gift_description": "Milk | Bold is winter comfort made fuller and darker, with marshmallow and tonka melting into smoky firewood. Amber, mahogany, and its room-filling woody sweetness make it feel like a hearth fragrance with extra heft."
				},
				{
					"handle": "rasputin",
					"gift_description": "Rasputin closes the set with a dense winter spine of cardamom, clove, cinnamon, and smoky incense. Dark chocolate, Indian oud, leather, and tonka make it the most spiced and brooding of the collection."
				},
				{
					"handle": "veles",
					"gift_description": "Veles suits this theme because its smoky oud and tarred leather evoke frozen earth and fire at once. Rhubarb, saffron, and labdanum keep the darkness from flattening, adding a tart, resinous lift."
				},
				{
					"handle": "23-59",
					"gift_description": "23:59 fits Winter Warmers as a late-night gourmand built from cognac, espresso, and crème brûlée. Benzoin, vanilla, and guaiac wood give it toasted depth, making the sweetness feel steamy and dimly lit."
				},
				{
					"handle": "nebula-viridis",
					"gift_description": "Nebula Viridis belongs in the collection because its citrus opening is quickly folded into saffron, smoky woods, and amber. Sandalwood, benzoin, and leather round the brightness into a golden warmth that suits cold weather beautifully."
				},
				{
					"handle": "ember-shaan",
					"gift_description": "Ember Shaan fits the theme by pairing coffee and chocolate with rose, oud, and sandalwood for a firelit richness. Incense, tobacco, and a gunpowder accord add a charred, ember-like edge that makes it distinctly wintery."
				}
			]
		},
		"festive-scents": {
			"title": "Festive Scents",
			"subtitle": "Spiced, warm, celebratory fragrances",
			"category": "By Season",
			"description": "A candlelit spread of spice, resin and sweet heat, these scents gather the glow of celebration without losing their shadowed edge.",
			"products": [
				{
					"handle": "patchouli-cannelle",
					"gift_description": "Patchouli Cannelle belongs here because its cinnamon-clove spine gives patchouli a festive flicker, while rose and frankincense add a ceremonial lift. Ambergris, musk and vanilla round the woods into a soft, hearthlike trail."
				},
				{
					"handle": "vanille-mafonja",
					"gift_description": "Vanille Mafonja fits the theme by turning vanilla into something warmly spiced and resinous rather than simple or sugary. Saline brightness, pink pepper and nutmeg meet myrrh and incense, giving it a salted, embered depth."
				},
				{
					"handle": "lu-bu",
					"gift_description": "Lü Bu earns its place through the contrast of clove and gunpowder with smouldering paper, a pairing that feels both celebratory and tense. Iris, benzoin and dark woods soften the smoke, while vanilla adds a muted glow."
				},
				{
					"handle": "or-du-srail",
					"gift_description": "Or du Sérail suits this collection because its rum-soaked tobacco and honeyed fruit read like a lavish, spiced banquet. Mango, candied notes and amber create a rich sweetness, while the woods keep it from becoming too smooth."
				},
				{
					"handle": "tiger-rouge",
					"gift_description": "Tiger Rouge fits the theme with its pepper, paprika and clove heat, sharpened by blood orange and lemon myrtle for a vivid, festive spark. Oud, incense and leather give the composition a fiercer, more nocturnal character than the sweeter picks here."
				},
				{
					"handle": "fat-cola",
					"gift_description": "Fat Cola is a natural match for this theme because cola, gingerbread and nutmeg instantly evoke spiced warmth with a fizzy, nostalgic twist. Brown sugar, marshmallow and patchouli deepen the sweetness, while vanilla smooths the edges."
				},
				{
					"handle": "ladano",
					"gift_description": "Ladano belongs in this lineup because labdanum, cinnamon leaf and licorice create a resinous warmth that feels like spice held in amber. Immortelle, vanilla and a faint animalic hum add texture and a dry, smoky comfort."
				},
				{
					"handle": "meek-passion",
					"gift_description": "Meek Passion works here by taking a bright, celebratory fruit burst and grounding it in cinnamon, patchouli and vetiver. Banana and pineapple give it a playful sweetness, but the woody base keeps the mood dusky rather than frivolous."
				},
				{
					"handle": "sugar-addict",
					"gift_description": "SUGAR ADDICT fits the collection through its blend of cinnamon, rum and vanilla with cocoa and coffee, a combination that turns sweetness into something deeply spiced. Cashmere wood adds a plush finish, making the gourmand feel rich and heated."
				}
			]
		},
		"discovery-sets-to-gift": {
			"title": "Discovery Sets to Gift",
			"subtitle": "Curated sets for fragrance exploration",
			"category": "Gift Ideas",
			"description": "When you want the gift to feel thoughtful, discovery sets let them wander before they choose a signature.",
			"products": [
				{
					"handle": "dark-drunk-discovery-kit",
					"gift_description": "Choose this for the recipient who likes their gifting with a daring edge: it suits someone who enjoys surprise, experimentation, and a playful unboxing moment. As a discovery set, it’s ideal when you want to give range rather than guess one perfect scent."
				},
				{
					"handle": "j-scent-discovery-set",
					"gift_description": "This is the more airy, garden-bright gift in the lineup, making it a lovely pick for someone who appreciates freshness and subtle charm. The green grass, peppermint, and yuzu give it a crisp lift, while geranium, violet, and vanilla keep the set varied enough for a curious nose."
				},
				{
					"handle": "oddity-discovery-set",
					"gift_description": "Gift this to someone who leans modern and enjoys scents that feel a little offbeat, especially if you’re shopping for a birthday or a fragrance-first occasion. The blackcurrant, mandarin, and yuzu bring a vivid opening, while ambroxan, orris, and vetiver give the set a dry, textured finish that sets it apart."
				}
			]
		},
		"sample-packs-for-beginners": {
			"title": "Sample Packs for Beginners",
			"subtitle": "Themed sample sets to explore fragrance",
			"category": "By Function",
			"description": "A gentle starting point for tracing fragrance families, these sample packs map the shift from easy comfort to stranger, greener terrain.",
			"products": [
				{
					"handle": "2025-bestsellers-for-her",
					"gift_description": "This fits a beginner’s sampler because it offers an immediately readable floral-woody profile: milky sweetness, peachy fruit and soft powder make the structure easy to grasp. Amber, vanilla and jasmine give it a warm, skin-close finish that shows how familiar notes can be layered into a plush whole."
				},
				{
					"handle": "sample-set-1",
					"gift_description": "This belongs here because it opens the door to a more atmospheric style of perfumery, letting a newcomer compare herbs, woods and smoke in one set. Angelica root, chamomile and green leaves keep it crisp, while incense, pine, vetiver and violet add a dry, forested edge that feels distinctly exploratory."
				}
			]
		},
		"full-bottles-worth-giving": {
			"title": "Full Bottles Worth Giving",
			"subtitle": "Commitment-worthy full bottles to wear every day, all the way down",
			"category": "Gift Ideas",
			"description": "These are the bottles that earn their place on the shelf: scents with enough character, balance, and depth to be worn to the last drop.",
			"products": [
				{
					"handle": "you-or-someone-like-you",
					"gift_description": "This belongs because its green clarity feels like a full bottle you can live in day after day, not a fleeting novelty. Mint, spearmint, and shiso give it a crisp bite, while eucalyptus and violet leaf keep the finish clean and quietly strange."
				},
				{
					"handle": "cashmere-beige",
					"gift_description": "A whole bottle makes sense here because its airy floral structure is built for repeat wear, with just enough nuance to stay interesting. Mandarin, cool orris, and violet are lifted by cedar, tonka, and musk, giving it a soft-focus elegance rather than a thin spray-on effect."
				},
				{
					"handle": "honey-and-tar",
					"gift_description": "This fits the theme by turning its bold contrast into something worth committing to for the long haul. Honey, birch tar, and smoky leather make the core distinctive, while vanilla and incense round the roughness into a warm, wearable depth."
				},
				{
					"handle": "iris-pallida",
					"gift_description": "It earns a place in this collection because the iris is substantial enough to justify a serious bottle, not just a dab. Hazelnut, creamy sandalwood, and amber soften the powdery orris, while patchouli and vetiver add a grounded, refined backbone."
				},
				{
					"handle": "mauna",
					"gift_description": "This is a full-bottle fragrance by nature: its quiet power comes from layers that reveal themselves over hours, not minutes. Honeyed sandalwood, incense, and resins give it a meditative core, while ginger and tonka keep the sweetness from going flat."
				},
				{
					"handle": "carine",
					"gift_description": "It fits because this is the kind of composition that rewards steady wear, with a confident presence that feels complete in full size. Jasmine, gardenia, and rose are steered by dark patchouli and vetiver, creating a floral with vintage drama and a cool, tailored finish."
				},
				{
					"handle": "tonkade",
					"gift_description": "A bottle-sized commitment suits it because the sweetness is balanced by enough structure to keep it from becoming a one-note comfort scent. Vanilla, tonka, and candied-fruit facets are sharpened by cardamom, incense, cedar, and patchouli, making the warmth feel dimensional."
				},
				{
					"handle": "20359",
					"gift_description": "This belongs here because its sweet-dark profile has the kind of personality that justifies owning the whole thing. Maraschino cherry, clove, and heliotrope give it a plush, nocturnal twist, while latex, tobacco, and leather push it into a more restless, memorable register."
				},
				{
					"handle": "agarwood",
					"gift_description": "A full bottle feels right for this one because its temple-smoke character has real everyday depth, not just ceremonial mood. Orange peel, cinnamon, and incense brighten the opening, while cedarwood, vetiver, and patchouli keep the oud impression clean and restrained."
				}
			]
		},
		"small-gifts-big-personality": {
			"title": "Small Gifts, Big Personality",
			"subtitle": "Compact-format gifts with outsized character",
			"category": "Gift Ideas",
			"description": "Small in size, but never shy: these gifts carry vivid signatures, from quiet teas to smoke, spice, and sunlit fruit.",
			"products": [
				{
					"handle": "luca-turin-loves-sample-pack",
					"gift_description": "This is the right compact gift when you want to give a fragrance lover range without making the present feel generic; each vial has its own point of view, from smoky woods to austere iris and sculpted florals. It suits a curious recipient or a birthday where you want the surprise to feel artful, not routine."
				},
				{
					"handle": "matcha-mist",
					"gift_description": "Choose this for someone who appreciates a calm, modern scent with personality in a small format; the matcha bitterness, basil brightness, and peppermint-cool lift make it feel considered rather than simple. The cedarwood base keeps it neat and giftable, especially for a friend who likes understated, tea-inspired fragrances."
				},
				{
					"handle": "patchouli-labdanum",
					"gift_description": "This compact cologne is a smart gift for someone who likes depth without heaviness, because the patchouli and labdanum give it smoky amber-wood character while the lighter format keeps it easy to wear. The leather-like warmth, resinous glow, and earthy backbone make it a strong choice for a confident recipient."
				},
				{
					"handle": "sample-set-1",
					"gift_description": "A sample set like this fits the theme perfectly because it turns a small gift into a conversation piece, ideal for the fragrance explorer who enjoys unusual materials and a bit of mystery. Angelica root, incense, and violet give it a cooler, more shadowed profile than the brighter picks in this collection."
				},
				{
					"handle": "marrakech-cologne",
					"gift_description": "Gift this when you want something compact that still feels vivid and grown-up; the orange brightness, mint tea coolness, and black incense smoke give it a distinctive contrast that stands out immediately. It’s especially apt for someone who likes travel-inspired scents with a crisp edge and a darker finish."
				},
				{
					"handle": "god-bless-cola",
					"gift_description": "This is the playful small-format gift for someone with a sweet tooth and a sense of humor, because cola fizz, caramel popcorn, and vanilla make it feel like a tiny treat rather than a standard gourmand. The metallic-can sparkle and toasted nuance keep it from reading too simple, which is exactly why it belongs here."
				},
				{
					"handle": "santorini",
					"gift_description": "Pick this for a recipient who likes their gifts serene but not bland; the green fig, earthy coconut, and milky heart give it a compact softness that still has character. Orris, sandalwood, and white musk add a smooth finish, making it a thoughtful choice for someone who prefers gentle elegance."
				},
				{
					"handle": "202",
					"gift_description": "This belongs in a small-gifts edit because it delivers instant sunshine in a modest bottle, perfect for someone who wants joy without fuss. Mango, watery melon, and grapefruit create a juicy trio that feels lively and easy to wear, while sandalwood gives the gift a little structure."
				},
				{
					"handle": "come-hell-or-high-water-discovery-set",
					"gift_description": "Choose this discovery set for the friend who likes gifts with a darker streak, since gunpowder, leather, and tobacco make it feel bold, compact, and far from ordinary. The guaiac wood and smoke give it a brooding signature that makes a memorable present for someone who enjoys dramatic fragrances."
				}
			]
		},
		"smell-expensive": {
			"title": "Smell Expensive",
			"subtitle": "Fragrances that radiate luxury and quality",
			"category": "Editorial",
			"description": "A study in scents that read as wealth without trying: polished materials, rare textures, and the quiet authority of knowing exactly what to wear.",
			"products": [
				{
					"handle": "rose-shiso",
					"gift_description": "Rose Shiso fits this theme because it makes rose feel edited and expensive, not merely lush: shiso's savory-green snap, iris's cool dusting, and cedar-sandalwood structure signal refined taste. It smells like money and understanding—floral, but with a crisp, tailored edge."
				},
				{
					"handle": "mansa-musa",
					"gift_description": "Mansa Musa belongs here for its unapologetic depth: oud, fermented leather, and amber create a dense, costly aura that feels collected rather than loud. The spice heat and restrained rose give it a dark sheen that reads like money and understanding."
				},
				{
					"handle": "montabaco",
					"gift_description": "Montabaco earns its place through restraint and richness at once: green tobacco leaf, black tea, and supple leather suggest a wardrobe of quality fabrics rather than flash. Juniper, cardamom, and ambergris keep it airy, which is exactly why it smells like money and understanding."
				},
				{
					"handle": "rouge-avignon",
					"gift_description": "Rouge Avignon fits the collection because its rose is rendered in a more cultivated register—raspberry, cocoa, and black truffle turn it into something plush and unmistakably high-end. The smoky amber-musk finish makes it feel like money and understanding with a gothic tilt."
				},
				{
					"handle": "vettii",
					"gift_description": "Vettii suits this theme by moving from luminous fig and quince into saffron, amber, and myrrh, then settling into oud and sandalwood with a polished sense of depth. Tolu balsam and the leathery drydown give it the kind of richness that smells like money and understanding."
				},
				{
					"handle": "high-to-lotus",
					"gift_description": "High to Lotus belongs here because it turns unusual materials into a rarefied statement: artichoke, watery lotus, and seaweed create a green mineral coolness that feels expensive in its eccentricity. Peppermint, anise florals, and oud make it smell like money and understanding without conventional sweetness."
				},
				{
					"handle": "lola-at-coat-check",
					"gift_description": "Lola at Coat Check fits the theme by balancing a crisp, upscale opening with a creamy, intimate core: grapefruit, cardamom, and orris make it feel tailored, while white chocolate and sandalwood add a discreet richness. It smells like money and understanding in a more playful, after-dark register."
				},
				{
					"handle": "cash-flower",
					"gift_description": "Cash Flower is here because it takes rose and gives it a plush, high-value finish: pepper adds lift, while cashmere, amber, and musk create a velvety aura of ease. The sandalwood depth keeps it from reading decorative, so it smells like money and understanding."
				},
				{
					"handle": "iris-sura-limited-release",
					"gift_description": "Iris Sura earns its place through an iris that feels rare and substantial rather than cosmetic: waxy orris, honeyed peach, and white chocolate give it a creamy density. Smoky whiskey nuances and dark woods add a savory seriousness that smells like money and understanding."
				}
			]
		},
		"scents-theyll-remember-you-for": {
			"title": "Scents They'll Remember You For",
			"subtitle": "Distinctive, memorable, signature-worthy fragrances",
			"category": "Editorial",
			"description": "These are the scents that etch a person into memory: distinctive compositions with a clear point of view and a trail that lingers in the mind.",
			"products": [
				{
					"handle": "cambodian-oud",
					"gift_description": "This fits the theme because it refuses to blur oud into the background; the rare Cambodian material stays unmistakable, so people will connect its smoky leather and woody austerity with one wearer alone. The resinous darkness and animalic edge make it feel like a personal stamp, not a generic woody scent."
				},
				{
					"handle": "ensang-noir",
					"gift_description": "This belongs here for the way it turns a rose into a signature with attitude: metallic aldehydes, smoked woods and opoponax make the floral heart impossible to forget. Magnolia’s creamy lift and patchouli’s shadow give it a vivid contrast that reads as a calling card, not a soft bouquet."
				},
				{
					"handle": "jany",
					"gift_description": "Jany earns its place by making gourmand comfort feel distinctly individual, so the memory it leaves is of a particular warmth rather than a standard dessert note. Baked apple, cinnamon and buttery pastry are sharpened by apricot, osmanthus and walnut, giving it a layered sweetness that stands apart."
				},
				{
					"handle": "195-a-c",
					"gift_description": "This works for the collection because it captures a personality in motion: green fig, pomegranate leaf and mint create a crisp, sunlit opening that feels instantly recognizable. Cedarwood and incense add a smoky, Mediterranean depth, so the scent lingers as a vivid orchard memory tied to one signature presence."
				},
				{
					"handle": "le-mat",
					"gift_description": "Le Mat fits because it has the kind of restless, enigmatic character people remember long after the room is empty. Grasse rose, patchouli and immortelle are threaded with dark spices and balsamic warmth, creating a chypre with a shadowy, unmistakable silhouette."
				},
				{
					"handle": "the-nirvana",
					"gift_description": "The Nirvana belongs here for its serene but distinctive contrast of bright citrus, black tea and jasmine sambac, a combination that reads like a personal aura. Ambrette adds a musky softness that makes the scent feel intimate and memorable, the sort of profile people associate with one calm, radiant figure."
				},
				{
					"handle": "awake",
					"gift_description": "Awake is a natural fit because it translates a very specific sensory image into identity: the bitter roast of coffee, the snap of lemon and the cool green bite of cardamom. Vetiver grounds it in dry woods, making the impression sharp enough to be remembered as someone’s unmistakable wake-up signature."
				},
				{
					"handle": "grande-lle",
					"gift_description": "Grande Ile earns its place through its textured amber profile, where Madagascan vanilla is not merely sweet but edged with pink pepper, clove and dry cedar. The resinous woods and smoky caramel nuance give it a recognizable warmth that feels tied to a single, memorable presence."
				},
				{
					"handle": "rose-de-taif",
					"gift_description": "Rose de Taif fits this collection by presenting rose as a clear personal emblem rather than a generic floral cloud. Lemon, geranium and nutmeg frame the Taif rose with green brightness and gentle spice, so its creamy musk reads as a refined, lasting signature people will recall instantly."
				}
			]
		},
		"gifts-with-personality": {
			"title": "Gifts With Personality",
			"subtitle": "Characterful, individual, non-generic fragrances",
			"category": "Gift Ideas",
			"description": "For the gift-giver who wants a scent with a point of view, these are fragrances that feel collected, not chosen by committee.",
			"products": [
				{
					"handle": "1805-tonnerre",
					"gift_description": "Choose Tonnerre when you want to gift someone who likes their style with a bracing, offbeat streak; the smoke, gunpowder and salt spray make it feel like a story on skin. Brandy, birch tar and dark woods give it a charred maritime signature that marks out a wearer with nerve."
				},
				{
					"handle": "cavallo-di-fuoco",
					"gift_description": "火马 - Cavallo di fuoco suits a recipient who enjoys being noticed for their curiosity rather than convention, especially if you’re buying for someone drawn to bold evening scents. Mint, celery and pomelo keep it startlingly fresh, while ginger, pepper and honeyed Chinese oud give it a smoky, leathery twist."
				},
				{
					"handle": "nuit-de-baklite",
					"gift_description": "Nuit de Bakélite is the one to give when you want a green floral with a sharp, opinionated personality for someone who prefers edge over prettiness. Galbanum, carrot seed and cardamom cut through the tuberose, and the leathered dryness makes it feel like a signature for a wearer with taste and grit."
				},
				{
					"handle": "cambodian-oud",
					"gift_description": "If your recipient loves rare materials and wears fragrance as a private statement, Cambodian Oud is the most direct gift here. Its smoky oud, leathery depth and woody backbone let the raw material speak plainly, which is exactly why it feels so individual."
				},
				{
					"handle": "pear-pavlova",
					"gift_description": "Pear Pavlova is a thoughtful pick for someone who likes charm with a sly undercurrent, perhaps as a birthday or celebratory gift. Juicy pear, airy meringue and pink pepper make it playful, but bitter almond, jasmine sambac and ambrette keep it from becoming merely sweet."
				},
				{
					"handle": "pink-sweats",
					"gift_description": "Pink Sweats works beautifully as a gift for someone with easy confidence and a sense of humor, especially if they enjoy fragrances that feel modern and unfussy. Rhubarb, lychee and rosé Champagne give it a sparkling lift, while marshmallow, white musk and pink peony soften it into something quietly distinctive."
				},
				{
					"handle": "rose-de-taif",
					"gift_description": "Rose de Taif is the elegant choice for a recipient who appreciates roses with character rather than cliché, ideal for a refined daytime gift. Lemon, geranium and nutmeg sharpen the Taif rose’s creamy musky glow, giving the flower a luminous, slightly green outline."
				},
				{
					"handle": "sand",
					"gift_description": "Gift Sand to someone who likes familiar woods with a left-field turn, or to a sandalwood fan who already owns the obvious options. Australian sandalwood, banana nuance, cardamom and incense create a resinous, playful contrast that makes the wearer seem delightfully hard to pin down."
				},
				{
					"handle": "arcadia-1",
					"gift_description": "Arcadia suits the person who prefers introspection over flash, making it a strong choice for a thoughtful, artistic recipient. Iris, olibanum and mineral earthiness meet bittersweet tonka and smoky vetiver, so the effect is serene but never bland."
				}
			]
		},
		"perfumes-for-compliments": {
			"title": "Perfumes for Compliments",
			"subtitle": "Crowd-pleasers that draw positive attention",
			"category": "Editorial",
			"description": "These are the bottles that turn heads in passing: bright, flattering signatures that seem to invite a second look and the inevitable “what are you wearing?”",
			"products": [
				{
					"handle": "guava-sorbet",
					"gift_description": "Guava Sorbet belongs here because its guava-mandarin spark and passionfruit tang read instantly cheerful, the kind of tropical brightness people notice right away. Peachy osmanthus, jasmine, and vanilla keep it smooth rather than sharp, making the reaction feel warm and easy."
				},
				{
					"handle": "lamar",
					"gift_description": "Lamar fits this collection by leaning into lush fruit sweetness that feels immediately likable and easy to compliment. Pineapple, apple, and berries give it a vivid opening, while rose, magnolia, and vanilla-musk depth make it linger with crowd-pleasing richness."
				},
				{
					"handle": "davana-vanille",
					"gift_description": "Davana & Vanille earns its place through its edible warmth without becoming heavy, which is exactly the sort of scent that prompts curious comments. Lychee, davana, and vanilla are sharpened by a light herbal edge, then rounded by sandalwood and tonka for a soft, inviting trail."
				},
				{
					"handle": "fig-porn",
					"gift_description": "FIG PORN works for this theme because its creamy fig profile feels distinctive yet universally appealing, the sort of skin-close sweetness that gets asked about. Pear, bergamot, and milky green fig set it apart, while peony, vanilla, and musk keep the finish supple and flattering."
				},
				{
					"handle": "damask",
					"gift_description": "Damask fits because it balances rose with fruit and mineral freshness, creating a fragrance that feels noticeable without being loud. Blackcurrant, pear, and lemon brighten the floral core, and the saline amber-musk accord gives it a crisp, memorable signature."
				},
				{
					"handle": "rouge-avignon",
					"gift_description": "Rouge Avignon belongs in a compliments collection for its dramatic rose that still reads approachable thanks to fruit and gourmand facets. Raspberry, cocoa, and black truffle add a dark, intriguing twist, while sandalwood and vetiver leave a smoky, polished trail people tend to ask about."
				},
				{
					"handle": "juice-personal",
					"gift_description": "Juice | Personal fits this theme by staying close to the skin while still projecting a bright, attractive fruitiness that draws people in. Raspberry and rhubarb give it a tart sparkle, and rose with soft woods and musk keeps it airy, modern, and easy to compliment."
				},
				{
					"handle": "desiree",
					"gift_description": "Désirée works here because its floral generosity is lifted by grapefruit and pink pepper, making it feel lively rather than formal. Gardenia, rose, amber, and white musk create a warm, cushioned aura that tends to trigger immediate positive attention."
				},
				{
					"handle": "marbella",
					"gift_description": "Marbella suits the collection with its peach-and-rose brightness, a combination that feels instantly charming and easy to love. Jasmine, pink pepper, and amber add sunlit glow and a refined sweetness, giving it the kind of smooth radiance that often earns a “what is that?”"
				}
			]
		},
		"the-easy-winners": {
			"title": "The Easy Winners",
			"subtitle": "Effortless, reliable, universally loved scents",
			"category": "Editorial",
			"description": "These are the fragrances that slip on easily and win people over fast: clear, likable, and almost suspiciously safe in a crowd.",
			"products": [
				{
					"handle": "vetiver-santal",
					"gift_description": "This fits the collection because its bergamot-to-vetiver arc is crisp, clean, and impossible to find objectionable, making it a near-guaranteed compliment magnet. The dry Haitian vetiver, milky sandalwood, and soft benzoin keep it approachable rather than severe."
				},
				{
					"handle": "ramune",
					"gift_description": "Ramune belongs here for its bright, instantly readable charm: lemon, mint, and aldehydes make it feel playful and easy to like from the first spray. The soft musk, vanilla, and delicate florals give it that crowd-winning sweetness without turning heavy."
				},
				{
					"handle": "santal",
					"gift_description": "Santal earns its place as a universal sandalwood with just enough spice to stay interesting and never polarizing. Citrus, lavender, and cardamom lift the composition, while cedar and amber keep it smooth, tidy, and broadly appealing."
				},
				{
					"handle": "musc-oli",
					"gift_description": "Musc Oli fits this theme because it smells like a refined skin scent that most people instinctively find pleasant and close to the body. White musk, Cashmeran, and sandalwood create the easygoing core, with damask rose adding a faintly plush lift."
				},
				{
					"handle": "pg0-4-le-musc-la-peau",
					"gift_description": "Le Musc & La Peau Extrait is here for its polished, low-risk softness: it reads as clean, warm, and quietly seductive without ever pushing too hard. Rosemary, ylang-ylang, and tonka add gentle character around the white musk and sandalwood base."
				},
				{
					"handle": "oud-cannelle",
					"gift_description": "Oud Cannelle fits the collection by making oud feel friendly rather than forbidding, which is exactly why it works so well as a crowd-pleaser. Cinnamon, smoky myrrh, and vanilla round off the darker woods and incense into something warmly accessible."
				},
				{
					"handle": "tears-on-mystic-sands",
					"gift_description": "Tears On Mystic Sands belongs because its bright fruit-and-flower opening makes the oud and incense feel surprisingly easy to wear and easy to enjoy. Lychee, rose, and freesia keep it luminous, while leather and sandalwood add just enough depth for broad appeal."
				},
				{
					"handle": "neon-night",
					"gift_description": "Neon Night fits this set through its clean, upbeat profile that feels fresh on first contact and never challenging. Bergamot, mandarin, and green tea give it a sparkling start, while peony and sandalwood keep the finish smooth and likable."
				},
				{
					"handle": "sous-le-pont-mirabeau",
					"gift_description": "Sous Le Pont Mirabeau earns its spot by turning mineral woods and watery notes into something quietly romantic and universally agreeable. Fig, violet leaf, and aquatic freshness are softened by cedar, sandalwood, and a trace of incense, making it an easy yes for many noses."
				}
			]
		},
		"unexpected-favourites": {
			"title": "Unexpected Favourites",
			"subtitle": "Surprising gems that become instant favourites",
			"category": "Editorial",
			"description": "These are the sleepers that catch you off guard: fragrances that look odd, then quietly become the ones you keep reaching for.",
			"products": [
				{
					"handle": "mezcal-chocolat",
					"gift_description": "This fits the collection because it’s the kind of left-field gourmand you wouldn’t expect to win you over, then suddenly does. Salted lime, chili, and fruity mezcal keep it vivid, while dark chocolate, rum, and incense give it a shadowy pull."
				},
				{
					"handle": "1805-tonnerre",
					"gift_description": "Tonnerre belongs here as the show-stealing outlier: a maritime scent that turns smoke and gunpowder into something strangely compelling. Salt spray, sharp citrus, and brandy make it feel brisk, while birch tar and dark woods give it a charred bite."
				},
				{
					"handle": "dancing-light",
					"gift_description": "You wouldn’t expect this to become a favourite from the first inhale, but its icy mint, Siberian pine needles, and luminous jasmine make that surprise immediate. Neroli, syringa, and sandalwood give it a crisp-green brightness with a soft, creamy finish."
				},
				{
					"handle": "scrtions-magnifiques",
					"gift_description": "This is the collection’s most provocative sleeper hit because it refuses to behave like a conventional perfume. Saline sharpness, a milky accord, and sandalwood create its unsettling hook, making the strangeness itself the reason it stands out."
				},
				{
					"handle": "afternoon-tea",
					"gift_description": "Afternoon Tea fits the theme by taking a familiar idea and making it unexpectedly magnetic. Lemon, maté tea, and champaca keep it bright and aromatic, while jasmine and dusty vetiver add a floral haze with a faint smoky edge."
				},
				{
					"handle": "milk-bold",
					"gift_description": "Milk | Bold earns its place as the unlikely favourite that feels instantly addictive. Marshmallow, tonka bean, and smoky firewood give it edible comfort with amber and mahogany underneath, so the sweetness lands with a darker, more dramatic twist."
				},
				{
					"handle": "solar-wind",
					"gift_description": "This is exactly the kind of hidden gem that surprises you with how much personality it has. Tart rhubarb, bright citrus, and guava make it pop, while strawberry and elemi push it into a sweet-sour register that feels unusually vivid."
				},
				{
					"handle": "comme-des-garcons-2",
					"gift_description": "Comme des Garçons 2 fits because it turns abstraction into something strangely lovable. Aldehydes, ink, and tea give it a clean, graphic opening, while incense and woods add a hushed depth that makes the brightness feel unexpectedly warm."
				},
				{
					"handle": "mistpouffer",
					"gift_description": "Mistpouffer belongs in this collection for its quiet oddity: it doesn’t announce itself, it drifts in and wins you over. Green fig leaf, pine, and vetiver keep it airy and woody, while cypriol, immortelle, and smoke leave a misty, offbeat trail."
				}
			]
		},
		"gifts-for-her": {
			"title": "Gifts for Her",
			"subtitle": "Beautiful feminine and feminine-leaning fragrances",
			"category": "Gift Ideas",
			"description": "For the woman who loves modern femininity: fragrances that feel thoughtful, flattering, and a little unexpected, ready to become a gift with personality.",
			"products": [
				{
					"handle": "bubble-tea",
					"gift_description": "Choose Bubble Tea when you want a gift that feels cozy but not childish: the roasted black tea, creamy milk, and brown sugar give it a chic gourmand tilt, while the dusty floral nuance keeps it poised for a contemporary feminine wardrobe."
				},
				{
					"handle": "gratia-aestiva",
					"gift_description": "Gratia Aestiva suits a recipient who lights up a room, making it ideal for birthdays or summer celebrations. Pineapple, melon, and strawberry bring juicy brightness, while passion fruit, jasmine, and ambered woods stop the sweetness from drifting into syrup."
				},
				{
					"handle": "misty-cypress-ash",
					"gift_description": "Misty Cypress Ash fits a gift for someone who prefers femininity with a quiet, untamed streak rather than overt prettiness. Wormwood, cypress, and tobacco create a cool, smoky-green profile, with vetiver and cedar adding an austere finish that feels striking and modern."
				},
				{
					"handle": "pg16-jardins-de-kerylos",
					"gift_description": "PG16 Jardins de Kérylos is a refined choice for the woman who likes softness with structure, especially for daytime gifting or elegant spring occasions. Green fig, white peach, and tuberose give it a luminous feminine core, while sycamore wood and musk keep the composition airy and composed."
				},
				{
					"handle": "pink-sweats",
					"gift_description": "Pink Sweats is the pick for a playful recipient who likes her femininity with wit, not sugar rush. Rhubarb, lychee, and rosé Champagne make it sparkle, then peony, marshmallow, and white musk smooth the edges into something relaxed and flirtatious."
				},
				{
					"handle": "roasted-green-tea",
					"gift_description": "ほうじ茶 Hojicha works beautifully as a thoughtful gift for someone drawn to comfort with a savory twist. Roasted tea leaves, creamy milk, and peanut give it a toasty softness, while mint and seaweed lend an offbeat freshness that sets it apart from sweeter tea scents."
				},
				{
					"handle": "talento",
					"gift_description": "Talento is the one to choose when you want to gift a protective, elegant floral that feels intimate rather than showy. Green rose, mint, and aldehydes create a cool, radiant bloom, and the woody base gives it the feeling of a modern talisman for daily wear."
				},
				{
					"handle": "god-bless-cola",
					"gift_description": "God Bless Cola makes a fun gift for the woman who enjoys fragrance with a wink, perfect for casual celebrations or a friend who loves novelty. Cola fizz, caramel popcorn, and vanilla bring the movie-night mood, while the citrus sparkle and metallic-can effect keep it sharp instead of cloying."
				},
				{
					"handle": "narcotic-v",
					"gift_description": "Narcotic V belongs in a gift edit for someone who wants her florals bold, glamorous, and unmistakably feminine. Tuberose, jasmine, and orange blossom create a lush white-floral statement, and cedarwood adds just enough frame to keep the scent feeling powerful rather than sweetly fragile."
				}
			]
		},
		"gifts-for-him": {
			"title": "Gifts for Him",
			"subtitle": "Masculine and masculine-leaning fragrances",
			"category": "Gift Ideas",
			"description": "For the man who prefers character over cliché, these gifts lean masculine with texture, restraint, and just enough intrigue to feel chosen, not generic.",
			"products": [
				{
					"handle": "misty-cypress-ash",
					"gift_description": "This suits a gift for someone who likes his masculinity tempered by wilderness: bitter wormwood, cypress, and tobacco feel brisk yet grounded, while vetiver, cedar, and mineral smoke give it a rugged, thoughtful edge."
				},
				{
					"handle": "monsieur",
					"gift_description": "Choose this when you want a more composed, grown-up woody gift; cedarwood, vetiver, and sandalwood are sharpened by oakmoss, patchouli, and papyrus, creating a forested profile that feels distinguished rather than loud."
				},
				{
					"handle": "pg-111-indian-wood",
					"gift_description": "A strong pick for the man who likes freshness with substance, this balances peppermint and cardamom with nutmeg, then settles into sandalwood, vetiver, and oakmoss for a relaxed but not casual signature."
				},
				{
					"handle": "rasputin",
					"gift_description": "Gift this to someone drawn to darker, more dramatic masculinity: cardamom, clove, and cinnamon meet dark chocolate, incense, and oud, with leather, patchouli, and tonka making it richly textured and unmistakably bold."
				},
				{
					"handle": "roasted-green-tea",
					"gift_description": "This is ideal for the recipient who appreciates quiet comfort with a masculine twist; roasted tea, milk, and peanut are made more distinctive by mint and seaweed, so it reads contemplative, savory, and unlike standard fresh scents."
				},
				{
					"handle": "602",
					"gift_description": "A refined choice for a man who wants woods with poise, not heaviness: cedar, patchouli, and pepper stay dry and airy, while vetiver and warm spice add enough depth to keep it interesting on skin."
				},
				{
					"handle": "apotheose",
					"gift_description": "Pick this for someone who enjoys old-world flair with a modern streak; aldehydes, eucalyptus, and pepper give it lift, then ylang-ylang, rose, benzoin, and musk turn the floral richness into something unexpectedly assertive."
				},
				{
					"handle": "kajal-i",
					"gift_description": "This works beautifully as a gift for the man who likes his scent luminous but grounded: cassis, grapefruit, and mandarin bring energy, while cardamom, orange blossom, sandalwood, oud, and vanilla lend a smooth, confident finish."
				},
				{
					"handle": "paradoxial",
					"gift_description": "For a recipient who prefers clean lines with a shadowed core, this blends citrus and cardamom with lavender and incense, then anchors everything in vetiver, cedar, and amber for a crisp yet quietly magnetic result."
				}
			]
		},
		"unisex-gifts": {
			"title": "Unisex Gifts",
			"subtitle": "Gender-fluid fragrances for anyone",
			"category": "Gift Ideas",
			"description": "A gift edit of gender-free fragrances chosen for their easy sharing, each one suited to whoever wears it and the moment you’re marking.",
			"products": [
				{
					"handle": "195-a-c",
					"gift_description": "Choose this for someone who likes their gifts fresh yet distinctive: fig, green pomegranate leaf and mint make it feel bright and easy to wear, while cardamom and incense give it a more memorable, shared-by-anyone finish. It suits a birthday or a thank-you gift when you want something refined but not tied to one style."
				},
				{
					"handle": "20359",
					"gift_description": "This is a strong pick for a recipient who enjoys a sweeter, moodier profile that still reads gender-free; maraschino cherry, clove and heliotrope keep it plush, while latex, tobacco and leather add a nightlife edge. It feels especially apt for an evening gift or a date-night gesture, since it has character without leaning masculine or feminine."
				},
				{
					"handle": "565",
					"gift_description": "Give this when you want a fragrance that feels generous and attention-getting on any wearer: citrus and candied bergamot lift the opening, then amber and dark musk create a warm, shared aura. The incense-like woods make it more than a sweet scent, so it works well for someone who likes a bold signature with depth."
				},
				{
					"handle": "a-pas-de-loup",
					"gift_description": "A thoughtful choice for a nature lover, because its damp earth, mushroom and oakmoss notes create a woodland atmosphere that feels equally at home on any person. Clary sage, sandalwood and benzoin keep it smooth and wearable, making it a lovely gift for someone who prefers quiet, outdoorsy fragrances."
				},
				{
					"handle": "al-sarab",
					"gift_description": "Pick this for a gift that feels dramatic but not gendered: mixed oud oils and aged Cambodian oud give it a smoky, leathery backbone, while fruity nuances soften the intensity. Its animalic depth and desert-mirage feel suit someone who wants a statement scent for special occasions or evening wear."
				},
				{
					"handle": "ara",
					"gift_description": "This works beautifully as a universal gift because it blends classic herbal freshness with a richer, more textured drydown that anyone can wear confidently. Holy basil, thyme and lavender give it an aromatic lift, while pine, oakmoss and styrax add a smoky, old-school finish."
				},
				{
					"handle": "arashiyama",
					"gift_description": "Choose this for someone who prefers calm, clean-leaning scents that still feel distinctive on any skin. Bamboo leaves, cut grass and rain-soaked earth make it serene and easy to share, while transparent violet and bamboo wood add a soft, modern polish."
				},
				{
					"handle": "arcadia-1",
					"gift_description": "A gift for the reflective wearer, this suits anyone who likes their fragrance understated but not plain: iris and olibanum create a cool, powdery core, and mineral earth gives it a grounded feel. Tonka and vetiver bring a savory, smoky finish that makes it versatile across wardrobes and occasions."
				},
				{
					"handle": "ayiti",
					"gift_description": "This is ideal when you want a universally wearable gift with a rain-washed, outdoorsy character: Haitian vetiver, petrichor and patchouli give it depth, while the powdery texture keeps it approachable. The smoky undertone adds complexity without pushing it toward any one gender expression."
				}
			]
		},
		"valentines-day-gifts": {
			"title": "Valentine's Day Gifts",
			"subtitle": "Romantic fragrances for your Valentine",
			"category": "By Occasion",
			"description": "For a Valentine’s gift, these scents read like a love letter in bloom—made for the moment the evening turns red and every glance means more.",
			"products": [
				{
					"handle": "lamar",
					"gift_description": "Choose Lamar if you want to gift a bright, irresistible declaration: pineapple, apple and berries make it feel celebratory, while rose, jasmine and magnolia keep the romance unmistakable. The amber, vanilla and musk base suits a Valentine who likes sweetness with a warm afterglow."
				},
				{
					"handle": "rouge-avignon",
					"gift_description": "Rouge Avignon is the bottle to give when you want the night to lean dramatic and devoted. Raspberry, cocoa and black truffle set its dark allure apart, while rose, ylang-ylang and smoky sandalwood make it feel made for a candlelit, high-stakes evening."
				},
				{
					"handle": "damask",
					"gift_description": "Damask fits this collection because it turns a classic rose gift into something vivid and intimate. Blackcurrant, pear and lemon give the opening a bright lift, then mineral amber and musk bring a saline softness that feels like a seaside promise."
				},
				{
					"handle": "purple-eclipse",
					"gift_description": "Purple Eclipse is for the Valentine who loves a floral gesture with a little flirtation. Crisp apple, blackcurrant and pink pepper sharpen the romance, while heliotrope, honeyed white flowers and vanilla give it a nostalgic, tender glow."
				},
				{
					"handle": "ylang-ylang-nosy-be",
					"gift_description": "Ylang Ylang Nosy Be belongs here for its lush, island-bright way of saying desire without restraint. Ylang-ylang, jasmine and vanilla create the heart of the gift, while the fresh lift and subtle woody thread keep it elegant for a red-letter evening."
				},
				{
					"handle": "jasmin-ylang",
					"gift_description": "Jasmin & Ylang makes a thoughtful Valentine’s gift when you want warmth without heaviness. Jasmine, ylang-ylang and frangipani give it a sunlit floral charm, and vanilla with benzoin add a soft balsamic finish that feels close and comforting."
				},
				{
					"handle": "desiree",
					"gift_description": "Désirée suits this theme because it feels like a playful invitation wrapped in flowers. Gardenia, rose and pink pepper give the gift its spark, while grapefruit, passion fruit and white musk keep it bright, affectionate and ready for an evening confession."
				},
				{
					"handle": "malli-nadu",
					"gift_description": "Malli Nadu is the choice for a Valentine who deserves something radiant and transportive. Jasmine and tuberose provide the lush floral core, while mandarin and davana add an apricot-herbal twist that sandalwood and vetiver smooth into a glowing finish."
				},
				{
					"handle": "delirium",
					"gift_description": "Delirium fits the collection as the most lavish way to make a declaration. Frangipani, gardenia and jasmine are deepened by champaca and tuberose, while white lotus, vanilla and raw sugar leave a sweet, powdery trail for a truly charged night."
				}
			]
		},
		"mothers-day-gifts": {
			"title": "Mother's Day Gifts",
			"subtitle": "Beautiful fragrances to spoil Mum",
			"category": "By Occasion",
			"description": "A bouquet of gifts for the woman who deserves the fragrance she’d never choose for herself, but will remember every time she wears it.",
			"products": [
				{
					"handle": "mathilde",
					"gift_description": "Mathilde is a beautiful choice when you want to spoil Mum with something lush and a little unexpected: the passion fruit and papaya brighten its grand jasmine and narcissus, while iris and sandalwood keep it graceful. It feels like a present for a mother who deserves to be wrapped in floral richness, not just another safe bouquet."
				},
				{
					"handle": "cherry-passion",
					"gift_description": "Cherry Passion suits Mother's Day if you want a gift that feels joyful and affectionate rather than formal, with ripe cherry, strawberry and sweet almond giving it a playful lift. Jasmine, tuberose and vanilla make it feel more special than a typical fruity perfume, which is exactly why it says she matters."
				},
				{
					"handle": "divina-flora",
					"gift_description": "Divina Flora fits this collection because it has the kind of radiant, elevated floral character that makes a mother feel truly celebrated, with lemongrass and passion fruit adding sparkle to peony, jasmine and narcissus. The herbal-green nuance and sandalwood-vanilla base give it a distinctive finish, so it feels like a gift chosen with real care."
				},
				{
					"handle": "ramune",
					"gift_description": "Ramune belongs here as the scent for the mum who’d never buy a soda-inspired perfume for herself, yet would love its bright lemon, mint and aldehydes. The delicate florals, soft musk and vanilla make it playful but wearable, turning an everyday memory into a thoughtful occasion gift."
				},
				{
					"handle": "asoral",
					"gift_description": "Asoral is a fitting Mother's Day pick if you want to give her something quietly luxurious and very different from an ordinary rose, with Turkish and Lani roses softened by magnolia and peony. The tea, liquorice and hay accents add an airy, nuanced twist, making it feel like a perfume for a mother who deserves something rare."
				},
				{
					"handle": "suze",
					"gift_description": "Suze works for this theme because it offers Mum a more intriguing floral than the usual polished bouquet, with spearmint and clementine flashing through jasmine, magnolia and orris. Black tea, soft woods and amber give it a reflective warmth, so it feels like a considered gift for a woman who appreciates character."
				},
				{
					"handle": "this-is-not-a-blue-bottle-1-7",
					"gift_description": "This is Not a Blue Bottle 1/.7 – Happiness is ideal if you want to give Mum a fragrance that feels like a holiday she didn’t know she needed, with coconut, frangipani and milky sandalwood creating a sunlit softness. The sun-lotion accord makes it especially giftable for Mother's Day, since it reads as a treat rather than a practical buy."
				},
				{
					"handle": "32-c",
					"gift_description": "32°C suits the occasion when you want to give Mum something creamy, warm and a little indulgent, with jasmine sambac, tiare and coconut set over vanilla and sandalwood. White tea and ginger keep it from feeling heavy, so it becomes the kind of sunny, skin-close perfume she may never pick out for herself."
				},
				{
					"handle": "rose-shiso",
					"gift_description": "Rose Shiso fits this collection because it gives Mum a rose with a fresh, savory twist, making it feel more distinctive than a standard floral gift. The Japanese shiso, iris and cedar sharpen the petal richness, so it becomes a thoughtful present for a mother who deserves something elegant but not predictable."
				}
			]
		},
		"fathers-day-gifts": {
			"title": "Father's Day Gifts",
			"subtitle": "Distinctive scents for Dad",
			"category": "By Occasion",
			"description": "A considered edit of scents that feel like a quiet upgrade for Dad—distinctive, refined, and chosen to honour him without raising its voice.",
			"products": [
				{
					"handle": "rasputin",
					"gift_description": "Choose Rasputin if you want to give him a bolder, more distinguished twist on his usual scent: cardamom, dark chocolate and smoky incense make it feel special for Father’s Day. The leather, Indian oud and patchouli keep it firmly in understated-luxury territory rather than showy territory."
				},
				{
					"handle": "monsieur",
					"gift_description": "Monsieur suits the dad who appreciates depth over flash, making it a thoughtful Father’s Day gift with a forested, composed edge. Cedarwood, vetiver and papyrus are sharpened by wet-stone mineral freshness, so it feels like a quiet step up from a standard woody fragrance."
				},
				{
					"handle": "drakon",
					"gift_description": "Drakon fits this collection because it gives dad a classic fougère shape, then darkens it with a more tailored, grown-up mood. Bitter betel leaf, resinous fir and powdery leather set it apart, while the mossy finish makes the whole composition feel cool and exacting."
				},
				{
					"handle": "misty-cypress-ash",
					"gift_description": "Misty Cypress Ash is a strong Father’s Day pick if you want to honour him with something austere yet elegant, like a walk through misted woodland. Bitter wormwood, aromatic cypress and tobacco give it a dry, contemplative character that feels more refined than routine."
				},
				{
					"handle": "paradoxial",
					"gift_description": "Paradoxical belongs here because it offers dad an easy, polished upgrade: fresh enough for daily wear, but with enough depth to feel gifted rather than grabbed. Sparkling citrus, lavender and incense are lifted by vetiver and cedar, giving it a clean profile with a quietly magnetic finish."
				},
				{
					"handle": "veles",
					"gift_description": "Veles is for the dad who likes his fragrance to feel rare and grounded, making it a memorable Father’s Day gesture. Tart rhubarb, saffron and smoky oud give it a rugged signature, while tarred leather and labdanum add a darker, more distinctive warmth."
				},
				{
					"handle": "vladimir",
					"gift_description": "Vladimir fits the theme by balancing crisp confidence with a softer, more intimate base—ideal if you want a fragrance that feels like a discreet upgrade for Dad. Grapefruit, sage and cedar lead into orris, incense and leather, which keeps it classic but not predictable."
				},
				{
					"handle": "602",
					"gift_description": "602 Bois Narcotique is a smart choice for Father’s Day because it reads as composed and unfussy, yet still feels considered and gift-worthy. Dry cedar, leafy patchouli and dusty pepper give it a restrained woody profile, with vetiver and warm spice adding just enough character."
				},
				{
					"handle": "letrog-acqua",
					"gift_description": "L'Etrog Acqua suits this collection if your dad prefers something brighter and more elegant than a heavy signature scent. Cedrat, lemon and bergamot bring a clean citrus lift, while petitgrain and dry cedarwood keep it tasteful, fresh and quietly refined."
				}
			]
		},
		"birthday-gifts": {
			"title": "Birthday Gifts",
			"subtitle": "Celebrate them with a beautiful fragrance",
			"category": "By Occasion",
			"description": "A birthday fragrance should feel like a bright promise: personal, present, and a little more radiant than the year before.",
			"products": [
				{
					"handle": "guava-sorbet",
					"gift_description": "Choose Guava Sorbet when you want the gift to feel instantly celebratory and easy to wear, with juicy guava, mandarin, and passionfruit giving the recipient a sunny lift for their birthday. Its peachy osmanthus, jasmine, and vanilla make it feel specially chosen rather than generic."
				},
				{
					"handle": "32-c",
					"gift_description": "32°C suits a birthday gift for someone who likes their fragrance to feel like a warm getaway, thanks to jasmine sambac, tiare, and coconut that read as carefree and intimate. The milky sandalwood, vanilla, and white tea-ginger brightness give it enough presence to mark the occasion without overwhelming."
				},
				{
					"handle": "davana-vanille",
					"gift_description": "Davana & Vanille is a thoughtful birthday pick if you want something charming and approachable, because its airy gourmand style feels like a sweet wish for the year ahead. Lychee, davana, and vanilla keep it distinct from richer dessert scents, while sandalwood and tonka add a gentle, comforting trail."
				},
				{
					"handle": "cherry-passion",
					"gift_description": "Cherry Passion works beautifully as a birthday present for someone who loves a more playful signature, with cherry, strawberry, and almond making the gift feel bright and festive. Jasmine, rose, and tuberose lift it beyond candy sweetness, and the sandalwood-tonka base gives it a warm, lasting finish."
				},
				{
					"handle": "far",
					"gift_description": "Far is a clever birthday gift for a recipient who enjoys cheerful, food-leaning scents, since yuzu, lime, and vanilla create a sparkling citrus treat that feels like a fresh start. The cream and buttery biscuit facets make it stand out in the collection, giving the fragrance a memorable sense of occasion."
				},
				{
					"handle": "koiame",
					"gift_description": "恋雨 Koiame (It's Raining Love) is ideal when you want the birthday gift to feel tender and personal, as mandarin, cassis, and apple give it a bright first impression that feels hopeful. Magnolia, water lily, and cedarwood add a quiet elegance, making it a soft but meaningful choice for the year ahead."
				},
				{
					"handle": "fleur-de-rocaille",
					"gift_description": "Fleur de Rocaille suits a birthday gift for someone who appreciates classic beauty with a luminous twist, because its aldehydic sparkle makes the bouquet feel especially dressed for the day. Lily-of-the-valley, rose, and ylang-ylang are lifted by sandalwood, so it feels refined without losing warmth."
				},
				{
					"handle": "mathilde",
					"gift_description": "Mathilde is a lovely birthday gift when you want something lush and expressive, since passion fruit, papaya, and jasmine make it feel like a generous celebration in scent form. Narcissus, iris, and sandalwood give it a more artistic depth than the sweeter picks here, so it feels personal and elevated."
				},
				{
					"handle": "haf",
					"gift_description": "Haf is a strong choice for a birthday present if you want the wearer to feel refreshed and uplifted all year, with bergamot, grapefruit, and blackcurrant delivering a vivid, cheerful opening. Magnolia, rose, jasmine, and creamy sandalwood give it enough softness and presence to read as a thoughtful gift."
				}
			]
		},
		"anniversary-gifts": {
			"title": "Anniversary Gifts",
			"subtitle": "Luxurious scents for a milestone moment",
			"category": "By Occasion",
			"description": "For an anniversary, choose a scent that feels like another chapter in the same love story—richer, deeper, and made to mark how far you've come together.",
			"products": [
				{
					"handle": "the-empress-kiss",
					"gift_description": "The Empress Kiss suits an anniversary gift when you want the message to be devotion with a little drama: saffron, cardamom, and clove give it a celebratory spark, while rose, oud, and caramel make it feel like a cherished continuation of years shared. It’s a strong choice for a recipient who likes their romance warm, memorable, and unmistakably special."
				},
				{
					"handle": "castiamanti",
					"gift_description": "Castiamanti is ideal for a milestone because it reads like love matured into something more intimate and golden, with myrtle, saffron, and honeyed rose setting a refined tone. Incense, labdanum, and vanilla give it a resinous glow that feels perfect for honoring a long relationship with depth rather than flash."
				},
				{
					"handle": "au-pied-du-rosier",
					"gift_description": "Au Pied du Rosier fits an anniversary gift for a partner who appreciates a bold twist on romance: Grasse rose keeps the gesture tender, while leather, rubber, and saffron make it feel modern and surprising. Oud adds gravitas, so it becomes a fitting nod to shared years that have grown more distinctive with time."
				},
				{
					"handle": "sirio",
					"gift_description": "Sirio works beautifully for a milestone celebration because its rhubarb brightness and lush fruit suggest the lively side of a long partnership, while rose and white musk keep it affectionate. Guaiac wood, oud, and vanilla give the gift a grounded finish, making it feel like a love story that still has new chapters to write."
				},
				{
					"handle": "ta-if",
					"gift_description": "Ta’if is a polished anniversary choice when you want the recipient to feel admired and cherished, with pink pepper and saffron adding a sparkling lift to the rose. Dates and amber bring a rich, dusk-like sweetness that suits the quiet confidence of a couple who know each other well."
				},
				{
					"handle": "106",
					"gift_description": "106 Chromatic Rose makes sense for an anniversary gift because it offers rose in a more nuanced, grown-up register—Damascena petals, pink pepper, and green davana keep it vivid rather than predictable. Vanilla and sandalwood smooth the edges, giving your recipient a graceful reminder of shared years and steady affection."
				},
				{
					"handle": "oud-cannelle",
					"gift_description": "Oud Cannelle is a strong fit for marking a milestone because its cinnamon, smoke, and incense create the sense of a celebration lit from within, while rose softens the mood. The oud and amber base gives it a lasting, comforting presence, ideal for a gift that says your bond has substance and history."
				},
				{
					"handle": "mansa-musa",
					"gift_description": "Mansa Musa suits an anniversary when you want to give something commanding and rare, the kind of fragrance that reflects a relationship with weight and depth. Animalic oud, leather, and spice make it unmistakable, while sandalwood, amber, and a trace of rose keep the darkness tied to affection rather than distance."
				},
				{
					"handle": "hey-bitch",
					"gift_description": "Hey Bxtch is the daring anniversary pick for a recipient who likes contrast, because its orange blossom, rhubarb, and leather create a flirtation between tenderness and edge. Sueded iris, milky sandalwood, and vanilla soften the impact, making it feel like a bold tribute to a love that has never gone stale."
				}
			]
		},
		"thank-you-gifts": {
			"title": "Thank You Gifts",
			"subtitle": "Thoughtful, affordable gestures of gratitude",
			"category": "By Occasion",
			"description": "A small fragrance can say thank you with care: considered, gracious, and just distinctive enough to show you noticed.",
			"products": [
				{
					"handle": "osmanthus-oolong",
					"gift_description": "This suits a thank-you gift when you want something airy and intelligent rather than showy; osmanthus and oolong feel attentive, while frangipani, orange blossom, and guaiac wood keep it quietly special."
				},
				{
					"handle": "damascus-cologne",
					"gift_description": "Choose this for someone who likes luminous classics with a modern edge, because grapefruit and rose make the gesture feel bright and refined, and labdanum adds a soft amber hum without turning heavy."
				},
				{
					"handle": "mister-bungalow",
					"gift_description": "This is a thoughtful pick for the friend who appreciates fresh, easygoing scents, since lemongrass, sweet orange, and spike lavender feel cheerful and tailored, with vetiver and patchouli giving it a grounded finish."
				},
				{
					"handle": "rose-bergamote",
					"gift_description": "A lovely way to say thanks to someone who prefers graceful simplicity, this works because bergamot and rose feel clean and kind, while the gentle sweetness and cologne-style lightness keep it approachable."
				},
				{
					"handle": "exploration-kit",
					"gift_description": "This makes a smart gratitude gift for a fragrance explorer, since the set lets the recipient sample cedarwood, iso e super, marshmallow, milk accord, sandalwood, and vanilla across different moods instead of one loud statement."
				},
				{
					"handle": "firenze",
					"gift_description": "Give this to someone who enjoys crisp elegance with a warm afterglow, because bergamot, bitter orange, and lemon make it feel polished for a thank-you, while sandalwood and vetiver add depth that lingers politely."
				},
				{
					"handle": "bloom-away-21-04",
					"gift_description": "This fits a thank-you present when you want something fresh but not plain; bergamot, peach, and rhubarb feel thoughtful and a little unexpected, and moss with patchouli keeps the floral heart from feeling too sweet."
				},
				{
					"handle": "mahoganysun",
					"gift_description": "Pick this for a recipient who likes relaxed, sunny scents, since milky fig, frangipani, and vanilla create an easygoing warmth that feels generous, with mahogany adding a smoother, more distinctive backbone."
				},
				{
					"handle": "brume-dete",
					"gift_description": "This is ideal when your thank-you should feel clean and considerate, because mandarin, petitgrain, rosemary, lavender, and mint create an understated herbal lift that suits everyday wear and doesn’t overwhelm the room."
				}
			]
		},
		"wedding-gifts": {
			"title": "Wedding Gifts",
			"subtitle": "Elegant fragrances for newlyweds",
			"category": "By Occasion",
			"description": "For a wedding gift, choose a scent that feels like a keepsake: ceremonial at first spray, then lasting long after the vows.",
			"products": [
				{
					"handle": "mathilde",
					"gift_description": "Mathilde suits a newlywed gift when you want the bouquet to feel celebratory and abundant, with jasmine, narcissus and ylang-ylang lifted by passion fruit and papaya. Its iris and sandalwood keep that floral radiance elegant enough for a first chapter together."
				},
				{
					"handle": "musc-oli",
					"gift_description": "Musc Oli is ideal if you’re gifting something intimate for the couple’s new life at close range, because it reads like skin made ceremonial. White musk, Cashmeran and sandalwood, touched with damask rose, make it a quiet signature they can wear as a private keepsake."
				},
				{
					"handle": "concrete",
					"gift_description": "Concrete fits this collection by turning a traditionally hard-edged idea into something unexpectedly tender and enduring, much like marriage itself. Clean sandalwood, metallic rose oxide and warm spices give it a modern, memorable structure for a couple who likes contrast."
				},
				{
					"handle": "joking-aside",
					"gift_description": "Joking Aside works beautifully as a wedding gift because its sweetness feels festive without becoming frivolous, making it easy to imagine on a honeymoon or anniversary-to-come. Jasmine, orange blossom and icing-sugar softness are sharpened by rose, spice and a sandalwood-tonka base that lingers with grace."
				},
				{
					"handle": "bo-bo",
					"gift_description": "Bo-Bo suits a newlywed present when you want something bright, easy to love and still polished enough to mark the occasion. Mandarin, lily of the valley and orange blossom keep it airy and joyful, while musk and dry vetiver give the gift a clean, lasting finish."
				},
				{
					"handle": "iris-altesse",
					"gift_description": "Iris Altesse is a strong choice for wedding gifting because it feels like a refined heirloom with a contemporary twist, perfect for a couple starting a new chapter in style. Chalky iris, clean leather and orange blossom are lifted by tuberose and cassis, making it both poised and distinctive."
				},
				{
					"handle": "malli-nadu",
					"gift_description": "Malli Nadu belongs in this collection for its sense of ceremony and romance, with white florals that feel lush enough for a wedding day and beyond. Jasmine, tuberose and mandarin are brightened by davana, then softened by sandalwood and vetiver into something luminous and enduring."
				},
				{
					"handle": "dancing-light",
					"gift_description": "Dancing Light makes a thoughtful wedding gift when you want freshness to stand for a clean beginning, yet still leave a gentle trace. Icy mint, Siberian pine and jasmine are bright and airy, while neroli, syringa and sandalwood give the scent a calm, keepsake-worthy warmth."
				},
				{
					"handle": "ruby-wood",
					"gift_description": "Ruby Wood fits the theme by offering a deeper, more grounded expression of commitment, ideal for a couple who wants something with presence and memory. Rose, frankincense and balsamic resins meet red-berry sweetness and coffee for a rich trail that feels lasting and ceremonial."
				}
			]
		},
		"graduation-gifts": {
			"title": "Graduation Gifts",
			"subtitle": "A grown-up first fragrance for a new chapter",
			"category": "By Occasion",
			"description": "A rite-of-passage scent edit for marking the first grown-up perfume: polished enough for the ceremony, distinctive enough for the next chapter.",
			"products": [
				{
					"handle": "santal",
					"gift_description": "Choose Santal when you want the gift to feel like a milestone in itself: it suits a first serious fragrance because the citrus-lavender lift keeps it approachable, while pepper, cardamom and nutmeg give it a more assured stride. The cedar, vetiver and frankincense base reads mature without feeling stern."
				},
				{
					"handle": "marcel",
					"gift_description": "Marcel fits this collection because it turns classic cologne into something a new graduate can wear with confidence beyond the ceremony. Bergamot, rosemary and ylang-ylang keep it bright and tidy, while vetiver, cedar and patchouli add a dry, grown-up finish that feels like stepping into a new role."
				},
				{
					"handle": "lola-at-coat-check",
					"gift_description": "Lola at Coat Check is for the gift-giver who wants the first grown-up perfume to feel like a celebration after dark. Cardamom, grapefruit and orris make it immediately distinctive, and the white chocolate, sandalwood and amber give it a more intimate, slightly dressed-up character for someone entering a new chapter."
				},
				{
					"handle": "mad",
					"gift_description": "Mad belongs here because it makes vanilla feel like an achievement rather than a treat, which is exactly the point of a rite-of-passage scent. Bergamot, blackcurrant and buchu keep the opening vivid, while clove, vetiver and that smoky forested edge make it feel more composed than confectionary."
				},
				{
					"handle": "hakka-japanese-mint",
					"gift_description": "Hakka suits a graduation gift when you want something fresh, uncommon and quietly refined for a first step into adulthood. Japanese mint, jasmine and lotus create a cool, airy profile, and the white musk with cedarwood adds just enough warmth to keep it from feeling too brisk."
				},
				{
					"handle": "white-rice",
					"gift_description": "White Rice fits the theme by offering a soft, comforting signature for someone leaving one chapter and beginning another. The rice, pandan and orris combination is what makes it special, while jasmine and white musk give it a clean tenderness that feels personal rather than flashy."
				},
				{
					"handle": "figlia-di-eva",
					"gift_description": "Figlia di Eva works well as a first grown-up perfume because it balances brightness with shadow, much like a new beginning. Bergamot and green fruit keep it lively, while oud, tonka bean and white musk add depth and softness that make the fragrance feel more serious than a typical citrus."
				},
				{
					"handle": "paradoxial",
					"gift_description": "Paradoxical is a strong graduation gift when you want the wearer to feel ready for the next chapter without losing freshness. Citrus, cardamom and lavender keep it crisp, and incense, vetiver and cedar bring a darker, more self-possessed character that sets it apart from brighter picks."
				},
				{
					"handle": "match",
					"gift_description": "Match belongs in this collection because it has the understated confidence of a first fragrance chosen for adulthood, not adolescence. The suede, orris and sandalwood are its defining details, and the ambroxan sheen gives it a clean, skin-close finish that feels quietly intentional."
				}
			]
		},
		"for-your-partner": {
			"title": "For Your Partner",
			"subtitle": "Intimate, sensual scents for someone you love",
			"category": "By Relationship",
			"description": "A gift of closeness: scents that feel borrowed from skin, meant to be recognised in a hug, a doorway kiss, or the hush of an ordinary day together.",
			"products": [
				{
					"handle": "musc-oli",
					"gift_description": "Choose Musc Oli when you want a gift that feels like your partner’s own skin, only more luminous. White musk, Cashmeran and sandalwood make it ideal for everyday intimacy, with damask rose adding just enough tenderness for someone you love."
				},
				{
					"handle": "the-empress-kiss",
					"gift_description": "The Empress Kiss suits a partner who likes their closeness with a little drama, making it a strong choice for date nights and special evenings. Saffron, cardamom and clove give it a spiced edge, while rose, oud and caramel create a signature you’ll notice lingering on them."
				},
				{
					"handle": "pg0-4-le-musc-la-peau",
					"gift_description": "Le Musc & La Peau Extrait fits this collection because it reads like a second skin scent you can gift for daily wear and private moments alike. Rosemary, white musk and sandalwood give it a clean, warm character, while ylang-ylang and tonka soften it into something deeply familiar."
				},
				{
					"handle": "cash-flower",
					"gift_description": "Cash Flower belongs here for the partner who likes a rose that feels personal rather than formal, perfect as a romantic gift that still works every day. Pepper, cashmere and amber give the scent a plush warmth, while musk and sandalwood keep the floral heart close and wearable."
				},
				{
					"handle": "yawahada",
					"gift_description": "Yawahada is an easy choice when you want a fragrance that suggests the softness of skin itself, especially for someone who prefers quiet affection over showiness. Pear, rice powder and rose bring a gentle, milky texture, and sandalwood, amber and musk leave a comforting trace on the wearer."
				},
				{
					"handle": "juice-personal",
					"gift_description": "Juice | Personal suits a partner who loves a playful, close-to-skin scent you can imagine catching on a scarf or collar. Raspberry and rhubarb give it a bright fruity spark, while rose, airy woods and musk keep it intimate rather than loud."
				},
				{
					"handle": "the-ghost-in-the-shell",
					"gift_description": "The Ghost In The Shell fits the theme for a partner drawn to something modern, but still tender enough to feel like shared space. Metallic yuzu, rain-fresh florals and a milky skin accord make it distinctive, with jasmine and soft woods adding a cool, intimate finish."
				},
				{
					"handle": "ta-if",
					"gift_description": "Ta’if is the gift for a partner who wears rose with confidence and likes a scent that feels like evening plans and lingering conversation. Pink pepper, saffron and dates give it a radiant, spiced richness, while amber grounds the floral brightness in warmth."
				},
				{
					"handle": "ylang-ylang-nosy-be",
					"gift_description": "Ylang Ylang Nosy Be belongs in this collection because it offers a lush floral embrace that still feels personal on skin, ideal for romantic gifting. Ylang-ylang, jasmine and vanilla give it a creamy glow, while its fresh lift and gentle woods keep it close, not cloying."
				}
			]
		},
		"for-your-best-friend": {
			"title": "For Your Best Friend",
			"subtitle": "Fun, bold, personality-packed gifts",
			"category": "By Relationship",
			"description": "A gift collection for the friend who gets the joke, loves the drama, and deserves a scent as lively as your bond.",
			"products": [
				{
					"handle": "pear-pavlova",
					"gift_description": "Choose Pear Pavlova when your friend is the one who loves a sweet surprise with a little bite; juicy pear and airy meringue make it instantly cheerful, while pink pepper and bitter almond keep it from feeling too obvious. The jasmine sambac and ambrette musk give it enough depth for a birthday brunch or a just-because gift that says you know their style."
				},
				{
					"handle": "fantomas",
					"gift_description": "Fantomas suits the friend with a mischievous streak, because its melon sweetness is deliberately twisted by metallic edges and a cool synthetic shimmer. Cashmeran’s woody-musky thickness makes it the right inside-joke gift for someone who likes being delighted and slightly unsettled at once."
				},
				{
					"handle": "bubble-tea",
					"gift_description": "Bubble Tea is the perfect present for the friend who turns every café run into a hangout, since its tea-and-milk comfort feels instantly familiar and shareable. Roasted black tea, brown sugar, and tapioca-like softness make it distinct from the sweeter picks here, with a honeyed floral nuance that keeps it polished enough for everyday wear."
				},
				{
					"handle": "santorini",
					"gift_description": "Santorini fits the friend who is all easy charm and sunlit spontaneity, because it feels like gifting them a weekend mood rather than just a perfume. Green fig, coconut milk, and orris give it a breezy, understated sweetness that stands apart from the louder gourmands in this set."
				},
				{
					"handle": "still-life-in-rio",
					"gift_description": "Still Life in Rio works for the friend who brings the spark to every plan, since its zesty citrus-and-ginger opening feels like a toast in fragrance form. Yuzu, coconut, and rum give it a tropical lift with enough woody copaiba depth to feel thoughtful, not generic."
				},
				{
					"handle": "kolada",
					"gift_description": "Kolada is the choice for the friend who loves a playful twist on a classic, because it takes piña colada joy and gives it a sharper, more modern attitude. Pineapple, metallic lily-of-the-valley, and creamy coconut milk make it the most extroverted option here, ideal for a celebration gift that should stand out."
				},
				{
					"handle": "meek-passion",
					"gift_description": "Meek Passion suits the friend who laughs first and reveals their depth later, since the banana-and-pineapple brightness quickly gives way to something darker and more grounded. Cinnamon, boozy sweetness, and earthy patchouli make it a great pick when you want the gift to feel fun but not frivolous."
				},
				{
					"handle": "pink-sweats",
					"gift_description": "Pink Sweats is for the friend who is effortlessly cool in a way only you notice, because its rhubarb, lychee, and rosé Champagne sparkle feels like a private wink. Marshmallow and white musk soften the edges without turning sugary, making it a lovely gift for a close birthday or a night-out surprise."
				},
				{
					"handle": "solar-wind",
					"gift_description": "Solar Wind fits the friend who always has a fresh idea and a bold opinion, since its tart-sweet profile feels lively without being predictable. Rhubarb, guava, and strawberry are brightened by elemi’s resinous snap, so it lands as the most vivid, conversation-starting choice in the collection."
				}
			]
		},
		"for-mum": {
			"title": "For Mum",
			"subtitle": "Elegant, warm fragrances she'll treasure",
			"category": "By Relationship",
			"description": "A tender edit of warm florals and creamy woods chosen to make mum feel cherished, feminine, and quietly spoiled.",
			"products": [
				{
					"handle": "mathilde",
					"gift_description": "Mathilde is a lovely pick when you want to honour mum with something graceful and richly affectionate. Jasmine, narcissus and ylang-ylang bloom over passion fruit and papaya, while iris and sandalwood give it a plush, composed softness that feels made for a special occasion."
				},
				{
					"handle": "cherry-passion",
					"gift_description": "Cherry Passion suits a mum who likes her gift to feel joyful and affectionate, with ripe cherry, strawberry and sweet almond giving a bright first impression. Jasmine, rose and tuberose make it feel especially feminine, while sandalwood and vanilla add the warm embrace that says thank you."
				},
				{
					"handle": "32-c",
					"gift_description": "32°C fits this collection because it pampers with the scent of sun-warmed skin and a creamy, flattering floral trail. Jasmine sambac, tiare and coconut are its most distinctive comforts, lifted by white tea and ginger so it feels fresh, tender and easy to wear for a mother who deserves to feel looked after."
				},
				{
					"handle": "divina-flora",
					"gift_description": "Divina Flora belongs here for a mum who enjoys elegance with a luminous, garden-fresh lift. Lemongrass and passion fruit brighten peony, jasmine and narcissus, while herbal greens and vanilla keep the bouquet feeling polished, airy and beautifully considered as a gift."
				},
				{
					"handle": "concrete",
					"gift_description": "Concrete works for this theme by giving mum something modern, but still soft and wearable enough to feel like a thoughtful present. Metallic rose oxide, warm spices and dense sandalwood make it distinctive, and that contrast of cool structure with a gentle finish feels quietly luxurious."
				},
				{
					"handle": "jasmin-ylang",
					"gift_description": "Jasmin & Ylang is an easy choice when you want to give mum a fragrance that feels sunny, comforting and unmistakably feminine. Jasmine, ylang-ylang and frangipani create the heart of the gift, while vanilla and benzoin wrap it in a soft, caring warmth that suits birthdays or Mother's Day."
				},
				{
					"handle": "koiame",
					"gift_description": "恋雨 Koiame fits because it captures the tenderness of being seen, with a fresh floral sweetness that feels like a loving gesture. Mandarin, cassis and apple sparkle at the start, then magnolia, jasmine and water lily soften into peach and musk for a clean, heartfelt finish."
				},
				{
					"handle": "suze",
					"gift_description": "Suze is for the mum who appreciates something a little more distinctive, yet still warm and gracious. Spearmint, clementine and black tea give it a cool-bright opening, while orris, benzoin and fossilized amber add a smoky, nostalgic depth that feels like a treasured gift."
				},
				{
					"handle": "this-is-not-a-blue-bottle-1-7",
					"gift_description": "This is Not a Blue Bottle 1/.7 – Happiness suits the collection because it offers mum a creamy, sunlit comfort that feels instantly pampering. Coconut, frangipani and milky sandalwood make it stand apart, and the sun-lotion accord gives it that relaxed, affectionate holiday feeling you’d choose to make her feel special."
				}
			]
		},
		"for-dad": {
			"title": "For Dad",
			"subtitle": "Refined, characterful scents he'll actually wear",
			"category": "By Relationship",
			"description": "A quietly elevated edit for the man who values character over showiness, with scents that feel like a thoughtful nod to his steadiness and style.",
			"products": [
				{
					"handle": "santal",
					"gift_description": "Santal suits this collection because it gives dad a dependable, well-cut sandalwood with just enough distinction to feel like a gift, not a safe fallback. The citrus lift, peppery cardamom and frankincense-warmed cedar keep it refined for daily wear or a dinner out."
				},
				{
					"handle": "smokin-gun",
					"gift_description": "SMOKIN' GUN fits the brief for the dad who likes something a little more individual, but still easy to wear with a jacket and boots. Peated whisky, juniper and incense give it a smoky, intimate edge, while cedar and oak add the kind of grounded depth that feels quietly luxurious."
				},
				{
					"handle": "marcel",
					"gift_description": "Marcel belongs here because it takes classic cologne territory and gives it a more mature, thoughtful finish that suits a dad who appreciates understatement. Bergamot, rosemary and ylang-ylang keep it bright, while vetiver, cedar and patchouli add a dry, composed backbone."
				},
				{
					"handle": "bohemian",
					"gift_description": "Bohemian is for the dad who carries a bit of grit well, making it a strong gift when you want something characterful rather than predictable. Its leather, oud and incense are sharpened by white pepper, with vetiver and patchouli giving it a rugged, worn-in feel."
				},
				{
					"handle": "zelen",
					"gift_description": "Zelen fits this collection by offering a fresher, more elegant take on vetiver for the dad who prefers clarity over heaviness. Lime, bergamot and pink pepper keep it vivid, while beeswax, cedar and Mysore sandalwood add a smooth, natural richness he may not buy for himself."
				},
				{
					"handle": "fauno",
					"gift_description": "Fauno works here because it feels like a more dramatic gift for a father with presence, especially if you want something for evenings or special occasions. Tobacco, frankincense and leather bring depth, while juniper and clary sage add an aromatic lift that stops it from feeling too heavy."
				},
				{
					"handle": "st-pauls-1",
					"gift_description": "St. Paul's suits the collection as the most dressed-up option, ideal if you want to give dad something with a little old-world ceremony. Orange blossom and jasmine brighten the opening, then labdanum, frankincense and dark woods give it a rich, baroque warmth."
				},
				{
					"handle": "sand",
					"gift_description": "Sand belongs in this lineup because it turns sandalwood into a conversation piece without losing its easy wearability, perfect for a dad who likes subtle surprises. Banana nuance, cardamom and smoky incense make it playful, while benzoin and labdanum keep the finish resinous and polished."
				},
				{
					"handle": "black",
					"gift_description": "Black fits the theme by offering a tougher, more practical kind of refinement that still feels gift-worthy. Black pepper, incense and birch tar create a smoky core, while leather, liquorice and dry woods give it the sort of rugged comfort that suits weekends and casual evenings."
				}
			]
		},
		"treat-yourself": {
			"title": "Treat Yourself",
			"subtitle": "Self-gifting — because you deserve it",
			"category": "By Relationship",
			"description": "A little luxury for the person who’s earned the right to linger a bit longer, spend a bit more, and wear joy like a private reward.",
			"products": [
				{
					"handle": "lamar",
					"gift_description": "Lamar suits the self-gift that says “go on, spoil yourself,” because its pineapple, apple, and berries feel instantly celebratory. Rose, jasmine, and magnolia give the bouquet a bright lift, while amber, vanilla, and musk make it linger like a well-deserved treat after a long week."
				},
				{
					"handle": "rouge-avignon",
					"gift_description": "Rouge Avignon is the choice when the gift is meant to feel a touch dramatic and utterly earned, with rose, raspberry, and ylang-ylang creating a lush opening. Cocoa and black truffle add a moody gourmand twist, and sandalwood, vetiver, and amber give it a smoky finish that feels perfect for evening me-time."
				},
				{
					"handle": "cash-flower",
					"gift_description": "Cash Flower fits this collection because it turns self-reward into something unmistakably luxe, with rose at the center and pepper adding a lively spark. Cashmere, amber, musk, and sandalwood give it a plush, confident drydown that feels like the fragrance equivalent of buying the nicer version for yourself."
				},
				{
					"handle": "davana-vanille",
					"gift_description": "Davana & Vanille belongs here as an easy indulgence for the person who wants a sweet pick-me-up without overdoing it, thanks to davana, lychee, and vanilla. Sandalwood and tonka keep it soft and cozy, making it a thoughtful self-gift for daytime wear, coffee runs, or a small personal celebration."
				},
				{
					"handle": "guava-sorbet",
					"gift_description": "Guava Sorbet is ideal when the present is simply permission to enjoy something bright and playful, with guava, mandarin, and passionfruit delivering a vivid tropical hit. Osmanthus, jasmine, and vanilla smooth it into a creamy floral finish that feels like a sunny reward for yourself."
				},
				{
					"handle": "vanille-glace",
					"gift_description": "Vanille Glacé is the splurge for vanilla lovers who want their me-time to feel rich and comforting, not plain. Brown sugar syrup, cinnamon, and toasted madeleine give it pastry warmth, while espresso, sandalwood, and tonka add depth that makes it feel like a deliberate, delicious treat."
				},
				{
					"handle": "tardes",
					"gift_description": "TARDES works beautifully as a self-gift for slow afternoons and restorative moments, because its almond, heliotrope, and rose create a calm, tender aura. Cedarwood, tonka bean, and musk keep it quietly sensual, making it the kind of fragrance you choose when you want to feel wrapped in your own time."
				},
				{
					"handle": "vanhera",
					"gift_description": "Vanhera fits this theme by giving vanilla a darker, more grown-up edge, perfect for the buyer who wants their reward to feel a little more intriguing. Madagascar vanilla absolute is sharpened by pepper, cardamom, and cinnamon, then grounded by sandalwood and amber for a warm, textured finish."
				},
				{
					"handle": "106",
					"gift_description": "106 Chromatic Rose is the self-treat for someone who wants a rose that feels elegant but not expected, with Damascena rose, pink pepper, and davana setting the tone. Vanilla and sandalwood soften the edges, so the gift feels polished for the wearer while still leaving room for a personal, indulgent moment."
				}
			]
		},
		"quiet-luxury": {
			"title": "Quiet Luxury",
			"subtitle": "Understated elegance, whispered opulence",
			"category": "Editorial",
			"description": "A study in restraint: materials so fine they seem to whisper, each fragrance tracing opulence without ever raising its voice.",
			"products": [
				{
					"handle": "cashmere-100",
					"gift_description": "Cashmere 100 belongs here because its orris, white musk, and sandalwood read like fabric rather than flourish. The honeysuckle lift and humid mangrove wood give it a tailored warmth that feels discreetly expensive."
				},
				{
					"handle": "lumire-blanche",
					"gift_description": "Lumiere Blanche fits the theme through its cool cardamom, almond-milk creaminess, and creamy sandalwood that suggest refinement without spectacle. Tonka bean softens the edges, making the whole composition feel like a hushed room rather than a statement."
				},
				{
					"handle": "white-rice",
					"gift_description": "White Rice earns its place by turning an everyday grain into a near-silent luxury, with nutty rice, pandan, and white musk doing the work of elegance. Powdery orris and gentle jasmine keep it clean, intimate, and slightly savory in a way that feels considered."
				},
				{
					"handle": "rose-shiso",
					"gift_description": "Rose Shiso belongs in Quiet Luxury because it tempers rose's richness with the crisp, herbal bite of shiso instead of amplifying it. Iris, sandalwood, and cedar give the floral heart a disciplined frame, making the scent feel composed rather than ornate."
				},
				{
					"handle": "flow",
					"gift_description": "Flow suits this collection by favoring transparency over impact, using lily of the valley, green herbs, and ambergris to create poise in motion. Sandalwood and cedar stay in the background, giving the fragrance structure while keeping its presence light."
				},
				{
					"handle": "match",
					"gift_description": "Match fits because its suede, orris, and musky sandalwood create a refined skin-close texture instead of a loud trail. The dry, cardboard-like nuance and ambroxan add an austere, modern edge that feels deliberately understated."
				},
				{
					"handle": "albatros",
					"gift_description": "Albatros belongs here for its sea-salt clarity, cedarwood, and vetiver, which evoke luxury through air and space rather than richness. The aquatic foam, modern rose accord, and soft musk keep it luminous, spare, and quietly anchored."
				},
				{
					"handle": "eastern-veil",
					"gift_description": "Eastern Veil fits the theme by making sweetness feel restrained: salty popcorn and maple are softened by iris, cedarwood, and oakmoss. A thread of spice keeps it intimate and textured, so the warmth never turns showy."
				},
				{
					"handle": "oblivion",
					"gift_description": "Oblivion earns its place with bergamot and yuzu that feel crisp, not flashy, then lavender, sandalwood, and cedar that settle into composed calm. The violet-like orris veil adds a powdery finish, giving it an old-world restraint suited to quiet luxury."
				}
			]
		},
		"clean-girl-aesthetic": {
			"title": "Clean Girl Aesthetic",
			"subtitle": "Fresh, soapy, just-showered scents",
			"category": "Editorial",
			"description": "A study in bare skin and fresh linen: these scents read as just-showered, quietly bright, and impeccably unforced.",
			"products": [
				{
					"handle": "the-ghost-in-the-shell",
					"gift_description": "It fits this collection by turning clean into something futuristic: metallic yuzu, rain-washed florals, and peary brightness make the freshness feel newly rinsed rather than sweet. The milky skin accord keeps the clarity close and personal."
				},
				{
					"handle": "pg-41-le-musc-la-peau",
					"gift_description": "This belongs here because the whole idea is skin made cleaner, with seven musks creating a soft, powdery halo that feels freshly washed and close to the body. Ylang-ylang, tonka bean, and cedar add subtle texture without disturbing the airy effect."
				},
				{
					"handle": "coton-egyptien",
					"gift_description": "It captures the theme through its laundry-fresh cotton impression, built from airy white musks, green galbanum, and a whisper of floral lift. Transparent woods give it the crisp structure of clean sheets rather than a heavy fabric scent."
				},
				{
					"handle": "flow",
					"gift_description": "Flow suits the collection because it suggests a minimalist clean-girl wardrobe in scent form: lily of the valley, green herbs, and soft ambergris keep it light and effortless. Sandalwood and cedar add a neat woody frame that stays understated."
				},
				{
					"handle": "sun-bleached",
					"gift_description": "This fits by evoking sun-dried cotton and clean skin in one bright sweep, where citrus and ginger make the opening feel freshly scrubbed. Ylang-ylang, jasmine, and ambroxan smooth the edges into a breezy, polished freshness."
				},
				{
					"handle": "this-is-not-a-blue-bottle-1-5",
					"gift_description": "It earns its place through an airy, almost architectural cleanliness: aldehydes, ozonic florals, and mineral facets make it feel like crisp air after a shower. White musk and ambroxan soften the synthetic shine into a warm, skin-close trail."
				},
				{
					"handle": "bo-bo",
					"gift_description": "Bo-Bo belongs in this lineup because mandarin, lily of the valley, and orange blossom create a bright, neat freshness that reads as tidy and relaxed. Musk and dry vetiver keep it from turning sugary, giving the citrus-floral core a clean finish."
				},
				{
					"handle": "hakka-japanese-mint",
					"gift_description": "This one fits the theme with a cool, face-splash clarity: Japanese mint, jasmine, and lotus make the freshness feel brisk and water-like. White musk and cedarwood temper the chill so it lands as clean rather than sharp."
				},
				{
					"handle": "vetiver-santal",
					"gift_description": "It anchors the collection by pairing bergamot with vetiver and sandalwood, creating a freshly showered dryness that feels refined and natural. Benzoin and white musk blur the edges just enough to leave a soft, clean-skin trace."
				}
			]
		},
		"dark-academia": {
			"title": "Dark Academia",
			"subtitle": "Old libraries, leather-bound books, tweed",
			"category": "Editorial",
			"description": "A study in soot, paper, and worn leather, this selection reads like a midnight shelf in a candlelit library.",
			"products": [
				{
					"handle": "black",
					"gift_description": "BLACK fits Dark Academia through its scorched, ash-dark profile: black pepper, incense, and birch tar conjure smoke-stained stacks and a coat left by the fire. Leather, liquorice, and dry woods give it the feel of a battered satchel and old bindings."
				},
				{
					"handle": "honey-and-tar",
					"gift_description": "Honey and Tar belongs here because it turns library gloom into something tactile and lived-in, with honeyed sweetness softening birch tar and smoky leather. Vanilla, resinous incense, and dark woods make it feel like waxed shelves warmed by a hidden lamp."
				},
				{
					"handle": "bohemian",
					"gift_description": "Bohemian earns its place by leaning into the rough, scholarly side of the theme: earthy oud, incense, and smoke suggest a dim archive with cracked spines and dust in the air. White pepper, vetiver, and patchouli add a stern, textured dryness that suits tweed and worn boots."
				},
				{
					"handle": "coeur-de-noir-1",
					"gift_description": "Cœur de Noir fits the collection most directly, with black ink and dry leather-bound paper making the bookish reference explicit. Spiced rum, birch tar, and vanilla tobacco add a shadowed warmth that feels like annotations in the margins of a forbidden volume."
				},
				{
					"handle": "mora",
					"gift_description": "Mora suits Dark Academia by bringing the cold edge of a winter courtyard into the library, with grapefruit, pepper, and aromatic herbs cutting through the gloom. Hemp, damp vetiver, fir, and incense give it a green, austere depth that contrasts the usual leather-and-smoke palette."
				},
				{
					"handle": "fauno",
					"gift_description": "Fauno belongs in this set because its torchlit, ancient mood recalls classical texts and dim stone corridors rather than modern polish. Smoky tobacco, frankincense, and supple leather are lifted by juniper and clary sage, giving it a dry, herbal gravitas."
				},
				{
					"handle": "smokin-gun",
					"gift_description": "SMOKIN' GUN fits the theme through its campfire-in-the-woods atmosphere, like a late study break outside a secluded college. Peated whisky, juniper, and incense bring a boozy smoke, while cedar, oak, and sandalwood add the grain of old furniture and paneling."
				},
				{
					"handle": "all-black",
					"gift_description": "All Black works for Dark Academia by pairing tobacco and incense with a restrained, shadowy leather accord that feels suitably severe. Black pepper, cinnamon, rosemary, and dark woods give it the clipped, stern character of a well-thumbed essay and a tailored wool coat."
				},
				{
					"handle": "beso-de-fuego",
					"gift_description": "Beso de Fuego fits because its incense-and-wood structure feels nocturnal and arcane, like a lecture hall after dark. Jasmine sambac, cardamom, black pepper, cedarwood, and cypress keep it smoky yet lifted, with a tense brightness that distinguishes it from the heavier leathers."
				}
			]
		},
		"main-character-energy": {
			"title": "Main Character Energy",
			"subtitle": "Bold, confident, impossible to ignore",
			"category": "Editorial",
			"description": "These are the scents that enter the room before you do: vivid, assertive, and impossible to overlook.",
			"products": [
				{
					"handle": "au-pied-du-rosier",
					"gift_description": "Au Pied du Rosier belongs here because it turns rose into a power move, lacing honeyed Grasse petals with new-trainer rubber and saffron bite. Oud gives the bloom a darker spine, so the contrast feels fearless rather than pretty."
				},
				{
					"handle": "eye-contact",
					"gift_description": "Eye Contact fits the theme by capturing the charge of being noticed, with pink pepper and saffron flashing over supple leather. Oud, black amber and musk keep the heat close to the skin, making it feel like a glance that lands and stays."
				},
				{
					"handle": "agrani",
					"gift_description": "Agrani earns its place through sheer presence: cherry and almond open with a lush, almost cinematic richness before pipe tobacco and leather take over. Cacao, castoreum and Burmese oud add a vintage, animalic depth that refuses to fade into the background."
				},
				{
					"handle": "nuit-de-baklite",
					"gift_description": "Nuit de Bakélite is main-character material because it rejects softness and makes green tuberose feel sharp, thorny and self-possessed. Galbanum, carrot seed and cardamom cut through the floral core, while leather adds a dry, uncompromising edge."
				},
				{
					"handle": "skin-fetish",
					"gift_description": "Skin Fetish fits this collection by pairing juicy dark cherry with smoky incense, creating a contrast that feels both alluring and a little dangerous. Rose, oud and warm amber keep the fragrance in commanding territory rather than simple sweetness."
				},
				{
					"handle": "cavallo-di-fuoco",
					"gift_description": "火马 - Cavallo di fuoco belongs here for its wild, off-kilter energy: mint, celery and pomelo make an immediate statement before ginger and pepper sharpen the pace. Honeyed Chinese oud and osmanthus ground the brightness in smoky leatheriness, giving it real authority."
				},
				{
					"handle": "cuirs",
					"gift_description": "CUIRS suits the theme because it reads like someone who knows exactly what they want, with smoky leather at the center and saffron and oud adding flash. Cedar, guaiac and labdanum build a dry, resinous frame that feels sturdy and self-assured."
				},
				{
					"handle": "kamel-oud",
					"gift_description": "Kamel Oud fits Main Character Energy by combining raspberry and rose with cumin and smoky oud, a blend that is both plush and confrontational. Cardamom, saffron and resinous woods give it a heated, unmistakable trail."
				},
				{
					"handle": "styrax",
					"gift_description": "STYRAX closes the set with raw force, built around animalic amber that feels untamed from the first breath. Patchouli, labdanum, saffron and vetiver create a leathery, feral depth that makes it impossible to ignore."
				}
			]
		},
		"cottagecore": {
			"title": "Cottagecore",
			"subtitle": "Wildflowers, herbs, honey and meadow warmth",
			"category": "Editorial",
			"description": "A walk through hedgerows and herb beds, where petals, leaves and warm earth keep the air softly alive.",
			"products": [
				{
					"handle": "dangerosa",
					"gift_description": "Dangerosa belongs here because its rose is not manicured but gathered from the wild edge of the garden, sharpened by bitter artemisia and peppery elemi. Metallic geranium and incense give it the bramble-thorn tension that keeps cottage blooms from feeling too neat."
				},
				{
					"handle": "ara",
					"gift_description": "ARC fits this theme through its old-world herbarium feel: holy basil, thyme, lavender and pine read like a basket of dried stems brought in from the meadow. Oakmoss, styrax and benzoin add smoky sweetness, making it feel rooted in both kitchen garden and woodland verge."
				},
				{
					"handle": "divina-flora",
					"gift_description": "Divina Flora suits cottagecore by turning floral abundance into a sunlit herb patch, where lemongrass and passion fruit flicker around peony, jasmine and narcissus. Coniferous greens and aromatic herbs keep the bouquet from becoming ornate, while sandalwood and vanilla leave a soft, pastoral warmth."
				},
				{
					"handle": "pg16-jardins-de-kerylos",
					"gift_description": "PG16 Jardins de Kérylos earns its place with green fig and white peach, fruits that feel picked beside a shaded garden path rather than arranged indoors. Sycamore wood, tuberose and musk create a leafy hush that matches the theme’s quiet, cultivated countryside mood."
				},
				{
					"handle": "rusalka-s-embrace",
					"gift_description": "Rusalka’s Embrace is the collection’s shadowed outlier, but it still fits through its submerged reeds-and-herbs atmosphere: seaweed, peppermint and pine suggest a wet meadow after rain. Propolis and ambergris add a raw, vegetal depth that recalls marshland rather than open sea."
				},
				{
					"handle": "debonaire",
					"gift_description": "Debonaire belongs in cottagecore for its easy blend of lemon, neroli, lavender and rosemary, like crushed sprigs on a wooden table. Sandalwood and vetiver bring a gentle, sun-dried finish that feels more herb garden than city cologne."
				},
				{
					"handle": "immortali-santi",
					"gift_description": "Immortali Santi fits by taking white florals into bright country air: bergamot, blood orange and Champagne lift tuberose, jasmine and honeysuckle into a sparkling bloom. Cashmeran and ambroxan keep the composition airy and clean, like petals warmed by afternoon light."
				},
				{
					"handle": "talento",
					"gift_description": "Talento suits the theme with its young green rose, mint and aldehydes, which feel like a dewy stem just cut from the border. The woody base gives it a protective, talismanic calm, as if the flower were pressed between pages of a field guide."
				},
				{
					"handle": "nerosa",
					"gift_description": "Nerosa sits at the darker end of cottagecore, but its rose, geranium and labdanum still read like a wild hedge at dusk. Saffron, oud and leather add a bristling, resinous depth that makes the floral feel foraged rather than tended."
				}
			]
		},
		"long-lasting-perfumes": {
			"title": "Long-Lasting Perfumes",
			"subtitle": "Beast-mode sillage that goes the distance",
			"category": "By Function",
			"description": "For those who prefer a scent to linger long after the room has changed, this edit favors bold formulas that cling to fabric, pulse on skin, and refuse to fade quietly.",
			"products": [
				{
					"handle": "al-sarab",
					"gift_description": "Al Sarab belongs here because its extrait concentration and dense oud structure are built for all-day wear, especially on clothes where its trail can keep unfolding. The mixed oud oils, aged Cambodian oud, and leathery animalic depth are brightened just enough by fruity nuances to keep the persistence from feeling flat."
				},
				{
					"handle": "hey-bitch",
					"gift_description": "Hey Bxtch fits this collection by turning a sharp opening into a long-haul leather-floral that stays present for hours without needing a reset. Tart rhubarb, buttery orange blossom, and sueded iris give it a distinctive contrast, while milky sandalwood and vanilla help it settle into a lasting ambered shadow."
				},
				{
					"handle": "tiger-rouge",
					"gift_description": "Tiger Rouge earns its place through sheer projection and tenacity, with a spicy-leathery core that keeps announcing itself well into the day. Pepper, paprika, and clove make it hotter than the other picks, while blood orange, oud, and incense add a smoky brightness that lingers on fabric."
				},
				{
					"handle": "502-iris-cartagena",
					"gift_description": "502 Iris Cartagena suits this theme because its dry gourmand richness is the kind of profile that clings to skin and scarf alike. Rum, coffee, and cocoa create a deep edible trail, while iris, cedar, and papyrus keep it textured and persistent rather than sugary."
				},
				{
					"handle": "fathom-v-directors-cut",
					"gift_description": "Fathom V Director's Cut belongs in a long-lasting lineup because its amplified green intensity feels designed to remain vivid from first spray to drydown. Crushed stems, salt, and vetiver give it a bracing, outdoorsy grip, while cedar and darker spices help it hold its shape for hours."
				},
				{
					"handle": "le-mat",
					"gift_description": "Le Mat fits this collection by pairing floral elegance with earthy heft, a combination that tends to stay woven into fabric and skin all day. Grasse rose, patchouli, immortelle, and dark spices give it a restless backbone, while balsamic warmth keeps the trail alive long after the opening fades."
				},
				{
					"handle": "devoti-peccatori",
					"gift_description": "Devoti Peccatori matches the brief with a thick, animalic composition that refuses to disappear quickly, especially once it settles into leather and ambergris. Dark cocoa, syrupy caramel, cypriol, and orange make it more distinctive than a standard leather scent, and the sandalwood-tonka-vanilla base helps it endure."
				},
				{
					"handle": "lumieres",
					"gift_description": "Lumières fits because its mineral-woody structure is the sort that can sit close yet persist for hours, especially on clothing. Pink pepper, juniper, concrete-like minerals, and white musk make it cleaner and sharper than the others here, while iris and cedar leave a powdery afterglow."
				},
				{
					"handle": "st-pauls-1",
					"gift_description": "St. Paul's belongs in this edit for its grand, resinous staying power: a floral amber that keeps radiating long after the first spray. Orange blossom, jasmine, labdanum, and frankincense give it a rich, architectural presence, while dark woods and dried fruits make the finish last with authority."
				}
			]
		},
		"best-for-the-office": {
			"title": "Best for the Office",
			"subtitle": "Professional, inoffensive, quietly impressive",
			"category": "By Function",
			"description": "A collection of scents that keep a clear desk and a clearer impression: composed, discreet, and quietly individual.",
			"products": [
				{
					"handle": "vetiver-santal",
					"gift_description": "This fits the office because bergamot and Haitian vetiver stay crisp and composed, while New Caledonian sandalwood and white musk keep the edges smooth. Benzoin adds just enough warmth to feel finished, not forceful."
				},
				{
					"handle": "rainy-city",
					"gift_description": "It belongs here for its rain-washed restraint: black tea and green tea read calm and focused, while vetiver and transparent woods avoid any heavy trail. The Earl Grey steam effect gives it personality without raising its voice."
				},
				{
					"handle": "albatros",
					"gift_description": "Sea salt, cedarwood, and vetiver make this a clean, airy choice that feels polished in close quarters. The marine foam, modern rose, and soft musk give it distinction while keeping the profile light and controlled."
				},
				{
					"handle": "beso-pasion",
					"gift_description": "This works in the office because citrus and ginger bring lift, while fig and green foliage add a natural, tidy green character. Warm woods, amber, and musk round it out so it feels personable rather than distracting."
				},
				{
					"handle": "bo-bo",
					"gift_description": "Mandarin, lily of the valley, and orange blossom make this bright and easy to wear in professional spaces. The dry vetiver and musk keep the floral heart neat and tailored, so it reads fresh instead of sweet."
				},
				{
					"handle": "602",
					"gift_description": "Its dry cedar, leafy patchouli, and dusty pepper suit a setting that rewards understatement. Vetiver and warm spice add depth and texture, giving it a competent, wood-led presence that never crowds the room."
				},
				{
					"handle": "coton-egyptien",
					"gift_description": "This fits the theme through its clean-clothes effect: airy white musks and galbanum feel crisp, while delicate florals keep it from becoming stark. Transparent woods provide structure, making the whole composition quietly assured."
				},
				{
					"handle": "floating",
					"gift_description": "Peach, bergamot, and wild orchid give this a soft brightness that feels approachable rather than loud. Linen, sequoia wood, and airy musk make it office-friendly, with aldehydes adding a refined, freshly pressed finish."
				},
				{
					"handle": "pg191-neroli-ad-astra",
					"gift_description": "Neroli, mandarin, and green geranium create a lucid opening that feels neat and alert. Agave flower and vetiver lend an uncommon twist and a dry, powdery close, so it stays distinctive while remaining discreet."
				}
			]
		},
		"date-night-scents": {
			"title": "Date Night Scents",
			"subtitle": "Seductive, memorable, close-quarters magnetic",
			"category": "By Function",
			"description": "Dimmed-light fragrances with a pull that starts at arm’s length and lingers long after the conversation breaks.",
			"products": [
				{
					"handle": "castiamanti",
					"gift_description": "Castiamanti suits this theme because its saffron, incense and oud build a slow-burning aura that feels made for close-range intrigue. Myrtle, honeyed rose and labdanum keep it warm, resinous and just a little mysterious."
				},
				{
					"handle": "the-empress-kiss",
					"gift_description": "The Empress Kiss belongs here for its assertive, near-the-skin tension: saffron, cardamom and clove give it a spiced edge, while rose and oud keep the center of gravity seductive. Raspberry, caramel and musk add a teasing sweetness that reads beautifully in low light."
				},
				{
					"handle": "ta-if",
					"gift_description": "Ta’if fits the collection because it captures that dusk-time charge with radiant rose and a confident, intimate glow. Pink pepper, saffron and dates create a vivid contrast of sparkle, richness and plush depth that feels immediately magnetic."
				},
				{
					"handle": "drunk-saffron",
					"gift_description": "DRUNK SAFFRON earns its place through its boozy warmth and skin-close heat, the sort of scent that seems to shimmer in candlelight. Cognac, saffron and leather give it a boisterous, tactile pull, while patchouli and vanilla leave a lingering warmth."
				},
				{
					"handle": "agrani",
					"gift_description": "Agrani works for this theme because it leans into a darker, more commanding kind of attraction. Cherry, almond and saffron open with a sweet bite, then pipe tobacco, leather and Burmese oud turn it smoky, animalic and hard to ignore."
				},
				{
					"handle": "sirio",
					"gift_description": "Sirio fits the collection by pairing flirtatious brightness with a grounded, intimate base. Tart rhubarb, boozy fruit and rose give it a vivid first impression, while white musk, guaiac wood and oud draw it back toward the skin."
				},
				{
					"handle": "au-pied-du-rosier",
					"gift_description": "Au Pied du Rosier belongs here because it turns rose into something daring and close-up, not merely pretty. Honeyed Grasse rose, saffron and oud are sharpened by a rubbery, trainer-like leather note, giving it a bold, sensual contrast."
				},
				{
					"handle": "honey-oud",
					"gift_description": "Honey Oud suits this theme through its soft-focus sweetness and restrained warmth, ideal for the kind of presence that draws people in rather than shouts. English honey, vanilla and rose cushion the oud, while amber adds a smooth, glowing finish."
				},
				{
					"handle": "lawrence",
					"gift_description": "Lawrence fits the collection because its leathery, animalic core has the kind of nocturnal magnetism this theme calls for. Egyptian jasmine, saffron, cumin and coriander bring spice and tension, while musk, tolu balsam and oud leave a warm, textured trail."
				}
			]
		},
		"bedtime-fragrances": {
			"title": "Bedtime Fragrances",
			"subtitle": "Calming, cocooning scents for the pillow",
			"category": "By Function",
			"description": "A hush of powders, woods, and milky warmth designed to blur the edges of the day and settle gently into the sheets.",
			"products": [
				{
					"handle": "cashmere-100",
					"gift_description": "Cashmere 100 belongs here for its feather-soft, skin-close texture that feels made for the last moments before sleep. Orris, honeysuckle, and white musk keep it airy, while sandalwood and humid mangrove wood add a quiet warmth that lingers like bed linen after a long exhale."
				},
				{
					"handle": "vetiver-santal",
					"gift_description": "Vetiver Santal fits this collection because it winds down without going flat: the bergamot lift eases the transition from day to night, then dry Haitian vetiver and milky sandalwood create a calm, grounded finish. Benzoin and white musk give it the kind of understated comfort that suits a bedside ritual."
				},
				{
					"handle": "cloud-essence-28-08",
					"gift_description": "Cloud Essence 28.08 is a natural fit for bedtime because its milk-and-rice accord feels like a soft blanket of scent. Mimosa, white musk, and sandalwood keep the mood hushed, while the faint savory nuance makes it feel dreamlike rather than sweetly obvious."
				},
				{
					"handle": "lumire-blanche",
					"gift_description": "Lumière Blanche earns its place through contrast: cool cardamom wakes the senses just enough, then almond-milk, sandalwood, and tonka bean fold everything back into softness. That clean, translucent cocoon effect is exactly what you want when settling into a quiet night routine."
				},
				{
					"handle": "tonkade",
					"gift_description": "Tonkade suits this theme because it turns warmth into a slow, soothing drift rather than a loud gourmand statement. Vanilla and tonka are cushioned by cardamom, incense, and dry woods, while the candied-fruit and cedar facets give it a dim, bedtime glow."
				},
				{
					"handle": "yawahada",
					"gift_description": "Yawahada fits the collection for its tender, close-to-the-body character, like scent resting on warm skin after a bath. Pear, rice powder, rose, and jasmine soften into sandalwood, amber, and musk, giving it a calm, lightly sweet finish that belongs on the pillow."
				},
				{
					"handle": "502-iris-cartagena",
					"gift_description": "502 Iris Cartagena belongs here because it takes edible notes and mutes them into a nocturnal, powdery hush. Coffee, cocoa, and rum are tempered by iris and vanilla, while cedar, papyrus, and vetiver keep the composition dry enough to feel restful rather than sticky."
				},
				{
					"handle": "figlia-di-eva",
					"gift_description": "Figlia di Eva fits this bedtime selection by pairing a bright opening with a shadowed, soothing base, like the last light slipping out of a room. Bergamot and green fruit give it lift, while oud, tonka bean, and white musk soften the edges into a gentle, sleep-ready veil."
				},
				{
					"handle": "sadonaso",
					"gift_description": "Sadonaso is included for the way it makes comfort feel a little secretive, with coffee, tobacco, and tonka wrapped in sandalwood. The powdery softness is ideal for winding down, yet its bodily warmth keeps it from becoming merely cozy; it feels intimate, dim, and close."
				}
			]
		},
		"travel-friendly-fragrances": {
			"title": "Travel-Friendly Fragrances",
			"subtitle": "Compact sizes and versatile scents for packing",
			"category": "By Function",
			"description": "Small, versatile scents for the carry-on: a compact wardrobe that shifts effortlessly from transit to arrival.",
			"products": [
				{
					"handle": "packed-your-holiday-perfume-wardrobe-unisex",
					"gift_description": "This fits the collection by offering a whole travel-ready wardrobe in one compact set, so you can pack light without losing range. Cool lavender, coconut, salt air and a leathered dusk give you options from daytime wandering to evening plans."
				},
				{
					"handle": "niche-discovery-box",
					"gift_description": "Built for on-the-move sampling, this box suits the theme by giving you a TSA-friendly way to explore multiple directions without overpacking. Bergamot, frankincense and lavender make it easy to wear across climates, while cedarwood, hay and peppermint keep each scent distinct."
				},
				{
					"handle": "matcha-mist",
					"gift_description": "Its mist format makes it especially practical for travel, slipping easily into a bag for quick refreshes between train rides, meetings or check-ins. Roasted matcha, green basil and peppermint create a calm, compact scent profile that stays airy rather than heavy."
				},
				{
					"handle": "eau-de-the-gingembre",
					"gift_description": "This belongs here because it reads like an easy carry-on cologne: bright, unfussy and versatile enough for warm days in transit. Mandarin, lemongrass and ginger give it a crisp lift, while the floral heart and vetiver drydown keep it balanced on the road."
				},
				{
					"handle": "jawa-cologne",
					"gift_description": "A travel-friendly pick by design, it delivers a fresh, adaptable citrus-green style that works from humid arrivals to breezy departures. Lime, bergamot and wet-green facets stand out against vetiver, giving it a clean profile with more depth than a standard cologne."
				},
				{
					"handle": "exploration-kit",
					"gift_description": "The discovery-box format makes this a natural fit for anyone packing light and wanting options that cover different moods and destinations. Cedarwood, milk accord and vanilla give the set a soft core, while iso-e-super and sandalwood add airy woods that wear easily anywhere."
				},
				{
					"handle": "firenze",
					"gift_description": "This suits the theme because it offers a bright, portable cologne style that feels made for city breaks and warm-weather packing. Bergamot, bitter orange and lemon keep it vivid, while sandalwood and vetiver lend enough structure to travel beyond the first hour."
				},
				{
					"handle": "mister-bungalow",
					"gift_description": "It fits the collection with its easygoing, suitcase-friendly freshness that can move from beachside mornings to casual evenings. Lemongrass, sweet orange and spike lavender bring a brisk aromatic snap, while vetiver and patchouli anchor it without weighing it down."
				},
				{
					"handle": "flower-frost-19-12",
					"gift_description": "A compact, versatile floral like this belongs in a travel-focused edit because it balances freshness and comfort without becoming too delicate or too heavy. Sparkling citrus, cool ozonic notes and green facets make it easy to wear in different climates, while magnolia, lily of the valley and cedar give it a soft landing."
				}
			]
		},
		"made-in-britain": {
			"title": "Best of British",
			"subtitle": "The best of British niche perfumery",
			"category": "Inspired by Places",
			"description": "A walk through British perfumery’s many accents: coastal, aristocratic, pastoral and quietly eccentric.",
			"products": [
				{
					"handle": "cape-wrath",
					"gift_description": "Cape Wrath belongs here because it captures Britain at its most elemental: wind-scoured, saline and unsentimental. Angelica root, seaweed and oakmoss give it a stark maritime signature that feels unmistakably northern."
				},
				{
					"handle": "afternoon-tea",
					"gift_description": "Afternoon Tea fits this collection by turning a very British ritual into something fragrant and finely judged. Lemon, maté tea and champaca are sharpened by vetiver and a faint smoky dust, balancing ceremony with a touch of bite."
				},
				{
					"handle": "damask",
					"gift_description": "Damask earns its place by showing the British talent for taking classic florals and giving them a modern, airy twist. Rose, blackcurrant and pear are lifted by lemon, then edged with mineral amber and musk for a cool, refined finish."
				},
				{
					"handle": "mulberry-fig",
					"gift_description": "Mulberry Fig belongs in Best of British for its parkland elegance and its restraint. Green fig, cardamom and orris sit over cedarwood, sandalwood and amber, making the fruit feel leafier and more tailored than lush."
				},
				{
					"handle": "a-masquerade",
					"gift_description": "A Masquerade fits because British perfumery often excels at character and contrast, and this one plays both beautifully. Raspberry, tuberose and rum are darkened by leather and oud, while the chypre structure keeps the drama controlled."
				},
				{
					"handle": "tea-rock-n-roll",
					"gift_description": "Tea & Rock ‘n‘ Roll is a quintessentially British contradiction: proper tea with a rebellious streak. Bergamot tea, smoky maté and tonka give it a crisp, textured profile that feels both urbane and a little rough around the edges."
				},
				{
					"handle": "summer-street",
					"gift_description": "Summer Street belongs in this set for its bright, gardened optimism and its distinctly English sense of light. Blackcurrant, apricot jam and honeysuckle are warmed by benzoin, vanilla and honey, creating a sunlit floral-fruity haze."
				},
				{
					"handle": "monkeyflower",
					"gift_description": "Monkeyflower fits by evoking a nostalgic, almost imagined Britain of meadows, allotments and late summer skin. Chamomile, fig and hay are joined by a sunscreen accord and musk, making it gentle, herbal and oddly familiar."
				},
				{
					"handle": "arcadia-1",
					"gift_description": "Arcadia closes the collection with a more contemplative British mood: reserved, cerebral and quietly textural. Iris, olibanum and vetiver meet mineral earth and tonka, giving the fragrance a dry, woody poise with a savory undertone."
				}
			]
		},
		"french-perfumery": {
			"title": "French Style",
			"subtitle": "The art of French fragrance-making",
			"category": "Inspired by Places",
			"description": "French perfumery here moves between restraint and allure, where texture, clarity, and a knowing twist matter as much as beauty.",
			"products": [
				{
					"handle": "monsieur",
					"gift_description": "This fits French style through its disciplined structure: cedarwood, vetiver, and sandalwood are sharpened by mineral freshness, a very Parisian way of keeping depth precise. Oakmoss, papyrus, and wet-stone nuances give it a sober, elegant dryness rather than sheer weight."
				},
				{
					"handle": "patchouli-labdanum",
					"gift_description": "Patchouli and labdanum are handled with French finesse here, turning a potentially heavy duo into a smoky eau de cologne with composure. Leather-like warmth, ambered resin, and earthy patchouli make it feel grounded but never blunt."
				},
				{
					"handle": "entre-ciel-et-mer",
					"gift_description": "Its Frenchness lies in the marine theme being rendered with elegance rather than brute force: sea salt, algae, and ambergris are lifted by lavender, pear, and white thyme. Cedar and sandalwood dry the composition into something mineral, airy, and faintly animalic."
				},
				{
					"handle": "pg-111-indian-wood",
					"gift_description": "It belongs in this collection for the way it stages contrast with control: peppermint coolness, cardamom sparkle, and nutmeg warmth are set against sandalwood and vetiver. The result feels relaxed yet exacting, with oakmoss adding a classic French green finish."
				},
				{
					"handle": "302",
					"gift_description": "This is French style in its more hedonistic register, where coconut, amber, and cardamom are arranged with enough restraint to stay chic. Creamy sandalwood and resinous warmth keep the sweetness in check, giving the scent a polished, modern curve."
				},
				{
					"handle": "tea-rock-n-roll",
					"gift_description": "Tea & Rock ‘n’ Roll earns its place by pairing French perfume craft with a cultivated, offbeat attitude: bergamot tea, smoky maté, and tonka create a crisp but slightly unruly accord. The leathery edge and woody frame give it that effortless, city-smart nonchalance."
				},
				{
					"handle": "coton-egyptien",
					"gift_description": "Coton Egyptien fits this theme by translating cleanliness into a distinctly French idea of texture and understatement. White musks, galbanum, and soft floral accents are supported by transparent woods, making the scent feel immaculate yet quietly persistent."
				},
				{
					"handle": "intime-extime",
					"gift_description": "This is a perfect French-style composition because it turns intimacy into an aesthetic: iris, incense, and vanilla are softened by a milk accord and white tea, then grounded by Itauba wood. Powdery, tender, and slightly smoky, it wears like a whispered signature."
				},
				{
					"handle": "lumieres",
					"gift_description": "Lumières belongs here for its urban refinement: pink pepper and juniper flash against mineral facets and white musk, then iris and cedar smooth everything into a powdery glow. It captures that French balance of cool architecture and skin-level warmth."
				}
			]
		},
		"japanese-scents": {
			"title": "Japanese Elegance",
			"subtitle": "Minimalist beauty from Japan",
			"category": "Inspired by Places",
			"description": "A study in Japanese restraint, where clean lines, green light, and quiet texture become perfume.",
			"products": [
				{
					"handle": "matcha-soft-serve",
					"gift_description": "This belongs here because it translates Japanese tea culture into a pared-back gourmand, with matcha’s vegetal bitterness kept in balance by milk, vanilla, and a discreet waffle-cone accent. The result feels spare yet comforting, like sweetness edited down to its essentials."
				},
				{
					"handle": "ramune",
					"gift_description": "Ramune fits the theme by capturing a distinctly Japanese moment of playfulness without losing clarity: lemon, mint, and aldehydes sparkle like the first hiss of the bottle, then soften into florals and musk. Its transparent sweetness and vanilla finish give it the airy, almost graphic simplicity this collection seeks."
				},
				{
					"handle": "resonant",
					"gift_description": "Resonant earns its place through its cool, modern take on Japanese brightness, where yuzu and ginger flash against a metallic sheen rather than a heavy base. Petitgrain, guaiac wood, and ambergris create a crisp, marine-tinged structure that feels sleek and understated."
				},
				{
					"handle": "leaf-crush",
					"gift_description": "Leaf Crush suits this collection because it leans into the beauty of green things in their raw state, echoing the Japanese appreciation for natural detail and seasonal nuance. Bruised leaves, bitter green tea, and wild thyme are sharpened by yuzu and neroli, then grounded by oakmoss and patchouli."
				},
				{
					"handle": "you-or-someone-like-you",
					"gift_description": "You or Someone Like You fits by stripping green freshness down to a transparent, almost calligraphic line, with mint, spearmint, and shiso giving it a distinctly Japanese herbal accent. Eucalyptus and violet leaf add cool clarity, while the soft woody drydown keeps the composition spare and composed."
				},
				{
					"handle": "oblivion",
					"gift_description": "Oblivion belongs here for its quiet discipline: bergamot and yuzu open with restrained brightness before lavender, sandalwood, and cedar settle into a calm, orderly frame. The orris-like powderiness gives it a measured elegance that mirrors the collection’s minimalist spirit."
				},
				{
					"handle": "shikoku-cologne",
					"gift_description": "Shikoku Cologne is a natural fit because it channels the Japanese landscape through citrus, smoke, and wood, evoking travel without excess. Yuzu and ginger bring lift, amyris adds a gentle resinous glow, and frankincense leaves a meditative trace."
				},
				{
					"handle": "naju-spring",
					"gift_description": "Naju Spring stands out in this theme by pairing Japanese-inspired notes with a carefully managed tension between delicacy and savory depth. Matcha, pear ice cream, and toasted rice suggest a refined seasonal scene, while hinoki, barbecue smoke, and castoreum add an unexpectedly grounded edge."
				},
				{
					"handle": "this-is-not-a-blue-bottle-1-6",
					"gift_description": "This is Not a Blue Bottle 1/.6 – Love fits the collection through its lucid citrus architecture, where yuzu, grapefruit, and orange blossom read as clean, bright, and uncluttered. Neroli, white musks, and a hint of vetiver soften the edges into a gentle, intimate finish."
				}
			]
		},
		"italian-houses": {
			"title": "Italian Aura",
			"subtitle": "Italian craftsmanship and Mediterranean soul",
			"category": "Inspired by Places",
			"description": "A walk through Italy in scent: orchard light, cathedral smoke, and handcrafted woods shaped by Mediterranean heat.",
			"products": [
				{
					"handle": "195-a-c",
					"gift_description": "This fits Italian Aura through its sun-warmed orchard imagery and the poised balance of fig, green pomegranate leaf, mint and cedar that feels distinctly Mediterranean. The incense and smoky cedar give it a crafted, artisanal finish rather than a simple fruity brightness."
				},
				{
					"handle": "sirio",
					"gift_description": "Sirio belongs here because it turns Italian elegance into something celestial yet rooted, with tart rhubarb, rose and white musk lifted by a supple woody frame. The guaiac wood, oud and vanilla add a textured depth that echoes refined craftsmanship with earthy warmth."
				},
				{
					"handle": "baraonda",
					"gift_description": "Baraonda suits the theme by translating Italian sensibility into a late-night mood of whisky, honey and smoky woods, as if polished tradition had been loosened by the evening. Cinnamon, vanilla and amber give it a warmly expressive trail that sets it apart from the brighter, more architectural picks."
				},
				{
					"handle": "cuoium",
					"gift_description": "Cuoium fits Italian Aura through its uncompromising leather character, evoking the tactile side of Italian making with smoke, violet leaf and rugged patchouli. Cade and labdanum darken the composition, giving it a hand-worked, almost industrial intensity absent from the more luminous fragrances here."
				},
				{
					"handle": "sacreste",
					"gift_description": "Sacreste earns its place by channeling Italian sacred architecture: incense, saffron and cedarwood create a contemplative, reverent atmosphere. Cashmeran softens the smoke with a velvety grain, making this the collection’s most quietly meditative take on resin and wood."
				},
				{
					"handle": "fig-lemonade",
					"gift_description": "Fig Lemonade fits the collection because it captures the playful, sunlit side of the Mediterranean with Italian lemon, Calabrian bergamot and green fig leaf. Buchu’s blackcurrant-like bite and the cedarwood base keep the brightness grounded, so it feels crafted rather than merely cheerful."
				},
				{
					"handle": "v-i-t-r-i-o-l",
					"gift_description": "V.I.T.R.I.O.L. belongs in this theme as the collection’s provocation, showing Italian artistry at its most confrontational and unvarnished. Rose, violet leaf and olive oil collide with civet, decaying cucumber and burnt rubber, making beauty feel deliberately corrupted."
				},
				{
					"handle": "via-dell-incenso",
					"gift_description": "Via dell’Incenso suits Italian Aura through its chapel-cool incense, pine and elemi, a profile that recalls both ritual spaces and resinous Mediterranean forests. The charred guaiac wood and ambered Cashmeran add a restrained smoky glow without dulling the clarity of the composition."
				},
				{
					"handle": "bois-dascse",
					"gift_description": "Bois d’Ascese fits the theme by presenting Italian-inspired austerity through dry woods, frankincense and tobacco, with peaty whisky lending a somber, handcrafted edge. Its charred oak and smouldering timber feel disciplined and reflective, a fitting close to the collection’s smoke-laced side."
				}
			]
		},
		"middle-eastern-luxury": {
			"title": "Middle Eastern Luxury",
			"subtitle": "Oud, saffron, rose and opulent amber",
			"category": "Inspired by Places",
			"description": "A corridor of oud, rose and saffron where resin, spice and amber gleam like carved stone in lamplight.",
			"products": [
				{
					"handle": "vettii",
					"gift_description": "Vettii belongs here because it stages the collection's shift from brightness to depth: fig and quince open the scene before saffron, amber and myrrh take over. Oud, sandalwood and tolu balsam give it the resinous, leathery finish that reads unmistakably Middle Eastern in mood."
				},
				{
					"handle": "arabesque",
					"gift_description": "Arabesque fits this theme with almost architectural precision, using saffron, rose and oud as its core ornament. Dark fruit, patchouli and musk add a richer shadow, making it feel like a textile of spice, wood and floral arabesque rather than a simple rose-oud blend."
				},
				{
					"handle": "ruby-wood",
					"gift_description": "Ruby Wood earns its place by pairing rose with frankincense and balsamic resins, which gives the floral heart a temple-like glow. The red-berry facet and dark coffee nuance keep it distinct from the others, adding a dusky, earthy richness to the collection."
				},
				{
					"handle": "parfum-sacre",
					"gift_description": "Parfum Sacré suits this curation because it treats rose as something sacred and weighty, wrapped in pepper, cinnamon and clove. Myrrh and musk bring a powdered resinous warmth, setting it apart as the most classically spiced amber in the lineup."
				},
				{
					"handle": "pg0-4-le-musc-la-peau",
					"gift_description": "Le Musc & La Peau Extrait fits by offering the collection's softer, skin-close side of luxury, with sandalwood and white musk at the center. Rosemary, ylang-ylang and tonka keep it airy yet warm, making it a subtler counterpoint to the oud-heavy fragrances around it."
				},
				{
					"handle": "blond-redhead-16-11",
					"gift_description": "Blond Redhead 16.11 belongs in this theme because it translates opulence into a gourmand register, letting rose and saffron cut through chocolate, vanilla and sweet tobacco. Myrrh and woods prevent it from turning sugary, giving the composition a darker Eastern thread."
				},
				{
					"handle": "osang",
					"gift_description": "Osang fits the collection through its raw, resinous intensity: fenugreek, honey and smoky myrrh create a dense, almost ritualistic heat. Pepper, incense and ashen iris sharpen the profile, making it the most feral and untamed expression here."
				},
				{
					"handle": "mansa-musa",
					"gift_description": "Mansa Musa is a natural fit because it centers oud as a force of wealth and texture, then dresses it in sandalwood, amber and spice. The fermented, leathery facet and restrained rose detail give it a deep, imperial character that feels firmly rooted in the theme."
				},
				{
					"handle": "oud-cannelle",
					"gift_description": "Oud Cannelle closes the circle with cinnamon-spiced woods, smoky myrrh and incense, all anchored by oud. Rose and vanilla soften the edges, while the leathery, ambered base keeps it firmly in the realm of plush Middle Eastern richness."
				}
			]
		},
		"oud-collection": {
			"title": "Oud Collection",
			"subtitle": "The king of perfumery ingredients",
			"category": "By Olfactive Direction",
			"description": "Oud here is treated as a sovereign material: smoky, animalic, resinous, and endlessly reimagined across woods, spice, fruit, and incense.",
			"products": [
				{
					"handle": "vi-et-armis",
					"gift_description": "Vi et Armis belongs here because it pushes oud into a tar-black, maritime darkness rather than a smooth luxury register. Black tea, pepper, and incense sharpen its bitter woods, making the material feel briny, austere, and battle-worn."
				},
				{
					"handle": "565",
					"gift_description": "565 fits this collection by showing how oud can anchor excess without losing its shadowy core. Candied bergamot peel, dark musk, and incense-like woods turn the sweetness into something sultry and weighty."
				},
				{
					"handle": "al-sarab",
					"gift_description": "Al Sarab is a direct ode to oud’s grandeur, built around mixed oud oils and aged Cambodian oud in extrait concentration. Its earthy leather, animalic depth, and faint fruity haze make it a study in oud’s most primal facets."
				},
				{
					"handle": "rouge-karkade",
					"gift_description": "Rouge Karkade earns its place by pairing oud smoke with a vivid oasis-like brightness, proving the note can glow as well as brood. Tart hibiscus tea, pomegranate jam, and Atlas cedar give it a vivid contrast to the resinous wood."
				},
				{
					"handle": "tiger-rouge",
					"gift_description": "Tiger Rouge fits the theme through oud’s feral, spiced side, where the material is framed by incense and leather rather than sweetness. Pepper, paprika, clove, and flashes of blood orange keep the composition tense and alive."
				},
				{
					"handle": "agarwood",
					"gift_description": "Agarwood is perhaps the most literal entry in the set, but it earns its spot by translating oud into a restrained temple-style meditation. Orange peel, cinnamon, cedarwood, and vetiver keep the smoke clean and disciplined."
				},
				{
					"handle": "black-afgano",
					"gift_description": "Black Afgano belongs here because it treats oud as part of a dense nocturnal accord, one that is sticky, smoky, and almost narcotic in feel. Coffee, hashish-like nuances, and tobacco deepen its resinous darkness."
				},
				{
					"handle": "liquid-incense",
					"gift_description": "Liquid Incense fits the collection by presenting oud as a luminous support beneath frankincense and mineral resin. Bergamot, dry woods, and a subtle animalic undertone make the composition feel like incense caught in sunlight."
				},
				{
					"handle": "arcimboldo",
					"gift_description": "Arcimboldo shows oud in a startlingly unconventional setting, where the note grounds a green, savory still life. Bitter artichoke, celery, mushroom, and smoky char make the oud feel earthy, strange, and tactile."
				}
			]
		},
		"rose-lovers": {
			"title": "Rose Lovers",
			"subtitle": "Every shade of rose, from dewy to dark",
			"category": "By Olfactive Direction",
			"description": "From first blush to midnight shadow, this collection traces rose through green sap, velvet fruit, smoke, and dusk.",
			"products": [
				{
					"handle": "in-principio-v2",
					"gift_description": "Though rose is absent here, this fits the collection by echoing rose’s creamy, petal-soft side through lily and ylang-ylang. Kumquat, bitter orange, and ambergris add a tart, mineral brightness that keeps the floral heart poised rather than lush."
				},
				{
					"handle": "rose-shiso",
					"gift_description": "This belongs for its rose woven with shiso, a pairing that shows the flower’s herbaceous, almost savory face. Iris, sandalwood, and cedar give it a cool framework, while the minty green edge makes the rose feel freshly cut."
				},
				{
					"handle": "asoral",
					"gift_description": "Asoral captures the dewy beginning of the rose story, with Turkish and Lani roses glowing beside magnolia and peony. Tea, liquorice, and hay lend a delicate, airy texture that keeps the bouquet luminous and tender."
				},
				{
					"handle": "fig-porn",
					"gift_description": "It earns a place here by showing rose in a softer, fruit-tinted register, where peony and milky fig blur the line between petal and skin. Pear, bergamot, vanilla, and musk give the composition a creamy brightness that feels ripe but not heavy."
				},
				{
					"handle": "ensang-noir",
					"gift_description": "This is the collection’s nocturnal rose: smoky, metallic, and shadowed, with the flower pushed into a darker register. Opoponax, patchouli, and smoked woods deepen the rose’s drama, while magnolia adds a faintly sweet gleam."
				},
				{
					"handle": "pg16-jardins-de-kerylos",
					"gift_description": "PG16 fits the theme by suggesting rose’s garden context rather than the bloom alone, with sunlit fruit and leafy air around the floral core. Green fig, white peach, and sycamore wood keep it airy, while tuberose and musk soften the edges."
				},
				{
					"handle": "rosi-rosa",
					"gift_description": "Rosi Rosa shows rose through a playful, forested lens, where raspberry gives the flower a tart, syrupy lift. Siberian fir and hinoki sharpen the sweetness, making the rose feel brisk, resinous, and a little wild."
				},
				{
					"handle": "experimentum-crucis",
					"gift_description": "This belongs because it treats rose as a radiant chypre, brightened by apple and honey before turning earthier. Cumin, patchouli, and textured woods give the floral core grit and depth, so the rose reads vivid and alive."
				},
				{
					"handle": "narcotic-v",
					"gift_description": "While built around white florals, it still fits by exploring rose’s lush, heady kinship with opulent blossoms. Tuberose, jasmine, and orange blossom create a dense floral haze, and cedarwood keeps the sweetness from floating away."
				}
			]
		},
		"white-flowers": {
			"title": "White Flowers",
			"subtitle": "Tuberose, jasmine, gardenia and intoxicating blooms",
			"category": "By Olfactive Direction",
			"description": "A study in white petals at their most narcotic, where creamy blooms, green stems and dusk-lit sweetness drift between purity and excess.",
			"products": [
				{
					"handle": "delirium",
					"gift_description": "Delirium belongs here for the way it piles gardenia, jasmine and tuberose into a lush white-floral mass, then rounds it with frangipani and champaca. White lotus, vanilla and raw sugar give it a milky, sugared finish that makes the bouquet feel almost opulent."
				},
				{
					"handle": "narcotic-v",
					"gift_description": "Narcotic V is a direct hit of the collection’s core theme: tuberose leads, with jasmine and orange blossom pushing the floral intensity higher. Cedarwood keeps the structure spare, but the fragrance is really about that creamy, forceful white bloom and its lily-like brightness."
				},
				{
					"handle": "mathilde",
					"gift_description": "Mathilde fits by widening the white-flower idea into a fuller floral still life, where jasmine and ylang-ylang sit beside narcissus in a richly petaled heart. Passion fruit, papaya and sandalwood add a ripe, luminous softness that sets it apart from the more strictly white-floral picks."
				},
				{
					"handle": "bo-bo",
					"gift_description": "Bo-Bo earns its place through its airy take on the theme, using orange blossom, jasmine and lily of the valley to sketch a clean white bouquet rather than a heavy one. Mandarin, musk and vetiver give it a breezy citrus-green clarity that keeps the flowers translucent."
				},
				{
					"handle": "fleur-de-rocaille",
					"gift_description": "Fleur de Rocaille belongs in a white-flowers collection because lily-of-the-valley and ylang-ylang are treated as radiant petals rather than background accents. The aldehydic sparkle, rose and sandalwood add lift, warmth and a faint vintage sheen that distinguishes it from the more creamy compositions."
				},
				{
					"handle": "suze",
					"gift_description": "Suze fits this theme by taking jasmine and magnolia into a more shadowed register, where their pale floral tones are sharpened by spearmint and clementine. Black tea, orris and fossilized amber make the white-flower impression feel hushed, smoky and unusually reflective."
				},
				{
					"handle": "in-principio-v2",
					"gift_description": "In principio V2 sits naturally among white flowers because its creamy white lily is the centerpiece, with ylang-ylang adding a soft, low glow. Kumquat, bitter orange and ambergris bring tartness, texture and saline depth, making the floral heart feel concentrated and contemplative."
				},
				{
					"handle": "jasmin-ylang",
					"gift_description": "Jasmin & Ylang is a classic white-flower study, built from jasmine, ylang-ylang and frangipani in a bright, sun-washed blend. Vanilla and benzoin give it a tender balsamic warmth, so the bouquet feels easygoing rather than sharp or austere."
				},
				{
					"handle": "fathom-v",
					"gift_description": "Fathom V fits this collection by showing white flowers in a darker habitat, where lily and mimosa rise from sea salt, green leaves and vetiver. Its mossy, aquatic depth makes the blooms feel submerged and strange, a useful counterpoint to the sweeter florals here."
				}
			]
		},
		"boozy-scents": {
			"title": "Boozy Scents",
			"subtitle": "Whisky, rum, cognac and after-hours warmth",
			"category": "By Olfactive Direction",
			"description": "A round of ambered sips and sugared shadows, where spirits, smoke and warmth cling to the skin long after last call.",
			"products": [
				{
					"handle": "drunk-saffron",
					"gift_description": "This belongs here because cognac is the pulse of the composition, and saffron plus supple leather turn it into a slow-burning boozy haze. Patchouli and vanilla keep the warmth close, making it feel like the trace of a drink shared in dim light."
				},
				{
					"handle": "tokyo-nights",
					"gift_description": "It fits the collection by pairing plum wine with smoked tobacco, so the boozy facet stays vivid without losing its nocturnal edge. Osmanthus and amber add a mellow glow, while the frangipane-like sweetness gives it a distinctly sipped, after-dark character."
				},
				{
					"handle": "chateau-de-velours",
					"gift_description": "Château de Velours earns its place through red wine and davana, which give the fruit a fermented, vinous lift rather than a simple berry accord. Dark chocolate and honey deepen the plushness, while oakmoss and patchouli keep the sweetness grounded and shadowy."
				},
				{
					"handle": "ziveli",
					"gift_description": "This is a boozy scent through and through: plum brandy leads, with raspberry and tobacco creating the impression of a spirited nightcap. Honeyed leather, saffron and tonka give it warmth and texture that separate it from the fruitier picks in the set."
				},
				{
					"handle": "or-du-srail",
					"gift_description": "Rum is the anchor here, and the honeyed tobacco makes the composition feel steeped in ambered liquor and late-evening heat. Mango, candied fruit and spicy woods push it toward a lush, cellar-like richness that suits the theme perfectly."
				},
				{
					"handle": "celestial-object",
					"gift_description": "Celestial Object fits by translating boozy comfort into a softer register: sugared almond, milky warmth and tonka create the creamy afterglow of a liqueur dessert. The rice-paper popcorn nuance and touch of salt keep it airy rather than heavy."
				},
				{
					"handle": "far",
					"gift_description": "Far belongs in the theme as the bright, playful counterpoint to the darker spirits, with yuzu and lime giving the gourmand a sparkling top-note fizz. Vanilla, cream and biscuit supply the soft, boozy-dessert feel without ever becoming syrupy."
				},
				{
					"handle": "milk",
					"gift_description": "Milk | Personal suits this collection because its marshmallow and tonka read like a creamy nightcap, then skin musk and cedar turn that sweetness intimate. Mahogany adds a quiet woody warmth, making it feel like the soft residue of a drink close to the body."
				},
				{
					"handle": "dark-honey",
					"gift_description": "Dark Honey is the most unruly dram in the lineup, built on rum, plum liqueur and baked peach for a saturated boozy richness. Cinnamon, dark vanilla and animalic honey give it a feral, heated finish that keeps it firmly in the after-hours lane."
				}
			]
		},
		"coffee-fragrances": {
			"title": "Coffee Fragrances",
			"subtitle": "Roasted, rich, caffeine-fuelled scents",
			"category": "By Olfactive Direction",
			"description": "A late-night coffeehouse in perfume form: bitter roast, sugared steam, and the dark warmth that lingers after the cup is empty.",
			"products": [
				{
					"handle": "vanille-glace",
					"gift_description": "This fits the collection by turning vanilla gourmand into a coffee dessert, with espresso cutting through brown sugar syrup and cinnamon. Toasted madeleine, sandalwood and tonka make the roast feel like a pastry counter rather than a plain sweet accord."
				},
				{
					"handle": "pg10-aomassa",
					"gift_description": "It belongs here because the coffee is treated as a charred, bitter core rather than a café accent. Roasted coffee, caramel and smoky incense are sharpened by dry woods and tolu balm, giving it an ember-like depth that sets it apart."
				},
				{
					"handle": "roissant-afe",
					"gift_description": "This is the breakfast-table side of coffee perfumery, built around roasted coffee and milky cappuccino. Buttery croissant, salty tonka and soft woods add a baked, café-au-lait comfort that keeps it distinct from the darker brews in the set."
				},
				{
					"handle": "vietnamese-coffee",
					"gift_description": "It earns its place through the contrast of dark-roasted coffee and creamy condensed milk, a classic iced-coffee signature. Bitter chocolate and smoky roast keep the sweetness in check, while tonka and warm woods give it a quieter, more intimate finish."
				},
				{
					"handle": "cacao-pimento",
					"gift_description": "This one widens the coffee theme into spiced gourmand territory, where roasted coffee sits beside cacao rather than dessert sugar. Saffron, Jamaican allspice and blond tobacco make the blend glow with a dry, woody heat that no other bottle in the group quite matches."
				},
				{
					"handle": "deer",
					"gift_description": "It fits the theme by showing coffee in its earthier, woodland register, not as a sweet café note. Green coffee, smoky tobacco and hay are grounded by cedar, leather and musk, with aromatic herbs and patchouli adding a rugged, forest-floor character."
				},
				{
					"handle": "sugar-addict",
					"gift_description": "This fragrance belongs because it pushes coffee into full dessert excess, using cocoa and coffee as the backbone of its sweetness. Cinnamon, vanilla and rum, cushioned by cashmere wood, give it a thick, boozy richness that feels more hedonistic than playful."
				},
				{
					"handle": "23-59",
					"gift_description": "It suits the collection as the dusk-hour coffee scent, where espresso and crème brûlée are darkened by smoke. Cognac, benzoin and guaiac wood create a toasted, resinous trail that makes the sweetness feel grown-up and shadowed."
				},
				{
					"handle": "sadonaso",
					"gift_description": "This closes the set on coffee as skin-close texture: coffee, tobacco and tonka are wrapped in sandalwood and powdery warmth. Its edible softness and bodily edge make it the most intimate take here, with a tactile depth that lingers rather than shouts."
				}
			]
		},
		"chocolate-cocoa": {
			"title": "Chocolate & Cocoa",
			"subtitle": "Dark, rich, dessert-like indulgence",
			"category": "By Olfactive Direction",
			"description": "A cabinet of dark confection: cocoa, smoke, spice and velvet woods drawn together in a deliberately shadowed gourmand line.",
			"products": [
				{
					"handle": "anti-blues",
					"gift_description": "This belongs here because it turns chocolate into something brooding rather than sugary, with smoky incense and tobacco giving the cocoa a dusky backbone. Saffron, vanilla and vetiver make it feel leathery, warm and slightly shadowed."
				},
				{
					"handle": "agrani",
					"gift_description": "It fits the theme by pushing cacao into a more baroque, almost nocturnal register, where cherry, almond and saffron are darkened by pipe tobacco and leather. Burmese oud, castoreum and amber add a vintage, animalic depth that sets it apart from sweeter cocoa blends."
				},
				{
					"handle": "pg04-musc-maori",
					"gift_description": "Musc Maori earns its place by wrapping milk-chocolate softness in a cooler, skin-close form of gourmand, so the cocoa feels intimate rather than heavy. Bitter bergamot, white musk and a luminous creamy texture give it a distinct airy contrast within the collection."
				},
				{
					"handle": "boccanera",
					"gift_description": "This is the collection's most provocative chocolate study, because dark cocoa here is sharpened by ginger and folded into an animalic woody accord. The powdery sweetness, earthy depth and slightly feral finish make it feel like cocoa with a bite."
				},
				{
					"handle": "07-orris-cacao",
					"gift_description": "It fits by showing cocoa in a mineral, contemplative guise, where dark cacao and Tuscan orris create a dusty chocolate-leather effect. Rain-on-concrete freshness, oakmoss and earthy woods keep the sweetness restrained and intriguingly austere."
				},
				{
					"handle": "chateau-de-velours",
					"gift_description": "Château de Velours belongs because it treats chocolate as part of a lush, edible tapestry rather than a solo note. Raspberry, blackcurrant and boozy red wine meet dark chocolate and honey, while oakmoss and patchouli give the fruit a velvety, grounded finish."
				},
				{
					"handle": "tabac-exquis",
					"gift_description": "This is a quintessential cocoa selection because it pairs dark chocolate with tobacco in a classic smoky gourmand frame, then softens it with iris and benzoin. Cinnamon and myrrh add resinous warmth, making the cocoa feel plush, bitter and gently sweet."
				},
				{
					"handle": "mezcal-chocolat",
					"gift_description": "It fits the theme by taking cacao into a bracing, spirited direction, where salted lime and chili cut through dark chocolate and mezcal. Rum, incense and earthy woods add smoky depth, so the sweetness never loses its rough-edged charm."
				},
				{
					"handle": "cacao-pimento",
					"gift_description": "Cacao Pimento earns its place through its richly spiced cacao core, lifted by saffron, rum and Jamaican allspice. Roasted coffee, tonka and sandalwood deepen the chocolate into a woody, glowing gourmand with real staying power."
				}
			]
		},
		"leather-suede": {
			"title": "Leather & Suede",
			"subtitle": "Tough, textured, skin-like compositions",
			"category": "By Olfactive Direction",
			"description": "Leather & Suede explores the grain, smoke, and softness of skin-like materials—where rough edges, powder, and warmth meet.",
			"products": [
				{
					"handle": "hey-bitch",
					"gift_description": "This fits the collection by turning leather into a bright, abrasive contrast: bitter leather, tart rhubarb, and sueded iris keep the texture front and center. Butter-rich orange blossom and milky sandalwood make the hide feel worn against skin rather than merely dark."
				},
				{
					"handle": "almond-suede",
					"gift_description": "Almond Suede belongs here because it frames suede as a tactile gourmand, with almond marzipan and supple leather sharing the stage. Neroli, rosemary, and saffron give it a green, spiced counterpoint that keeps the softness from becoming bland."
				},
				{
					"handle": "flight-mode",
					"gift_description": "Flight Mode earns its place through its cool, pared-back take on suede: smoky guaiac wood, airy lavender, and lime give the leather a clean, almost technical feel. Rum and vanilla add warmth without weighing down the crisp, mechanical finish."
				},
				{
					"handle": "orange-patina",
					"gift_description": "This is a leather scent through a vivid, almost surreal lens, where orange and watermelon sweetness crash into osmanthus and worn-in hide. The guaiac wood and supple leather give the fruit a shadowed, textured frame that feels unmistakably on theme."
				},
				{
					"handle": "iris-tannat",
					"gift_description": "Iris Tannat fits the collection by pairing velvety iris with tannic wine, weathered oak, and supple leather, creating a material, skin-close darkness. Mineral powder, earthy depth, and nutmeg make it feel stern, textured, and resolutely unsmoothed."
				},
				{
					"handle": "malt-night-fighter",
					"gift_description": "Night Warrior belongs here because its smoked tea and toasted malt give the floral core a charred, suede-like density. Carnation, ylang-ylang, and vintage violet add a dusky, waxy softness that keeps the composition firmly in the leather-and-texture register."
				},
				{
					"handle": "cuoium",
					"gift_description": "Cuoium is a direct hit for this theme: smoky animalic leather is sharpened by violet leaf and deepened with cade, labdanum, and patchouli. Its rugged, tactile force makes the hide feel raw, dark, and almost feral."
				},
				{
					"handle": "kar-wai",
					"gift_description": "Kar-Wai fits because it treats leather as supple and intimate rather than heavy, with smoked Longjing tea and osmanthus giving it a polished, slightly bitter warmth. Cardamom, rose, jasmine, and vetiver keep the texture fluid and close to the skin."
				},
				{
					"handle": "chere-colette",
					"gift_description": "Chère Colette belongs in this collection for its suede finish, which softens linden blossom, apple, and almond into something gently tactile. Hay, honeyed grain, and musk give the composition a sun-warmed, fabric-soft grain that reads as suede rather than floral alone."
				}
			]
		},
		"powdery-iris": {
			"title": "Powdery & Iris",
			"subtitle": "Soft, sophisticated iris and powdery elegance",
			"category": "By Olfactive Direction",
			"description": "A study in iris at its most tactile: veiled, cosmetic, and quietly radiant, with powder settling like light on skin.",
			"products": [
				{
					"handle": "alyazia",
					"gift_description": "Alyazia belongs here because its violet-iris core reads like vintage face powder, made softer by orange blossom and wild strawberry. Vanilla, tonka bean, and sandalwood give it a cushioned, old-school cosmetic finish that deepens the theme."
				},
				{
					"handle": "almaz",
					"gift_description": "Almaz fits the collection by using iris as a velvety counterpoint to its tart blackcurrant and raspberry, keeping the whole composition airy rather than syrupy. Heliotrope, rose, and a brown-sugar musk base add a plush, dusted texture that feels unmistakably powdery."
				},
				{
					"handle": "afrique-en-fleurs",
					"gift_description": "Afrique en Fleurs earns its place through the way iris and chamomile are lifted into a sun-bleached floral haze rather than a lush bouquet. Orange, ginger, sandalwood, and vanilla create a dry creaminess that echoes powder warmed by heat."
				},
				{
					"handle": "baise-moi-on-the-dancefloor",
					"gift_description": "Baise Moi On The Dancefloor suits this theme because violet leaf and orris deliver that smudged, cosmetic iris effect at the heart of the scent. Cashmeran and sandalwood add a soft woody blur, while the nightlife mood gives the powdery accord a modern, lived-in edge."
				},
				{
					"handle": "les-cahiers-secrets",
					"gift_description": "Les Cahiers Secrets (Secret Diaries) fits the collection by pairing orris and heliotrope with the unmistakable texture of pressed powder. Lily, cumin, and musk introduce a flushed warmth that makes the iris feel intimate and human rather than merely delicate."
				},
				{
					"handle": "iris-cendr",
					"gift_description": "Iris Cendré belongs in a powdery iris selection because it treats orris as something cool, rooty, and refined, then veils it in incense smoke. Violet, tobacco, amber, and leather add austere depth, giving the softness a vintage, almost ash-toned elegance."
				},
				{
					"handle": "merina",
					"gift_description": "Merina fits this theme through its milky almond and powdery iris pairing, which creates a pale, fabric-like softness from the start. Fennel's faint anise lift and jasmine's restraint keep it distinctive, while the Spanish wool inspiration makes the effect especially tactile."
				},
				{
					"handle": "intime-extime",
					"gift_description": "Intime-Extime deserves a place here because iris is wrapped in milk, vanilla, and incense, producing exactly the kind of soft-focus aura this collection seeks. White tea and the Itauba-wood accord add a clean, woody clarity that keeps the powdery heart from feeling heavy."
				},
				{
					"handle": "iris-coriandre",
					"gift_description": "Iris Coriandre fits the collection by giving iris a classic cosmetic dryness, then sharpening it with bergamot and coriander. Violet, osmanthus, and a subtle leathery facet add a savory, old-world twist that makes the powder feel poised rather than sweet."
				}
			]
		},
		"lavender-reimagined": {
			"title": "Lavender Reimagined",
			"subtitle": "Modern, unexpected takes on lavender",
			"category": "By Olfactive Direction",
			"description": "Lavender here is never merely clean—it’s smoked, salted, powdered, and set against woods, fruit, and shadow until it feels newly alive.",
			"products": [
				{
					"handle": "ara",
					"gift_description": "ARС belongs here because it pushes lavender into a rugged fougère register, where holy basil, thyme, and pine sharpen its green edge. Oakmoss, styrax, and benzoin give it a smoky, spiced depth that keeps the lavender from feeling predictable."
				},
				{
					"handle": "oblivion",
					"gift_description": "Oblivion fits this theme by reframing lavender through a cool, modern clarity: bergamot and yuzu flash at the top, then cedar and sandalwood draw a dry frame around it. The orris adds a violet-powder softness that makes the whole composition feel quietly offbeat."
				},
				{
					"handle": "pour-un-homme",
					"gift_description": "Pour Un Homme de Caron earns its place as a classic that shows lavender can be both crisp and tender, not just brisk. Vanilla, rose, and musk round out the herbal core, giving it a creamy warmth and a distinctly vintage poise."
				},
				{
					"handle": "provence",
					"gift_description": "Provence fits because it treats lavender as part of a sharper aromatic landscape, not a solitary note. Diva lavender is lifted by clary sage, hyssop, spearmint, and pepper, creating a brisk herbal profile with a warm, grounded finish."
				},
				{
					"handle": "rusalka-s-embrace",
					"gift_description": "Rusalka’s Embrace belongs in this collection for the way it drags lavender into dark waters rather than open fields. Seaweed, ambergris, peppermint, and pine make the lavender feel briny and eerie, while propolis and musk add a submerged, tactile strangeness."
				},
				{
					"handle": "brume-dete",
					"gift_description": "Brume d'été fits as the lightest, most breezy lavender here, turning the note into a translucent herbal wash. Mandarin, petitgrain, rosemary, and mint keep it airy and green, making the lavender feel like a fleeting summer haze rather than a full-bodied bouquet."
				},
				{
					"handle": "flight-mode",
					"gift_description": "Flight Mode belongs in this edit because it turns lavender into something aerodynamic and modern, clipped against leather and smoke. Lime, guaiac wood, rum, and vanilla give it a cool-warm contrast that makes the lavender feel engineered rather than pastoral."
				},
				{
					"handle": "forbidden-rouge",
					"gift_description": "Forbidden Rouge fits by taking lavender into a richer, more dramatic territory without losing structure. Cherry, blackcurrant, saffron, and anise give it a dark-fruited tension, while patchouli and tonka keep the lavender threaded through a plush, melancholic base."
				},
				{
					"handle": "giardinodiercole",
					"gift_description": "Giardinodiercole suits the theme because it casts lavender as part of a mythic, aromatic garden rather than a simple herb. Bergamot, juniper, black pepper, incense, and woods give it a resinous, faintly animalic intensity that feels both classical and unexpected."
				}
			]
		},
		"perfumes-that-dont-smell-like-perfume": {
			"title": "Perfumes That Don't Smell Like Perfume",
			"subtitle": "Unconventional, abstract, genre-defying scents",
			"category": "More Editorial",
			"description": "These are fragrances that sidestep perfume’s usual grammar, turning materials into ideas, textures, and uneasy beauty.",
			"products": [
				{
					"handle": "osang",
					"gift_description": "Osang belongs here because it treats perfume as raw matter: fenugreek, molten honey, and smoky myrrh create a dark, feral accord that feels more alchemical than decorative. Pepper, incense, and ashen iris sharpen its resinous body into something restless and uncategorizable."
				},
				{
					"handle": "cashmere-beige",
					"gift_description": "Cashmere Beige fits this collection by making floralness feel almost weightless and abstract, with mandarin, violet, and cool orris sketched like a washed-out memory rather than a bouquet. Cedar, tonka, and musk give it a soft-focus texture that reads more like fabric and light than classic perfume structure."
				},
				{
					"handle": "black-oregano",
					"gift_description": "Black Oregano earns its place through contrast and tension, using lime, rosemary, and sun-scorched oregano to push aromatic brightness into leathery territory. Sandalwood, smoky vetiver, and patchouli darken the composition so it feels like a landscape of herbs, smoke, and skin rather than a standard fougère."
				},
				{
					"handle": "scrtions-magnifiques",
					"gift_description": "Secretions Magnifiques is the collection’s most confrontational idea of perfume, replacing prettiness with saline sharpness, a milky accord, and sandalwood. Its bodily, experimental character makes it a direct statement about scent as sensation and disturbance, not ornament."
				},
				{
					"handle": "boar",
					"gift_description": "Boar fits because it smells like a scene rather than a formula: apple, mushroom, and truffle collide with green conifers and smoky woods to evoke damp earth and bristling fur. Leather and vetiver push it further from polished perfumery into something wild, tactile, and almost untamed."
				},
				{
					"handle": "indian-grass",
					"gift_description": "Indian Grass belongs in this lineup for its off-kilter green profile, where cannabis, coriander, and sweet elemi create an aromatic haze that refuses neat categorization. Guaiac wood, incense, and patchouli add smoke and depth, giving it a restless, slightly animalic edge."
				},
				{
					"handle": "monopteros",
					"gift_description": "Monopteros is a perfect fit because it turns familiar notes sideways, pairing creamy coconut with icy aldehydes, coffee, and cardamom in a way that feels architectural rather than gourmand. Neroli brightens the oddness, while the woody base and subtle animalic nuance keep it intriguingly offbeat."
				},
				{
					"handle": "v-i-t-r-i-o-l",
					"gift_description": "V.I.T.R.I.O.L. is included because it weaponizes perfume’s materials, setting rose and soft fruit against civet, decaying cucumber, and burnt rubber with no attempt to smooth the edges. Violet leaf and olive oil intensify its abrasive, almost anti-beautiful texture, making it a deliberate study in rupture."
				},
				{
					"handle": "rouge",
					"gift_description": "Rouge fits this theme by making the edible and the earthy feel strangely alien: beetroot, smoky incense, pink pepper, and ginger create a savory brightness that resists easy floral or gourmand labels. Geranium’s metallic sheen and leathery labdanum give it a dry, skewed finish that feels quietly subversive."
				}
			]
		},
		"scents-that-tell-a-story": {
			"title": "Scents That Tell a Story",
			"subtitle": "Narrative, cinematic, emotionally rich fragrances",
			"category": "More Editorial",
			"description": "These fragrances read like scenes in a novel: each one opens a door, shifts the light, and leaves a trace of emotion behind.",
			"products": [
				{
					"handle": "damask",
					"gift_description": "Damask belongs here because its rose feels like a chapter in motion, lifted by blackcurrant, pear and lemon before the saline amber turns it seaward. The result is a floral story with fruit, salt and musk as its plot points."
				},
				{
					"handle": "talento",
					"gift_description": "Talento fits this collection by turning a rose into a character rather than a backdrop, with mint, aldehydes and green stems giving it a vivid, cinematic spark. Its woody frame and cool brightness make it feel like a scene of guarded tenderness."
				},
				{
					"handle": "liquid-incense",
					"gift_description": "Liquid Incense suits the theme because it captures the moment before ritual begins, when bergamot, resin and mineral warmth suggest anticipation rather than ceremony. Dry woods and a faint oud trace give the composition a golden, narrative glow."
				},
				{
					"handle": "lu-bu",
					"gift_description": "Lü Bu earns its place through tension: clove, gunpowder and smouldering paper conjure a dramatic tableau before iris and benzoin soften the edges. Vanilla and dark woods add a shadowed afterword, making it feel like a story told in low light."
				},
				{
					"handle": "mistpouffer",
					"gift_description": "Mistpouffer fits this collection because it behaves like a half-seen landscape, where fig leaf, pine and vetiver drift through smoke and mist. Immortelle adds honeyed warmth, giving the fragrance a quiet, enigmatic arc."
				},
				{
					"handle": "sirenide",
					"gift_description": "Sirenide belongs in this set for the way it stages contrast like a plot twist: aniseed, lotus and iris are interrupted by bitter almond, PVC and pinseal effects. The marine leather base keeps the tension alive, making the scent feel like a dark scene beneath calm water."
				},
				{
					"handle": "princess-of-china",
					"gift_description": "Princess of China | 中國公主 fits the theme by painting a poised, almost regal portrait with peony, white lotus, pear and lychee. Its silk-like texture gives the floral-fruity accord a graceful narrative softness rather than simple prettiness."
				},
				{
					"handle": "vanille-mafonja",
					"gift_description": "Vanille Mafonja belongs here because it tells a fuller vanilla tale, beginning with saline brightness and spice before moving into ylang-ylang, myrrh and incense. Dark amber deepens the final act, so the sweetness feels storied rather than flat."
				},
				{
					"handle": "01-nagarmotha",
					"gift_description": "01 Nagarmotha suits this collection through its earthy, almost monastic character, where nagarmotha, patchouli and makrut lime leaf create a scene of green austerity. Frankincense, vetiver and cedar lend it a grounded, commanding finish that feels quietly literary."
				}
			]
		},
		"brands-before-they-blow-up": {
			"title": "Sleeper Hits",
			"subtitle": "Emerging houses and hidden gems to discover now",
			"category": "More Editorial",
			"description": "These are the fragrances that slip under the radar at first sniff, then linger in memory as proof that the most interesting houses are often the least expected.",
			"products": [
				{
					"handle": "mezcal-chocolat",
					"gift_description": "Mezcal Chocolat belongs here because it shows how a newer voice can twist gourmand rules into something unruly and specific. Salted lime, chili and fruity mezcal keep the dark chocolate from feeling familiar, while rum and incense push it into shadow."
				},
				{
					"handle": "1805-tonnerre",
					"gift_description": "Tonnerre fits the sleeper-hits theme by refusing to play safe, turning maritime freshness into something fierce and singular. Gunpowder, blood accord and salt spray make it more than a sea scent, with birch tar and dark woods giving it a scorched, bracing finish."
				},
				{
					"handle": "dancing-light",
					"gift_description": "Dancing Light earns its place by taking a classic green floral idea and making it feel newly observed rather than retread. Icy mint, Siberian pine and luminous jasmine create the contrast, while neroli and soft sandalwood keep the composition airy but not thin."
				},
				{
					"handle": "fantomas",
					"gift_description": "Fantomas is a hidden gem because it weaponizes a fruit note into something strange enough to stay with you. Juicy melon, metallic edges and Cashmeran give it a cool, synthetic shimmer, so the comfort of fruit is constantly unsettled."
				},
				{
					"handle": "scrtions-magnifiques",
					"gift_description": "Secretions Magnifiques belongs in this collection precisely because it is the kind of cult outlier only a bold niche house would make. Saline aldehydes, a milky accord and sandalwood turn attraction into discomfort, making it one of the most provocative discoveries here."
				},
				{
					"handle": "moko",
					"gift_description": "Moko fits the theme as an emerging-house scent that feels alive with place and season rather than trend. Damp earth, green fig leaves and raspberry are sharpened by peppery Akigalawood, then moss and sandalwood root the freshness in woodland depth."
				},
				{
					"handle": "lola-at-coat-check",
					"gift_description": "Lola at Coat Check earns sleeper-hit status by pairing an easy-to-love sweetness with a sharper, more nocturnal frame. Cardamom, grapefruit and white chocolate give it lift and texture, while sandalwood and amber make the composition feel intimate without becoming heavy."
				},
				{
					"handle": "tears",
					"gift_description": "Tears belongs here because it turns an unexpected subject into a quietly radical floral-woody study. Green galbanum, sage and powdery flowers sit over hay, vetiver and cypriol, giving the cotton idea a dry, earthy realism."
				},
				{
					"handle": "arashiyama",
					"gift_description": "Arashiyama | 嵐山 fits this collection through its restraint: it makes stillness feel like a discovery. Bamboo leaves, cut grass and rain-soaked earth are softened by transparent violet, while bamboo wood adds a calm warmth that distinguishes it from louder greens."
				}
			]
		},
		"best-value-per-ml": {
			"title": "Best Value Per ML",
			"subtitle": "Maximum quality for your money",
			"category": "More Editorial",
			"description": "This shelf is for fragrance that spends wisely: compositions with real texture, character, and lasting impact per millilitre.",
			"products": [
				{
					"handle": "1993",
					"gift_description": "1993 earns its place by delivering a full, high-drama chypre profile without feeling thin or decorative. Peach, dark chocolate, and earthy patchouli give it a lot of material to work with, so every spray feels like serious value."
				},
				{
					"handle": "matcha-mist",
					"gift_description": "Brume Matcha fits this theme because it turns a modest mist format into a surprisingly complete aromatic statement. Roasted matcha, basil, and peppermint create crisp dimension, while geranium and cedarwood keep the effect clean and efficient rather than excessive."
				},
				{
					"handle": "osmanthus-oolong",
					"gift_description": "Osmanthus & Oolong belongs here for the way it stretches elegant ingredients into a refined, well-judged composition. Apricot-toned osmanthus, oolong tea, and guaiac wood make it feel composed and substantial, with orange blossom and frangipani adding lift without waste."
				},
				{
					"handle": "vanille-creme",
					"gift_description": "Vanille Creme is the collection’s clearest proof that sweetness can still be smartly priced when the formula has depth. Bergamot, caramel, and buttery custard notes give it a rich gourmand core, so the vanilla and musk land with more presence than its price suggests."
				},
				{
					"handle": "blueforest",
					"gift_description": "Blueforest fits this selection because it offers crisp woods and spice in a stripped-back, high-impact style that feels deliberately efficient. Cedarwood, black pepper, and airy musk create a polished freshness that wears like far more than a simple woody blend."
				},
				{
					"handle": "cloud-essence-28-08",
					"gift_description": "Cloud Essence 28.08 earns its spot through density of texture: milk, rice powder, and sandalwood make it feel quietly luxurious without overcomplication. Mimosa and white musk add softness, but the savory-powdery contrast gives you real complexity for the spend."
				},
				{
					"handle": "figo",
					"gift_description": "Figo is here because it captures the full fig-tree experience with very little fuss and a lot of nuance. Orange brightness, milky fig flesh, and guaiacwood make it feel layered and natural, so the fragrance reads as generous rather than minimal."
				},
				{
					"handle": "301",
					"gift_description": "301 Noces de Santal represents maximum value per ml by packing a broad, creamy amber accord into one smooth, lasting shape. Sandalwood, benzoin, and coconut bring weight and warmth, while ylang-ylang and patchouli keep the sweetness from flattening out."
				},
				{
					"handle": "rital-date",
					"gift_description": "Rital Date fits the theme by being unusually inventive without feeling overbuilt, which is exactly the kind of smart spending this collection celebrates. Limoncello, pesto herbs, and pistachio ice cream give it three distinct registers, so each wear feels like a lot of personality for the price."
				}
			]
		},
		"cult-classics": {
			"title": "Cult Classics",
			"subtitle": "The bestsellers everyone talks about",
			"category": "More Editorial",
			"description": "These are the fragrances that earned their status the hard way: talked about, revisited, and kept close by nose-nerds for good reason.",
			"products": [
				{
					"handle": "comme-des-garcons-2",
					"gift_description": "Comme des Garcons 2 belongs here because it is one of the house’s defining conversation pieces, a cult reference point for abstract fragrance. Bright aldehydes, ink, and tea make it instantly recognizable, while incense and woods give the legend its hushed depth."
				},
				{
					"handle": "20359",
					"gift_description": "20359 fits this collection as a newer cult object with a story people trade like a secret, prized for its bold, night-bound character. Maraschino cherry, clove, and heliotrope set the tone, then latex, tobacco, and leather push it into memorable territory."
				},
				{
					"handle": "ayiti",
					"gift_description": "Ayiti earns its place as a quietly revered vetiver study, the sort of bottle fragrance obsessives point to when discussing earthy modern classics. Haitian vetiver, petrichor, and patchouli create its signature damp-rooted profile, with smoky undertones adding grit."
				},
				{
					"handle": "kolada",
					"gift_description": "Kolada qualifies as a cult classic because it takes a familiar tropical idea and turns it into something sharply distinctive that provokes discussion. Pineapple, metallic lily-of-the-valley, and coconut milk give it its strange allure, while amber leaves a mineral afterglow."
				},
				{
					"handle": "lumieres",
					"gift_description": "Lumières fits the theme as a niche staple for people who collect textures as much as notes, admired for its restrained but unmistakable presence. Pink pepper, juniper, and concrete-like minerals define its cool profile, with iris and cedar softening it into a luminous skin scent."
				},
				{
					"handle": "mango-sticky-rice",
					"gift_description": "Mango Sticky Rice belongs in a cult collection because it captures a beloved edible idea with enough precision to become a reference point. Mango, tender rice, and coconut milk make the composition instantly legible, while its buttery finish keeps it from feeling generic."
				},
				{
					"handle": "pear-pavlova",
					"gift_description": "Pear Pavlova earns cult status through its unusual balance of dessert-like charm and structural bite, a fragrance people remember and recommend. Pear, meringue, and pink pepper give it lift, then bitter almond and ambrette musk add a deeper, more textured pull."
				},
				{
					"handle": "monkeyflower",
					"gift_description": "Monkeyflower fits because it feels like a rediscovered classic from an imagined archive, the kind of scent enthusiasts champion for its singular atmosphere. Chamomile, fig, and hay create its sunlit meadow effect, while musk and sunscreen accord give it a nostalgic, skin-close signature."
				},
				{
					"handle": "panorama",
					"gift_description": "Panorama belongs in this lineup as a true nose-nerd talking point, famous for making green freshness feel daring rather than polite. Wasabi, fig leaf, and bamboo supply the vivid opening, with galbanum, violet leaf, and fir balsam extending its sharp-edged depth."
				}
			]
		},
		"gender-bending-scents": {
			"title": "Gender-Bending Scents",
			"subtitle": "Fragrances that blur the lines beautifully",
			"category": "More Editorial",
			"description": "A playful cabinet of scents that slip past old labels, where softness, smoke, and sparkle trade places with ease.",
			"products": [
				{
					"handle": "fantomas",
					"gift_description": "Fantomas belongs here because its juicy melon and metallic sheen turn a usually bright note into something slyly androgynous. The Cashmeran woods and musky texture make it feel both tender and coolly synthetic, which is exactly the sort of boundary-blurring this collection celebrates."
				},
				{
					"handle": "anti-blues",
					"gift_description": "Anti-Blues fits this theme by taking gourmand comfort and giving it a shadowed, modern twist that refuses easy categorization. Dark chocolate, smoky incense, and saffron-led tobacco create a warm but rugged contrast, while vanilla and vetiver keep the composition fluid rather than fixed."
				},
				{
					"handle": "cashmere-beige",
					"gift_description": "Cashmere Beige earns its place through its airy, gender-free elegance: floral, but stripped of anything fussy or ornate. Mandarin, violet, and cool orris give it a clean brightness, while cedar and tonka soften the edges into a quiet, wearable haze."
				},
				{
					"handle": "fig-porn",
					"gift_description": "FIG PORN is a perfect match because it makes fig feel lush, fresh, and distinctly unboxed by gendered expectations. Milky green fig, pear, and bergamot bring a vivid opening, then peony, vanilla, and musk push it into a creamy, softly woody finish with real attitude."
				},
				{
					"handle": "mezcal-chocolat",
					"gift_description": "Mezcal Chocolat belongs in this lineup for the way it turns sweetness into something brisk, smoky, and unpinned. Salted lime, chili, and mezcal keep the profile sharp and spirited, while dark chocolate, rum, and incense add depth without settling into a familiar mold."
				},
				{
					"handle": "iris-altesse",
					"gift_description": "Iris Altesse fits the collection by reimagining iris as crisp, cool, and quietly subversive rather than delicate or prim. Chalky leather, orange blossom, and tuberose give it a polished tension, while jasmine sambac and cassis add a modern lift that loosens the old codes."
				},
				{
					"handle": "502-iris-cartagena",
					"gift_description": "502 Iris Cartagena is here because it takes powdery iris and folds it into a dry, boozy gourmand that feels free of stereotypes. Rum, coffee, and cocoa bring warmth and bite, while cedar, papyrus, and sandalwood give the scent a spare, tactile structure."
				},
				{
					"handle": "kar-wai",
					"gift_description": "Kar-Wai suits this theme by balancing floral softness with smoked tea and leather, creating a profile that feels fluid and self-assured. Longjing tea, osmanthus, and cardamom sharpen the opening, then rose, jasmine, and vetiver blur the lines between airy and dark."
				},
				{
					"handle": "myrrh-blood-drop",
					"gift_description": "No. 8 – Drop of Blood fits because its resinous darkness and metallic flash make it feel defiantly outside the usual scent binaries. Mandarin and saffron add a bright sting before incense, patchouli, oud, and myrrh pull it into a leathery, enigmatic depth."
				}
			]
		},
		"nostalgia-in-a-bottle": {
			"title": "Nostalgia in a Bottle",
			"subtitle": "Scents that trigger memories and emotion",
			"category": "More Editorial",
			"description": "These scents feel like memory kept close: worn fabrics, old rooms, and the quiet warmth of things remembered.",
			"products": [
				{
					"handle": "iris-cendr",
					"gift_description": "Iris Cendré belongs here because its rooty iris and violet recall the powdery trace of vintage cosmetics and well-kept drawers. Cool citrus and incense smoke sharpen that memory, while tobacco and amber add the hush of an older, more austere past."
				},
				{
					"handle": "alyazia",
					"gift_description": "Alyazia fits this collection through its tender, cosmetic softness that feels instantly familiar, like a childhood vanity left in the sun. Orange blossom, wild strawberry, and plush iris are cushioned by vanilla and tonka, turning the fragrance into a sweetened recollection rather than a simple floral."
				},
				{
					"handle": "josephine",
					"gift_description": "Joséphine earns its place by evoking the plush, sepia-toned elegance of another era, where rose and violet seem lifted from an old dressing table. Hawthorn, caramel-like opoponax, and incense give it a resinous warmth that reads as memory rather than mere ornament."
				},
				{
					"handle": "lances",
					"gift_description": "Lances suits the theme because its toasted hazelnut and lavender combination feels like a remembered comfort: savory, soft, and slightly old-fashioned. Vanilla, sandalwood, and cedar keep that familiarity dry and calm, like a favorite sweater preserved in the back of a closet."
				},
				{
					"handle": "oblivion",
					"gift_description": "Oblivion belongs in Nostalgia in a Bottle for the way its bergamot and yuzu flicker like a bright first impression before settling into something more familiar. Aromatic lavender, orris, and cedar create a powdery composure that feels tied to old routines and quiet afternoons."
				},
				{
					"handle": "patchouli-cannelle",
					"gift_description": "Patchouli Cannelle fits because its spiced patchouli suggests the scent of old books, warm kitchens, and coats brushed with winter air. Cinnamon, clove, and frankincense give it a lived-in depth, while musk and vanilla soften the edges into a comforting recollection."
				},
				{
					"handle": "fawaki-fawaki",
					"gift_description": "Fawaki Fawaki works in this collection by turning fruit into a memory of carefree sweetness rather than a modern tropical blast. Pineapple, green apple, and mango are wrapped in coconut, almond, and vanilla, with cedar and resins adding the faded warmth of a beloved childhood treat."
				},
				{
					"handle": "ara",
					"gift_description": "ARС fits the theme through its classic fougère structure, which feels like inherited grooming rituals and familiar masculine comfort. Basil, thyme, lavender, and pine are grounded by oakmoss, while styrax and benzoin add a smoky sweetness that lingers like a remembered aftershave."
				},
				{
					"handle": "ambre-de-noir",
					"gift_description": "Ambre de Noir belongs here because its cola-dark amber feels like a half-remembered room lit by low lamplight. Saffron, tobacco, and magnolia fold into ambergris, sandalwood, and vanilla, creating a dense warmth that reads as intimate, old-world memory."
				}
			]
		}
	},
	"quiz": {
		"steps": [
			{
				"question": "What are you looking for?",
				"sub": "Pick a vibe — we'll match a theme",
				"key": "vibe",
				"options": [
					{
						"id": "gift",
						"icon": "🎁",
						"title": "A Gift",
						"sub": "Something to give someone"
					},
					{
						"id": "exploring",
						"icon": "🔍",
						"title": "Just Exploring",
						"sub": "Browse by mood & aesthetic"
					},
					{
						"id": "myself",
						"icon": "💜",
						"title": "For Myself",
						"sub": "Something to enjoy"
					},
					{
						"id": "surprise",
						"icon": "🎲",
						"title": "Surprise Me",
						"sub": "Curator's pick"
					}
				]
			},
			{
				"question": "What's your budget?",
				"sub": "Every price range has gems",
				"key": "budget",
				"options": [
					{
						"id": "u50",
						"icon": "💰",
						"title": "Under £50",
						"sub": "Smart & stylish"
					},
					{
						"id": "u100",
						"icon": "🎁",
						"title": "£50 – £100",
						"sub": "The sweet spot"
					},
					{
						"id": "u150",
						"icon": "💎",
						"title": "£100 – £150",
						"sub": "Premium territory"
					},
					{
						"id": "over",
						"icon": "👑",
						"title": "£150+",
						"sub": "Go all out"
					}
				]
			},
			{
				"question": "What kind of scent?",
				"sub": "Trust your instinct — or let us surprise you",
				"key": "mood",
				"options": [
					{
						"id": "fresh",
						"icon": "🍃",
						"title": "Fresh & Clean",
						"sub": "Light, airy, breezy"
					},
					{
						"id": "warm",
						"icon": "🧣",
						"title": "Warm & Cosy",
						"sub": "Vanilla, amber, soft"
					},
					{
						"id": "sweet",
						"icon": "🍯",
						"title": "Sweet & Gourmand",
						"sub": "Edible, addictive"
					},
					{
						"id": "dark",
						"icon": "🔥",
						"title": "Dark & Smoky",
						"sub": "Leather, incense, oud"
					},
					{
						"id": "floral",
						"icon": "🌺",
						"title": "Floral & Romantic",
						"sub": "Rose, jasmine, soft"
					},
					{
						"id": "surprise",
						"icon": "🎲",
						"title": "Surprise Me",
						"sub": "Dealer's choice"
					}
				]
			}
		],
		"map": {
			"vibe": {
				"gift": [
					"Gifts for Her",
					"Gifts for Him",
					"Unisex Gifts",
					"First Niche Gift",
					"Statement Gifts",
					"Can't-Go-Wrong Gifts",
					"Mother's Day Gifts",
					"Father's Day Gifts",
					"Valentine's Day Gifts",
					"Birthday Gifts",
					"Anniversary Gifts",
					"Wedding Gifts",
					"Graduation Gifts",
					"Thank You Gifts",
					"For Mum",
					"For Dad",
					"For Your Partner",
					"For Your Best Friend"
				],
				"exploring": [
					"Cosy Comfort",
					"Fresh Escape",
					"Dark & Smoky",
					"Soft Skin Scents",
					"Sweet but Sophisticated",
					"Joyful Citrus",
					"Quiet Luxury",
					"Clean Girl Aesthetic",
					"Dark Academia",
					"Main Character Energy",
					"Cottagecore",
					"For the Curious Nose",
					"For the Minimalist",
					"For the Romantic"
				],
				"myself": [
					"Treat Yourself",
					"For the Collector",
					"For the Maximalist",
					"Smell Expensive",
					"Cult Classics",
					"Scents They'll Remember You For",
					"Long-Lasting Perfumes",
					"Best Value Per ML"
				],
				"surprise": [
					"The Easy Winners",
					"Unexpected Favourites",
					"Sleeper Hits",
					"Perfumes That Don't Smell Like Perfume",
					"Scents That Tell a Story",
					"Gender-Bending Scents",
					"Nostalgia in a Bottle"
				]
			},
			"budget": {
				"u50": [
					"Gifts Under £50",
					"Best Value Per ML",
					"Small Gifts, Big Personality",
					"Sample Packs for Beginners",
					"Travel-Friendly Fragrances",
					"Thank You Gifts"
				],
				"u100": [
					"Gifts Under £100",
					"Little Luxuries",
					"Discovery Sets to Gift",
					"Last-Minute Gifts",
					"Birthday Gifts",
					"Graduation Gifts"
				],
				"u150": [
					"Gifts Under £150",
					"Anniversary Gifts",
					"Full Bottles Worth Giving",
					"Best of British",
					"French Style",
					"Italian Aura",
					"Japanese Elegance"
				],
				"over": [
					"Smell Expensive",
					"Statement Gifts",
					"For the Collector",
					"Middle Eastern Luxury",
					"Wedding Gifts",
					"Anniversary Gifts"
				]
			},
			"mood": {
				"fresh": [
					"Fresh Escape",
					"Clean Girl Aesthetic",
					"Joyful Citrus",
					"Tea Scents",
					"Salt, Sea & Mineral",
					"Neroli & Orange Blossom Gifts",
					"Spring Gifting",
					"Summer in a Bottle",
					"Sunshine Gifts",
					"Best for the Office",
					"Lavender Reimagined",
					"Fig, Green & Herbaceous"
				],
				"warm": [
					"Cosy Comfort",
					"Vanilla Beyond Vanilla",
					"Sandalwood Gifts",
					"Autumn Mood",
					"Winter Warmers",
					"Festive Scents",
					"Bedtime Fragrances",
					"Quiet Luxury",
					"Cottagecore",
					"Powdery & Iris",
					"Soft Skin Scents",
					"Nostalgia in a Bottle"
				],
				"sweet": [
					"Sweet but Sophisticated",
					"Chocolate & Cocoa",
					"Coffee Fragrances",
					"Boozy Scents",
					"Cherry & Red Fruits",
					"Perfumes for Compliments",
					"Gifts With Personality",
					"Date Night Scents"
				],
				"dark": [
					"Dark & Smoky",
					"Oud Collection",
					"Leather & Suede",
					"Incense & Resin",
					"Dark Academia",
					"Main Character Energy",
					"Middle Eastern Luxury",
					"Long-Lasting Perfumes",
					"Scents That Tell a Story"
				],
				"floral": [
					"Rose Lovers",
					"White Flowers",
					"For the Romantic",
					"Valentine's Day Gifts",
					"Mother's Day Gifts",
					"Wedding Gifts",
					"Spring Gifting",
					"Cottagecore"
				],
				"surprise": [
					"Can't-Go-Wrong Gifts",
					"The Easy Winners",
					"Unexpected Favourites",
					"Perfumes That Don't Smell Like Perfume",
					"For the Curious Nose",
					"Sleeper Hits",
					"Scents They'll Remember You For",
					"Cult Classics"
				]
			}
		}
	},
	"browse": {
		"menu": {
			"Budget & Format": {
				"sections": [
					{
						"label": "By Budget",
						"items": [
							"Gifts Under £50",
							"Gifts Under £100",
							"Gifts Under £150",
							"Little Luxuries",
							"Best Value Per ML"
						]
					},
					{
						"label": "By Format",
						"items": [
							"Discovery Sets to Gift",
							"Sample Packs for Beginners",
							"Full Bottles Worth Giving",
							"Small Gifts, Big Personality",
							"Travel-Friendly Fragrances"
						]
					}
				]
			},
			"By Mood & Aesthetic": {
				"sections": [
					{
						"label": "By Mood",
						"items": [
							"Cosy Comfort",
							"Fresh Escape",
							"Dark & Smoky",
							"Soft Skin Scents",
							"Sweet but Sophisticated",
							"Joyful Citrus"
						]
					},
					{
						"label": "By Aesthetic",
						"items": [
							"Quiet Luxury",
							"Clean Girl Aesthetic",
							"Dark Academia",
							"Main Character Energy",
							"Cottagecore"
						]
					}
				]
			},
			"By Scent": {
				"sections": [
					{
						"label": "Scent Families",
						"items": [
							"Sandalwood Gifts",
							"Vanilla Beyond Vanilla",
							"Rose Lovers",
							"Cherry & Red Fruits",
							"Tea Scents",
							"Incense & Resin",
							"Neroli & Orange Blossom Gifts",
							"Fig, Green & Herbaceous",
							"Salt, Sea & Mineral"
						]
					},
					{
						"label": "More Notes",
						"items": [
							"Oud Collection",
							"White Flowers",
							"Leather & Suede",
							"Coffee Fragrances",
							"Chocolate & Cocoa",
							"Boozy Scents",
							"Powdery & Iris",
							"Lavender Reimagined"
						]
					}
				]
			},
			"For Someone": {
				"sections": [
					{
						"label": "By Gender",
						"items": [
							"Gifts for Her",
							"Gifts for Him",
							"Unisex Gifts"
						]
					},
					{
						"label": "By Relationship",
						"items": [
							"For Your Partner",
							"For Your Best Friend",
							"For Mum",
							"For Dad",
							"Treat Yourself"
						]
					},
					{
						"label": "By Personality",
						"items": [
							"For the Curious Nose",
							"For the Minimalist",
							"For the Maximalist",
							"For the Collector",
							"For the Romantic",
							"For the Trend-Lover"
						]
					}
				]
			},
			"By Occasion": {
				"sections": [
					{
						"label": "By Occasion",
						"items": [
							"Birthday Gifts",
							"Valentine's Day Gifts",
							"Mother's Day Gifts",
							"Father's Day Gifts",
							"Anniversary Gifts",
							"Wedding Gifts",
							"Thank You Gifts",
							"Graduation Gifts"
						]
					},
					{
						"label": "By Function",
						"items": [
							"Date Night Scents",
							"Best for the Office",
							"Long-Lasting Perfumes",
							"Bedtime Fragrances"
						]
					}
				]
			},
			"Curated & Origin": {
				"sections": [
					{
						"label": "Gift Guides",
						"items": [
							"First Niche Gift",
							"Can't-Go-Wrong Gifts",
							"Statement Gifts",
							"Last-Minute Gifts"
						]
					},
					{
						"label": "Editorial",
						"items": [
							"Smell Expensive",
							"The Easy Winners",
							"Perfumes for Compliments",
							"Cult Classics",
							"Unexpected Favourites",
							"Nostalgia in a Bottle"
						]
					},
					{
						"label": "By Origin",
						"items": [
							"Best of British",
							"French Style",
							"Italian Aura",
							"Japanese Elegance",
							"Middle Eastern Luxury"
						]
					},
					{
						"label": "By Season",
						"items": [
							"Spring Gifting",
							"Summer in a Bottle",
							"Sunshine Gifts",
							"Autumn Mood",
							"Winter Warmers",
							"Festive Scents"
						]
					}
				]
			}
		},
		"icons": {
			"Budget & Format": "£",
			"By Mood & Aesthetic": "✨",
			"By Scent": "🌺",
			"For Someone": "🎁",
			"By Occasion": "🎉",
			"Curated & Origin": "★"
		}
	}
};
